import { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAdmin } from "@/contexts/AdminContext";
import adminApi from "@/lib/adminApi";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import ModeSwitcher from "@/components/ModeSwitcher";
import TimeClockButton from "@/components/admin/TimeClockButton";  // v2.8+E342
import QuickSwitcherCommand from "@/components/QuickSwitcherCommand";
import { fmtUsd } from "@/lib/format";
import { Shield, LogOut, Users, User, Car, Map, Bell, DollarSign, CreditCard, Plus, Trash2, Phone, MapPin, ShieldCheck, Download, FileText, Gauge, ArrowLeft, LifeBuoy, Lock, AlertTriangle, Loader2, Zap, RotateCw, X as XIcon, MessageSquare, Calculator, ScrollText, Fuel, Sparkles, Building2, FileBadge2, Printer, Image as ImageIcon, Flag, Webhook, UserCheck, Wifi, Activity, BadgeDollarSign, Banknote, Route, Mail, Wrench, Minimize2, Maximize2, Smartphone, Search } from "lucide-react";
import IncidentCommanderTab from "@/components/admin/IncidentCommanderTab";
import RoleManagerDialog from "@/components/admin/RoleManagerDialog";
import MetricsTab from "@/components/admin/MetricsTab";
import DuplicateCustomersTool from "@/components/admin/DuplicateCustomersTool";
import UserManagementTab from "@/components/admin/UserManagementTab";
import UserPresenceDot from "@/components/admin/UserPresenceDot";
import AdminChatTab from "@/components/admin/AdminChatTab";
import EditTripDialog from "@/components/admin/EditTripDialog";
import RepairTotalsDialog from "@/components/admin/RepairTotalsDialog";
import RestoreCompletedRidesDialog from "@/components/admin/RestoreCompletedRidesDialog";
import { CustomerTypeBadge } from "@/components/CustomerTypeBadge";
import ChatInsightsCard from "@/components/admin/ChatInsightsCard";
import ApproachingFutureFaresStrip from "@/components/admin/ApproachingFutureFaresStrip";
import ImportantInboxCard from "@/components/admin/ImportantInboxCard";
import OpsBadgesWidget from "@/components/admin/OpsBadgesWidget";  // v2.7.65+E136
import BookRideTab from "@/components/admin/BookRideTab";
import CoordinatorSearchTab from "@/components/admin/CoordinatorSearchTab";  // v2.8+E325
import PositionMultiSelect from "@/components/admin/PositionMultiSelect";  // v2.7.65+E190 · Dossier identity edit
import { RiderFlagBanner } from "@/components/RiderFlagBanner";
import { downloadWaybillPDF } from "@/lib/waybill";
import VerifiedBadge from "@/components/VerifiedBadge";
import GoogleAuthBadge from "@/components/admin/GoogleAuthBadge";
import AccountingTab from "@/components/admin/AccountingTab";
import AdminEarningsTab from "@/components/admin/AdminEarningsTab";
import DeliveryPlatformTab from "@/components/admin/DeliveryPlatformTab";
import RiderPricePageTab from "@/components/admin/RiderPricePageTab";
import FrontEndTab from "@/components/admin/FrontEndTab";
import IntegrationsTab from "@/components/admin/IntegrationsTab";
import LiveDispatchMap from "@/components/LiveDispatchMap";
import BrandLogo from "@/components/BrandLogo";
import CommandConsoleLauncher from "@/components/admin/CommandConsoleLauncher";
import PrintableEmployeeBadge from "@/components/admin/PrintableEmployeeBadge";
import useSiteConfig from "@/hooks/useSiteConfig";
import useFleetStream from "@/hooks/useFleetStream";
import useCountUp from "@/hooks/useCountUp";
import { playSignalRecoveredChime } from "@/lib/dispatchAlarm";
import AdminSidebar, { useHashSubTab } from "@/components/admin/AdminSidebar";
import DisplaySettingsMenu from "@/components/DisplaySettingsMenu";
import useDisplayPrefs from "@/hooks/useDisplayPrefs";
import useBackendBranding from "@/hooks/useBackendBranding";
import useActiveTripsCount from "@/hooks/useActiveTripsCount";
import AdminProfileBadge from "@/components/admin/AdminProfileBadge";
import { announceProfileUpdated } from "@/components/admin/SidebarProfileFooter";
import { ProfilePhotoUploader, SavedTick } from "@/components/admin/ProfilePhotoUploader";
import { TeamPhotoWall } from "@/components/admin/TeamPhotoWall";
import DraggableStatsGrid, { DraggableWidget } from "@/components/admin/DraggableStatsGrid";
import ChatMetricsStrip from "@/components/admin/ChatMetricsStrip";
import SignoffAnalyticsCard from "@/components/admin/SignoffAnalyticsCard";
import AdminTopNav from "@/components/admin/AdminTopNav";
import { SidebarMobileTrigger } from "@/components/admin/AdminLayoutShell";
import WidgetSelectorSheet from "@/components/admin/WidgetSelectorSheet";
import { WIDGET_REGISTRY, defaultActiveSet, useWidgetFeed, ManualDownloadsWidget } from "@/components/admin/widgetLibrary";
import useWidgetLayoutSync from "@/hooks/useWidgetLayoutSync";
import DispatchHistory from "@/components/admin/DispatchHistory";
// v2.6.9 batch BT · DevLicenseDialog replaced by /dev-license route.
// Admin sidebar's "Dev License" sub-item now navigates instead of opening
// a modal (see AdministrationGroup.handleSubChange below).
import FleetManagementTab from "@/components/admin/FleetManagementTab";
import SubscriptionsAdminTab from "@/components/admin/SubscriptionsAdminTab";
import { DeveloperPortalTab, DeveloperCodingTab, DeveloperBrandingTab, ApiKeysTab, RolePinsTab, AudioTestPanelTab } from "@/components/admin/DeveloperTabs";
import TexNotificationManagerTab from "@/components/admin/TexNotificationManagerTab";
import RiderSubscriptionsEditor from "@/components/admin/RiderSubscriptionsEditor";
import BackendBrandingCard from "@/components/admin/BackendBrandingCard";
import DiscountsAdminCard from "@/components/admin/DiscountsAdminCard";
import StorefrontQRCard from "@/components/admin/StorefrontQRCard";
import TwilioInboundWebhookCard from "@/components/admin/TwilioInboundWebhookCard"; // v2.7.65+E169
import StopRateLedgerCard from "@/components/admin/StopRateLedgerCard"; // v2.7.65+E171
import ThemeToggle from "@/components/ThemeToggle";
import { useConfig } from "@/contexts/ConfigContext";
import { useTheme } from "@/contexts/ThemeContext";

// v2.7.43 · Widgets temporarily hidden per UX overhaul spec. Toggle
// localStorage `rtg_show_widgets`="1" (in DevTools) to re-show them
// during a debugging session WITHOUT touching code. To permanently
// re-enable, flip the default below to true.
const ADMIN_WIDGETS_VISIBLE = (() => {
  if (typeof window === "undefined") return false;
  try { return window.localStorage.getItem("rtg_show_widgets") === "1"; }
  catch { return false; }
})();

export default function AdminDashboardPage() {
  const { admin, logout } = useAdmin();
  const nav = useNavigate();
  const [tab, setTab] = useState("overview");
  // v2.8.3 · Track the active Driver-Mgmt sub-tab (from the URL hash) so the
  // Sign-Off Analytics ("Sign-offs by tier") + Recent Downloads cards can be
  // HIDDEN on the three dispatcher working views (Book Ride, Ride Coordinator,
  // Pending Rides) for more space. Display-only — data/logic untouched.
  const [currentSub] = useHashSubTab("");
  const hideDispatchWidgets = tab === "driver-mgmt"
    && ["book-ride", "coordination", "coordinator-search", "pending"].includes(currentSub || "coordination");
  // v2.8.4/v2.8.5 · Console display density + text size — one shared engine
  // (Compact / Auto spacing + Small/Medium/Large text) persisted PER ACCOUNT
  // so it follows the operator across devices, with a localStorage snapshot
  // for instant first paint.
  const fetchAdminPrefs = useCallback(async () => {
    try {
      const { data } = await adminApi.get("/admin/me/profile");
      return {
        compact: !!data?.ui_compact,
        autoCompact: !!data?.ui_auto_compact,
        fontScale: data?.ui_font_scale || "md",
      };
    } catch { return null; }
  }, []);
  const persistAdminPrefs = useCallback((p) => {
    const body = {};
    if ("compact" in p) body.ui_compact = p.compact;
    if ("autoCompact" in p) body.ui_auto_compact = p.autoCompact;
    if ("fontScale" in p) body.ui_font_scale = p.fontScale;
    if (Object.keys(body).length) adminApi.post("/admin/me/preferences", body).catch(() => {});
  }, []);
  const {
    compact, autoCompact, fontScale, effectiveCompact,
    setCompact, setAutoCompact, setFontScale,
  } = useDisplayPrefs({ prefix: "rtg_admin", fetchPrefs: fetchAdminPrefs, persistPrefs: persistAdminPrefs });
  const [stats, setStats] = useState(null);
  // v2.6.9 batch BJ · sidebar needs to know whether the Subscriptions tab is
  // visible — lift it here so the rail's nav tree stays in sync.
  const [subsEnabled, setSubsEnabled] = useState(true);
  const reloadSubsFlag = useCallback(() => {
    adminApi.get("/subscriptions/plans")
      .then((r) => setSubsEnabled(r.data?.enabled !== false))
      .catch((e) => console.warn("admin subs flag fetch failed", e?.response?.status));
  }, []);

  useEffect(() => { if (!admin) nav("/admin/login"); }, [admin, nav]);
  const refreshStats = useCallback(async ({ force = false } = {}) => {
    try {
      if (force) {
        // Manual override → bust caches, sweep any unlogged completions, then refetch.
        await adminApi.post("/admin/compliance/recalculate").catch(() => {});
      }
      const r = await adminApi.get("/admin/stats");
      setStats(r.data);
    } catch { /* silent */ }
  }, []);

  useEffect(() => { refreshStats(); }, [refreshStats]);

  // v2.7.5 · 30 s auto-poll so Revenue Today + counters update without manual refresh.
  useEffect(() => {
    const id = setInterval(() => refreshStats(), 30_000);
    return () => clearInterval(id);
  }, [refreshStats]);

  // v2.7.6 Audit · INSTANT (< 1 s) refresh the moment a trip completes —
  // listens for the WebSocket-fed `rtg:trip_completed` event. No 30 s wait,
  // no cache delay. Triggers a fresh /admin/stats fetch immediately.
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const onTripCompleted = () => refreshStats();
    window.addEventListener("rtg:trip_completed", onTripCompleted);
    return () => window.removeEventListener("rtg:trip_completed", onTripCompleted);
  }, [refreshStats]);
  useEffect(() => { reloadSubsFlag(); }, [reloadSubsFlag]);

  // v2.7.0 batch DO++++ · FEATURE 2 · Fetch the caller's full canonical roles[]
  // for the User Management view's visibility gating (Developer / Admin tabs).
  const [adminRolesArr, setAdminRolesArr] = useState([]);
  useEffect(() => {
    adminApi.get("/admin/me/roles")
      .then((r) => setAdminRolesArr(r.data?.roles || []))
      .catch(() => setAdminRolesArr([]));
  }, []);

  // v2.6.9 batch CR · Mount the global dispatch-stream subscriber here so
  // public-booking toasts fire regardless of which tab the dispatcher is
  // currently viewing. Without this, the WS connection only existed when
  // the Live Map tab was active and toasts were missed on Overview / Book
  // A Ride / Drivers / etc.
  // v2.7.65+E191 · Global Driver Dossier — mounted at the AdminDashboardPage
  // level so the sidebar footer's pencil icon can pop the target user's
  // dossier from ANY tab (Overview, Live Map, Dispatch, etc.) without first
  // navigating to Account Users → Drivers. DriversTab still has its own
  // per-row dossier trigger — this is a superset.
  const [globalDossier, setGlobalDossier] = useState(null);
  const [globalDossierBusy, setGlobalDossierBusy] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const openViaEvent = async (ev) => {
      const did = ev?.detail?.driverId || ev?.detail?.id;
      if (!did) return;
      setGlobalDossierBusy(true);
      try {
        const { data } = await adminApi.get(`/admin/drivers/${did}/dossier`);
        setGlobalDossier({ ...data, _self: !!ev?.detail?.self });
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Could not load dossier");
      } finally {
        setGlobalDossierBusy(false);
      }
    };
    window.addEventListener("rtg:open-dossier", openViaEvent);
    // One-shot: also react to ?dossier=<id> query param (e.g. deep link)
    try {
      const params = new URLSearchParams(window.location.search);
      const q = params.get("dossier");
      if (q) {
        openViaEvent({ detail: { driverId: q } });
        params.delete("dossier");
        const clean = window.location.pathname + (params.toString() ? `?${params}` : "") + window.location.hash;
        window.history.replaceState({}, "", clean);
      }
    } catch { /* ignore */ }
    return () => window.removeEventListener("rtg:open-dossier", openViaEvent);
  }, []);
  // v2.8+E337 · Deep-link tab select (e.g. the Dialpad caller-match page hands
  // off to /admin?tab=driver-mgmt#sub=book-ride). One-shot on mount, plus a
  // window-event path for IN-APP hand-offs (e.g. "Start booking" from the
  // Dialpad call-activity log while already inside /admin — no remount fires).
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const applyTabFromUrl = () => {
      try {
        const params = new URLSearchParams(window.location.search);
        const t = params.get("tab");
        if (t) {
          setTab(t);
          params.delete("tab");
          const clean = window.location.pathname + (params.toString() ? `?${params}` : "") + window.location.hash;
          window.history.replaceState({}, "", clean);
        }
      } catch { /* ignore */ }
    };
    applyTabFromUrl();
    const onSelectTab = (ev) => {
      const t = ev?.detail?.tab;
      if (t) setTab(t);
    };
    window.addEventListener("rtg:admin-select-tab", onSelectTab);
    return () => window.removeEventListener("rtg:admin-select-tab", onSelectTab);
  }, []);
  const printGlobalDossier = (id) => {
    const base = process.env.REACT_APP_BACKEND_URL || "";
    const token = localStorage.getItem("rtg_admin_token");
    fetch(`${base}/api/admin/drivers/${id}/dossier/export.html`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.text() : Promise.reject(r.statusText))
      .then(html => {
        const blob = new Blob([html], { type: "text/html" });
        const url = URL.createObjectURL(blob);
        window.open(url, "_blank");
      })
      .catch(() => toast.error("Could not open dossier print view"));
  };

  useGlobalDispatchAlerts();
  // v2.7.65+E120 · `useAudioAlerts()` + `<AudioActivationOverlay/>` are now
  // mounted globally in <AdminOpsProvider> (see App.js AdminProtected) so the
  // audio engine keeps firing on every admin page, not just this dashboard.
  const activeTrips = useActiveTripsCount();  // v2.7.7 · Live Map counter feed
  const { backgroundStyle, navStyle } = useBackendBranding();
  // v2.7.45 Phase-1 · Backend branding nav color is suppressed in light mode
  // so the top nav doesn't ship its admin-chosen navy/blue tint into the
  // bright workspace (the original "static blue background" bug). Light
  // mode is always pure white; dark mode keeps the operator's choice.
  const { resolved: resolvedTheme } = useTheme();
  const effectiveNavStyle = resolvedTheme === "dark" ? navStyle : {};
  const effectiveBgStyle  = resolvedTheme === "dark" ? backgroundStyle : {};

  // v2.7.16 · Widget Selector — manages WHICH widgets render in the
  // draggable grid. Default set comes from `defaultActiveSet()` (the
  // 6 originals). Persists in localStorage under the same prefix the
  // grid uses for ordering/sizing so the entire layout state lives
  // together.
  const ACTIVE_KEY = "rtg_admin_stats_order_v1:active";
  const [activeWidgets, setActiveWidgets] = useState(() => {
    try {
      const raw = window.localStorage.getItem(ACTIVE_KEY);
      const parsed = raw ? JSON.parse(raw) : null;
      if (Array.isArray(parsed) && parsed.length) {
        // Drop any unknown ids that may have been retired from the registry.
        const validIds = new Set(WIDGET_REGISTRY.map((w) => w.id));
        return parsed.filter((id) => validIds.has(id));
      }
    } catch { /* fall through */ }
    return defaultActiveSet();
  });
  useEffect(() => {
    try { window.localStorage.setItem(ACTIVE_KEY, JSON.stringify(activeWidgets)); } catch { /* quota */ }
    // v2.7.17 · Notify the layout-sync hook so it debounces a push to the server.
    try { window.dispatchEvent(new CustomEvent("widget-layout:changed")); } catch { /* noop */ }
  }, [activeWidgets]);

  // v2.7.17 · Rehydrate React state when the layout-sync hook pulled fresh
  // data from the server on login. The localStorage write happens inside
  // the hook; here we just propagate it into local state so the UI matches.
  useEffect(() => {
    const onHydrated = (e) => {
      const incoming = e?.detail || {};
      if (Array.isArray(incoming.active) && incoming.active.length) {
        const validIds = new Set(WIDGET_REGISTRY.map((w) => w.id));
        setActiveWidgets(incoming.active.filter((id) => validIds.has(id)));
      }
    };
    window.addEventListener("widget-layout:hydrated", onHydrated);
    return () => window.removeEventListener("widget-layout:hydrated", onHydrated);
  }, []);

  // v2.7.17 · Bidirectional localStorage ↔ server-side preference sync.
  // Pulls server layout on mount (taking the newest of server vs. local)
  // and pushes any local change back after a 1.5 s debounce.
  useWidgetLayoutSync({ enabled: true });
  const toggleWidget = useCallback((id) => {
    setActiveWidgets((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  }, []);
  // Only poll the widget-feed when at least one "extras" widget is on
  // → saves a request when the dashboard only shows the classic 6.
  const needsFeed = activeWidgets.some((id) => {
    const e = WIDGET_REGISTRY.find((w) => w.id === id);
    return e?.needsFeed;
  });
  const widgetFeed = useWidgetFeed(needsFeed);

  if (!admin) return null;

  return (
    <div className={`min-h-screen bg-white dark:bg-[#0c0d10] text-slate-900 dark:text-zinc-100 ui-scale-${fontScale}${effectiveCompact ? " ui-compact" : ""}`} style={effectiveBgStyle} data-testid="admin-root" data-compact={effectiveCompact ? "1" : "0"} data-font-scale={fontScale}>
      <QuickSwitcherCommand mode="admin"/>
      <header className="sticky top-0 z-30 backdrop-blur-xl bg-white/95 dark:bg-[#0c0d10]/80 border-b border-slate-200 dark:border-amber-400/30" style={effectiveNavStyle} data-testid="admin-nav-bar">
        <div className="max-w-7xl xl:max-w-screen-2xl 2xl:max-w-none mx-auto px-3 sm:px-5 2xl:px-8 py-3 sm:py-4 flex flex-wrap items-center justify-between gap-y-2 gap-x-3">
          <div className="flex items-center gap-3 min-w-0">
            {/* v2.7.44 · Hamburger trigger — shown only below lg breakpoint
                (the sidebar is permanently visible on desktop). Inserted
                where the logo/brand text used to live; both were removed
                per Phase-2 Task 4 (top bar is functional-only now). */}
            <SidebarMobileTrigger/>
          </div>
          <div className="flex items-center gap-2 sm:gap-3 flex-wrap justify-end">
            {/* v2.6.7 batch I · Portal entry hidden — feature flagged off. */}
            {false && (admin.role === "admin" || admin.role === "supervisor") && (
              <Button asChild variant="outline" size="sm" className="bg-zinc-950 border-amber-400/40 text-amber-300 hover:bg-amber-400/10 h-8 text-xs" data-testid="admin-open-portal">
                <Link to="/portal"><Building2 className="h-3 w-3 mr-1"/>Portal</Link>
              </Button>
            )}
            <ThemeToggle/>
            <DisplaySettingsMenu
              compact={compact}
              autoCompact={autoCompact}
              fontScale={fontScale}
              effectiveCompact={effectiveCompact}
              setCompact={setCompact}
              setAutoCompact={setAutoCompact}
              setFontScale={setFontScale}
              align="end"
              buttonClassName="bg-white border-slate-300 text-slate-800 hover:bg-slate-100 hover:text-slate-900 dark:bg-zinc-950 dark:border-zinc-700 dark:text-zinc-300 dark:hover:text-amber-400"
            />
            <ModeSwitcher mode="admin"/>
            <TimeClockButton/>
            <Button asChild variant="outline" size="sm" className="bg-white border-slate-300 text-slate-800 hover:bg-slate-100 hover:text-slate-900 dark:bg-zinc-950 dark:border-zinc-700 dark:text-zinc-300 dark:hover:text-amber-400 h-8 text-xs" data-testid="admin-back-home">
              <Link to="/login"><ArrowLeft className="h-3 w-3 mr-1"/>App Home</Link>
            </Button>
            <Button data-testid="admin-logout" variant="ghost" size="icon" onClick={() => { logout(); nav("/login"); }} className="text-slate-600 hover:text-red-600 dark:text-zinc-400 dark:hover:text-red-400" title="Log out & return to home">
              <LogOut className="h-4 w-4"/>
            </Button>
            {/* v2.7.7 · Profile avatar — far right. Version/online badge
                relocated to the sidebar navigation per user spec. */}
            <div className="ml-1">
              <AdminProfileBadge adminFallback={admin}/>
            </div>
          </div>
        </div>
      </header>

      {/* v2.7.17 · Fluid full-width — capped at 7xl up to xl, screen-2xl at
          xl→2xl, then 100% viewport on 2xl+ monitors for dispatchers running
          large-format displays. Padding scales similarly to keep the
          breathing room consistent. */}
      <main
        data-admin-workspace="1"
        className="max-w-7xl xl:max-w-screen-2xl 2xl:max-w-none mx-auto px-3 sm:px-5 2xl:px-8 py-6"
      >
        {/* v2.7.43 · Widgets HIDDEN by default per UX overhaul spec.
            To re-enable, set localStorage `rtg_show_widgets`="1" or flip
            ADMIN_WIDGETS_VISIBLE below to true. Hidden state collapses the
            entire DraggableStatsGrid + the refresh toolbar so the page lands
            directly on the (now full-width) navigation + tabbed content. */}
        {/* v2.7.65+E108 · Global Chat Metrics strip. Renders regardless
            of widget visibility so ops always sees active chats + queue
            length at the top of the console. */}
        <ChatMetricsStrip/>
        {/* v2.7.65+E109 · Sign-Off Analytics — small chart of resolutions
            by tier so ops can see which teams are closing the loop.
            v2.8.3 · Hidden on the three dispatcher working views for space. */}
        {!hideDispatchWidgets && <SignoffAnalyticsCard/>}
        {/* v2.7.65+E122 · Recent Manual Downloads audit card — L5-gated
            (self-hides for non-L5 callers via a 403 from the audit route).
            v2.8.3 · Also hidden on the three dispatcher working views. */}
        {!hideDispatchWidgets && (
          <div className="mb-2" data-testid="manual-downloads-card-wrap">
            <ManualDownloadsWidget/>
          </div>
        )}

        {ADMIN_WIDGETS_VISIBLE && stats && (
          <DraggableStatsGrid storageKey="rtg_admin_stats_order_v1" className="mb-2">
            {activeWidgets.map((id) => {
              const entry = WIDGET_REGISTRY.find((w) => w.id === id);
              if (!entry) return null;
              // Stat widgets render via the AdminDashboardPage's local StatBox
              // because they consume the live `stats` payload directly. Extras
              // render via the registry's `render(ctx)` function.
              if (entry.group === "stats") {
                const node = (() => {
                  switch (id) {
                    case "drivers":       return <StatBox label="Drivers"        value={stats.drivers}                 tint="lavender" Icon={Users}        caption="Roster"        testId="stat-drivers"/>;
                    case "active":        return <StatBox label="Active"         value={stats.active_drivers}          tint="teal"     Icon={UserCheck}    caption="On platform"   testId="stat-active"/>;
                    case "online":        return <StatBox label="Online"         value={stats.online_drivers}          tint="mint"     Icon={Wifi}         caption="Live drivers"  accent="green" testId="stat-online"/>;
                    case "trips":         return <StatBox label="Trips"          value={stats.trips}                   tint="amber"    Icon={Activity}     caption="Lifetime"      testId="stat-trips"/>;
                    case "revenue-today": return <StatBox hero glow="blue"  label="Revenue Today" value={fmtUsd(stats.revenue_today)} mono caption="Today · America/Chicago" testId="stat-revenue-today"/>;
                    case "revenue-total": return <StatBox hero glow="mint"  label="Revenue Total" value={fmtUsd(stats.revenue_total)} mono caption="Lifetime gross"           testId="stat-revenue-total"/>;
                    default: return null;
                  }
                })();
                return <DraggableWidget key={id} id={id} label={entry.label}>{node}</DraggableWidget>;
              }
              // Extras
              return (
                <DraggableWidget key={id} id={id} label={entry.label}>
                  {entry.render?.({ stats, feed: widgetFeed }) ?? null}
                </DraggableWidget>
              );
            })}
          </DraggableStatsGrid>
        )}
        {ADMIN_WIDGETS_VISIBLE && (
        <div className="flex items-center justify-end gap-2 mb-3 text-[10px] text-zinc-500">
          <span className="hidden sm:inline">auto-refresh every 30 s</span>
          <WidgetSelectorSheet activeIds={activeWidgets} onToggle={toggleWidget}/>
          <Button
            size="sm"
            variant="outline"
            onClick={() => refreshStats({ force: true })}
            className="h-7 bg-zinc-950 border-zinc-700 hover:border-amber-400 hover:text-amber-400 text-[11px]"
            data-testid="stats-manual-refresh"
            title="Force recalculation + bypass cache (Super User override)"
          >
            <RotateCw className="h-3 w-3 mr-1"/>
            REFRESH NOW
          </Button>
        </div>
        )}

        {/* v2.7.14 · TWO-TIER FULL-WIDTH NAV — sits directly beneath the
            widget grid. Tier 1 = metadata + currently-online tracker, Tier 2
            = primary nav links with preserved dropdown sub-menus. */}
        <div className="mb-4">
          <AdminTopNav
            activeTab={tab}
            onTopNav={(v) => setTab(v)}
            adminRole={admin.role}
            adminRoles={adminRolesArr}
            subsEnabled={subsEnabled}
            unreadBadges={{ "active-trips": activeTrips }}
          />
        </div>

        {/* v2.6.9 batch BJ · Mobile-only sidebar drawer (sheet trigger only;
            desktop renders the top nav above instead). */}
        <div className="flex flex-col gap-5">
          <div className="md:hidden">
            <AdminSidebar
              activeTab={tab}
              onTopNav={(v) => setTab(v)}
              adminRole={admin.role}
              subsEnabled={subsEnabled}
              unreadBadges={{ "active-trips": activeTrips }}
            />
          </div>
          <div className="flex-1 min-w-0">
            <Tabs value={tab} onValueChange={setTab}>
              <TabsList className={`sr-only`} data-testid="admin-top-tabs">
                <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
                <TabsTrigger value="live" data-testid="tab-live">Live Map</TabsTrigger>
                <TabsTrigger value="driver-mgmt" data-testid="tab-driver-mgmt">Driver Mgmt</TabsTrigger>
                <TabsTrigger value="user-mgmt" data-testid="tab-user-mgmt">User Management</TabsTrigger>
                <TabsTrigger value="fleet" data-testid="tab-fleet">Fleet</TabsTrigger>
                <TabsTrigger value="commander" data-testid="tab-commander">TAXIcab Commander</TabsTrigger>
                <TabsTrigger value="chat" data-testid="tab-chat" className="hidden">Chat</TabsTrigger>
                {admin.role !== "dispatcher" && (
                  <TabsTrigger value="earnings" data-testid="tab-earnings">Earnings</TabsTrigger>
                )}
                {admin.role !== "dispatcher" && (
                  <TabsTrigger value="metrics" data-testid="tab-metrics">Metrics</TabsTrigger>
                )}
                {admin.role !== "dispatcher" && (
                  <TabsTrigger value="admin" data-testid="tab-admin">Administration</TabsTrigger>
                )}
                {/* v2.7.57+E34 · DELIVERY SYSTEM · Section 2 · admin surface
                    for the courier-logistics pricing engine. Read-tier admins
                    see the config; write-tier can edit. Section 3 will
                    extend this tab with the dispatch board. */}
                <TabsTrigger value="delivery-platform" data-testid="tab-delivery-platform">Delivery Platform</TabsTrigger>
                <TabsTrigger value="team-wall" data-testid="tab-team-wall">Team Photo Wall</TabsTrigger>
              </TabsList>
              <TabsContent value="overview"><OverviewTab onJumpToTab={setTab}/></TabsContent>
              <TabsContent value="live"><LiveTab/></TabsContent>
              <TabsContent value="driver-mgmt"><DriverManagementGroup adminRole={admin.role}/></TabsContent>
              <TabsContent value="user-mgmt">{tab === "user-mgmt" && <UserManagementTab adminRole={admin.role} adminRoles={adminRolesArr} CustomersComponent={CustomersTab} DriversComponent={DriversTab}/>}</TabsContent>
              <TabsContent value="fleet"><FleetManagementTab/></TabsContent>
              <TabsContent value="commander"><IncidentCommanderTab/></TabsContent>
              <TabsContent value="chat">{tab === "chat" && <AdminChatTab/>}</TabsContent>
              {admin.role !== "dispatcher" && (
                <TabsContent value="earnings">{tab === "earnings" && <AdminEarningsTab/>}</TabsContent>
              )}
              {admin.role !== "dispatcher" && (
                <TabsContent value="metrics">{tab === "metrics" && <MetricsTab/>}</TabsContent>
              )}
              {admin.role !== "dispatcher" && (
                <TabsContent value="admin"><AdministrationGroup adminRole={admin.role} subsEnabled={subsEnabled} onSubsToggle={reloadSubsFlag}/></TabsContent>
              )}
              <TabsContent value="delivery-platform">{tab === "delivery-platform" && <DeliveryPlatformTab/>}</TabsContent>
              <TabsContent value="team-wall">{tab === "team-wall" && <TeamPhotoWall/>}</TabsContent>
            </Tabs>
          </div>
        </div>
      </main>

      {/* v2.7.65+E191 · Global Driver Dossier — reachable from any tab via
          the sidebar footer pencil (dispatches `rtg:open-dossier`) or a
          deep-link `?dossier=<id>` query param. */}
      <DriverDossierDialog
        dossier={globalDossier}
        busy={globalDossierBusy}
        onClose={() => setGlobalDossier(null)}
        onPrint={() => globalDossier && printGlobalDossier(globalDossier.id)}
        onToggleVerification={async (field, value) => {
          const pin = window.prompt("Enter your Operating Partner / admin PIN to update attestation:");
          if (!pin) return;
          try {
            await adminApi.put(`/admin/drivers/${globalDossier.id}/verifications`, { pin, [field]: value });
            setGlobalDossier(d => ({ ...d, [field]: value }));
            toast.success(`${field === "verified_osbi" ? "OSBI" : "DMV"} attestation ${value ? "set" : "cleared"}`);
          } catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
        }}
        onSaveIdentity={async (patch) => {
          try {
            const clean = {};
            if (Object.prototype.hasOwnProperty.call(patch, "employee_id")) {
              clean.employee_id = (patch.employee_id || "").toUpperCase().trim();
            }
            if (Object.prototype.hasOwnProperty.call(patch, "positions")) {
              clean.positions = Array.isArray(patch.positions) ? patch.positions : [];
            }
            // v2.8+E349 · own profile (sidebar card pencil) saves through the
            // self-identity endpoint so the card + staff row stay in sync.
            if (globalDossier._self) {
              await adminApi.put("/admin/me/identity", clean);
            } else {
              await adminApi.patch(`/admin/drivers/${globalDossier.id}`, clean);
            }
            announceProfileUpdated();
            setGlobalDossier(d => ({ ...d, ...clean }));
            toast.success("Identity updated");
          } catch (e) {
            toast.error(e?.response?.data?.detail || "Save failed");
            throw e;
          }
        }}
        adminRole={admin?.role}
        autoSave={!!globalDossier?._self}
      />
    </div>
  );
}

/**
 * v2.6.9 batch CR · useGlobalDispatchAlerts
 * ===========================================
 * Mounts a single dispatch-stream WebSocket subscription at the dashboard
 * root and listens for the `rtg:public_booking` CustomEvent fired by
 * useFleetStream when a homepage Quick Booking lands. Renders a sonner
 * toast + light chime regardless of which admin tab is active. The
 * LiveDispatchMap also subscribes (for its own driver-marker updates) —
 * useFleetStream is keyed on (role, token) so the underlying WS is
 * de-duped between the two consumers.
 *
 * Without this hook, the WS connection only existed when the Live Map tab
 * was actively rendered, so public-booking toasts were silently dropped
 * whenever a dispatcher was sitting on Overview / Book A Ride / Drivers / etc.
 */
function useGlobalDispatchAlerts() {
  const token = typeof window !== "undefined"
    ? localStorage.getItem("rtg_admin_token")
    : null;
  // Open + maintain the dispatch WebSocket. We don't consume the returned
  // driver-list state here — its purpose is just to keep the WS alive so
  // server-side `public_booking` broadcasts reach the window-level CustomEvent.
  useFleetStream({ role: "dispatch", token, active: !!token });

  useEffect(() => {
    const onPublicBooking = (e) => {
      const d = e.detail || {};
      const route = `${d.origin || "—"} → ${d.destination || "—"}`;
      const pingMsg = d.drivers_pinged
        ? `${d.drivers_pinged} driver${d.drivers_pinged === 1 ? "" : "s"} pinged`
        : "⚠ NO DRIVERS ONLINE — call the rider back";
      toast.success(`📞 New Quick Booking · ${d.trip_id || ""}`, {
        description: `${d.rider_name || "Anonymous"} · ${d.rider_phone || "—"}\n${route}\n${pingMsg}`,
        duration: 15000,
        id: `global-public-booking-${d.trip_id}`,
      });
      try {
        playSignalRecoveredChime();
      } catch (err) {
        // Autoplay-policy blocked / WebAudio not yet ready — toast already
        // delivered the visual signal so this is purely supplemental.
        // eslint-disable-next-line no-console
        console.debug("[public-booking-toast] chime blocked:", err?.message || err);
      }
    };
    window.addEventListener("rtg:public_booking", onPublicBooking);
    return () => window.removeEventListener("rtg:public_booking", onPublicBooking);
  }, []);
}


// v2.7.0 batch DO+++++++ · Premium StatBox refactor.
// Pastel-tinted glassmorphism with role-themed icon tile + optional trend
// delta indicator. Hover lifts -translate-y-1 and intensifies the shadow.
// Reuses lucide-react icons → matches the existing component library.
function StatBox({ label, value, mono, accent, tint = "lavender", Icon, trend, caption, testId, hero = false, glow = "blue" }) {
  // v2.7.24 · Smooth count-up on every value change so dispatch screens
  // *tick* into new totals rather than snapping. Pass-through for any
  // non-numeric strings (e.g. driver names, statuses).
  const displayValue = useCountUp(value);
  if (hero) {
    // v2.7.23 · Hero revenue tiles also get the underline. Hero uses a
    // colored gradient bar — we mirror it with a 1px line that picks up
    // the `glow` color so Revenue Today (blue) and Revenue Total (amber)
    // land their own tint.
    const heroBorder = {
      blue:    "border-sky-400/40",
      amber:   "border-amber-400/40",
      emerald: "border-emerald-400/40",
      rose:    "border-rose-400/40",
    }[glow] || "border-zinc-700/40";
    return (
      <Card
        className={`hero-revenue-card glow-${glow} p-5 h-full w-full flex flex-col justify-center`}
        data-testid={testId || `stat-${(label || "").toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
      >
        <div className={`text-[10px] uppercase tracking-[0.22em] text-zinc-400 font-bold pb-1.5 mb-2 border-b ${heroBorder}`}>{label}</div>
        <div className={`hero-revenue-figure text-3xl sm:text-4xl font-extrabold ${mono ? "meter-display" : ""}`}>
          {displayValue}
        </div>
        {caption && <div className="text-[10px] text-zinc-500 mt-2">{caption}</div>}
      </Card>
    );
  }
  const tintClass = `stat-card-tint-${tint}`;
  const iconTintClass = {
    lavender: "bg-violet-400/15 text-violet-200",
    teal:     "bg-teal-400/15 text-teal-200",
    mint:     "bg-emerald-400/15 text-emerald-200",
    amber:    "bg-amber-400/15 text-amber-200",
    sky:      "bg-sky-400/15 text-sky-200",
    rose:     "bg-rose-400/15 text-rose-200",
  }[tint] || "bg-zinc-700/30 text-zinc-200";
  // v2.7.23 · Tint-matched 1px underline beneath the title row.
  const labelUnderline = {
    lavender: "border-violet-400/40",
    teal:     "border-teal-400/40",
    mint:     "border-emerald-400/40",
    amber:    "border-amber-400/40",
    sky:      "border-sky-400/40",
    rose:     "border-rose-400/40",
  }[tint] || "border-zinc-700/40";
  const valueColor = accent === "green" ? "text-emerald-300" : "text-zinc-50";
  const trendColor = !trend ? "" : (trend.dir === "up" ? "text-emerald-400" : trend.dir === "down" ? "text-rose-400" : "text-zinc-400");
  const TrendIcon = !trend ? null : (trend.dir === "up" ? "▲" : trend.dir === "down" ? "▼" : "·");
  return (
    <Card
      className={`stat-card-base ${tintClass} p-3.5 h-full w-full flex flex-col group cursor-default`}
      data-testid={testId || `stat-${(label || "").toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
    >
      <div className="flex items-start gap-2.5">
        {Icon && (
          <div className={`shrink-0 h-9 w-9 rounded-lg flex items-center justify-center ${iconTintClass} transition-transform duration-200 group-hover:scale-110`}>
            <Icon className="h-4 w-4"/>
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className={`flex items-center justify-between gap-1 pb-1 border-b ${labelUnderline}`}>
            <div className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold leading-tight">{label}</div>
            {trend && (
              <span className={`text-[10px] font-bold ${trendColor} flex items-center gap-0.5 tabular-nums`}>
                {TrendIcon}{trend.label}
              </span>
            )}
          </div>
          <div className={`text-2xl font-extrabold mt-1.5 ${mono ? "meter-display" : ""} ${valueColor} tabular-nums leading-none`}>
            {displayValue}
          </div>
        </div>
      </div>
      {caption && (
        <div className="mt-2.5 pt-2 border-t border-white/5 text-[10px] text-zinc-500 flex items-center justify-between">
          <span>{caption}</span>
          {trend?.delta != null && (
            <span className={`${trendColor} font-bold`}>{TrendIcon} {trend.delta}</span>
          )}
        </div>
      )}
    </Card>
  );
}

// ============================================================================
// Driver Management group (v2.6.2) — Rider Coord. · Drivers · Customers ·
// Rides/Waybills · Pending. Has its own internal tab strip with sub-tabs so
// the parent admin TabsList stays at 4 buttons.
// ============================================================================
// ============================================================================
// v2.6.9 batch S · OpenRiderModeButton
// One-tap "open the rider app as me" shortcut. Hits /admin/staff/rider-mode
// to mint a customer JWT bound to the staff member's linked rider profile,
// stashes it in localStorage with the keys customerApi expects, and opens
// `/ride` in a new tab so the dispatch session stays alive.
// ============================================================================
function OpenRiderModeButton() {
  const [busy, setBusy] = useState(false);
  const open = async () => {
    setBusy(true);
    try {
      const { data } = await adminApi.post("/admin/staff/rider-mode");
      // Reuse the same storage keys customerApi.js + CustomerContext expect.
      localStorage.setItem("rtg_customer_token", data.access_token);
      localStorage.setItem("rtg_customer", JSON.stringify(data.customer));
      toast.success("Rider mode opened in a new tab");
      window.open("/ride", "_blank");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not open rider mode");
    } finally { setBusy(false); }
  };
  return (
    <Button
      onClick={open}
      disabled={busy}
      variant="outline"
      size="sm"
      data-testid="admin-open-rider-mode"
      className="bg-zinc-950 border-blue-400/40 text-blue-300 hover:bg-blue-400/10 h-8 text-xs"
      title="Open the rider app as your staff identity in a new tab"
    >
      {busy ? <Loader2 className="h-3 w-3 mr-1 animate-spin"/> : <User className="h-3 w-3 mr-1"/>}Rider Mode
    </Button>
  );
}

// ============================================================================
// v2.6.9 batch CI · OpenDriverModeButton — mirrors OpenRiderModeButton but
// hits /admin/staff/driver-mode to mint a driver JWT, then opens /dashboard
// in a new tab. The token is stored under the same localStorage keys that
// AuthContext / api.js use for the driver session.
// ============================================================================
function OpenDriverModeButton() {
  const [busy, setBusy] = useState(false);
  const open = async () => {
    setBusy(true);
    try {
      const { data } = await adminApi.post("/admin/staff/driver-mode");
      localStorage.setItem("rtg_token", data.access_token);
      // v2.7.0 batch DO++++++ · AuthContext reads `rtg_driver`, not
      // `rtg_user`. Persist BOTH so legacy + current consumers stay happy.
      localStorage.setItem("rtg_driver", JSON.stringify(data.driver));
      localStorage.setItem("rtg_user", JSON.stringify(data.driver));
      toast.success("Driver mode opened in a new tab");
      window.open("/dashboard", "_blank");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not open driver mode");
    } finally { setBusy(false); }
  };
  return (
    <Button
      onClick={open}
      disabled={busy}
      variant="outline"
      size="sm"
      data-testid="admin-open-driver-mode"
      className="bg-zinc-950 border-amber-400/40 text-amber-300 hover:bg-amber-400/10 h-8 text-xs"
      title="Open the driver app as your staff identity in a new tab"
    >
      {busy ? <Loader2 className="h-3 w-3 mr-1 animate-spin"/> : <Car className="h-3 w-3 mr-1"/>}Driver Mode
    </Button>
  );
}

// ============================================================================
// Driver Management Group · sub-tabs container
// ============================================================================
function DriverManagementGroup({ adminRole }) {
  const [sub, setSub] = useHashSubTab("coordination");
  // v2.6.9 batch V · Dispatcher gets the same Driver Mgmt access as
  // Supervisor (Rider Coord. included). The Administration restriction is
  // enforced at the top-level tab list, not here.
  // v2.7.0 batch DO++ · FEATURE 1 · Operating Partner + Developer are absolute
  // super users with full access. Dispatchers + Field Supervisors + Team
  // Leaders also get Rider Coord. since they're hands-on dispatch personas.
  const allowCoord = adminRole === "supervisor" || adminRole === "admin" || adminRole === "dispatcher" || adminRole === "operating_partner" || adminRole === "developer" || adminRole === "team_leader" || adminRole === "field_supervisor";
  return (
    <Tabs value={sub} onValueChange={setSub}>
      <TabsList className="sr-only" data-testid="driver-mgmt-subtabs">
        {allowCoord && (
          <TabsTrigger value="coordination" data-testid="dm-tab-coordination">Rider Coord.</TabsTrigger>
        )}
        {allowCoord && (
          <TabsTrigger value="coordinator-search" data-testid="dm-tab-coordinator-search">Coordinator Search</TabsTrigger>
        )}
        <TabsTrigger value="book-ride" data-testid="dm-tab-book-ride">Dispatch Booking</TabsTrigger>
        <TabsTrigger value="drivers" data-testid="dm-tab-drivers">Regular Drivers</TabsTrigger>
        <TabsTrigger value="team-leaders" data-testid="dm-tab-team-leaders">Team Leaders</TabsTrigger>
        <TabsTrigger value="field-supervisors" data-testid="dm-tab-field-supervisors">Field Supervisors</TabsTrigger>
        <TabsTrigger value="dispatchers" data-testid="dm-tab-dispatchers">Dispatchers</TabsTrigger>
        <TabsTrigger value="supervisors" data-testid="dm-tab-supervisors">Supervisors</TabsTrigger>
        <TabsTrigger value="developers" data-testid="dm-tab-developers">Super Users</TabsTrigger>
        <TabsTrigger value="admins" data-testid="dm-tab-admins">Admins</TabsTrigger>
        <TabsTrigger value="customers" data-testid="dm-tab-customers">Customers</TabsTrigger>
        <TabsTrigger value="rides" data-testid="dm-tab-rides">Completed Rides</TabsTrigger>
        <TabsTrigger value="pending" data-testid="dm-tab-pending">Pending Rides</TabsTrigger>
      </TabsList>
      {allowCoord && <TabsContent value="coordination"><RideCoordinationTab/></TabsContent>}
      {allowCoord && (
        <TabsContent value="coordinator-search">{sub === "coordinator-search" && <CoordinatorSearchTab/>}</TabsContent>
      )}
      <TabsContent value="book-ride"><BookRideTab/></TabsContent>
      <TabsContent value="drivers"><DriversTab adminRole={adminRole} roleFilter="driver"/></TabsContent>
      <TabsContent value="team-leaders"><DriversTab adminRole={adminRole} roleFilter="team_leader"/></TabsContent>
      <TabsContent value="field-supervisors"><DriversTab adminRole={adminRole} roleFilter="field_supervisor"/></TabsContent>
      <TabsContent value="dispatchers"><DriversTab adminRole={adminRole} roleFilter="dispatcher"/></TabsContent>
      <TabsContent value="supervisors"><DriversTab adminRole={adminRole} roleFilter="supervisor"/></TabsContent>
      <TabsContent value="developers"><DriversTab adminRole={adminRole} roleFilter="developer"/></TabsContent>
      <TabsContent value="admins"><DriversTab adminRole={adminRole} roleFilter="admin"/></TabsContent>
      <TabsContent value="customers"><CustomersTab/></TabsContent>
      <TabsContent value="rides"><RidesTab/></TabsContent>
      <TabsContent value="pending"><PendingTab/></TabsContent>
    </Tabs>
  );
}

// ============================================================================
// Administration group (v2.6.2) — Accounting · Pricing · Payments · Legal ·
// Support · Dev License. Accounting only renders for admin/supervisor (PIN-gated inside).
// ============================================================================
function AdministrationGroup({ adminRole, subsEnabled = true, onSubsToggle }) {
  // v2.7.0 batch DO++ · FEATURE 1 · OP + Developer are absolute super users.
  const allowAccounting = adminRole === "supervisor" || adminRole === "admin" || adminRole === "operating_partner" || adminRole === "developer";
  const [sub, setSub] = useHashSubTab(allowAccounting ? "accounting" : "pricing");
  const navigate = useNavigate();
  // v2.6.9 batch BT · "devlicense" no longer opens a modal — it routes to
  // the dedicated /dev-license page. Sub change handler intercepts the value
  // and navigates instead of swapping tab content.
  const handleSubChange = (v) => {
    if (v === "devlicense") { navigate("/dev-license"); return; }
    setSub(v);
  };
  // Bounce off Subscriptions tab if the feature is disabled while we're on it.
  useEffect(() => { if (!subsEnabled && sub === "subscriptions") setSub(allowAccounting ? "accounting" : "pricing"); }, [subsEnabled, sub, allowAccounting, setSub]);
  return (
    <Tabs value={sub} onValueChange={handleSubChange}>
      <TabsList className="sr-only" data-testid="admin-subtabs">
        {allowAccounting && <TabsTrigger value="accounting" data-testid="adm-tab-accounting">Accounting</TabsTrigger>}
        {subsEnabled && <TabsTrigger value="subscriptions" data-testid="adm-tab-subscriptions">Subscriptions</TabsTrigger>}
        <TabsTrigger value="pricing" data-testid="adm-tab-pricing">Pricing</TabsTrigger>
        <TabsTrigger value="rider-price-page" data-testid="adm-tab-rider-price-page">Rider Price Page</TabsTrigger>
        <TabsTrigger value="front-end" data-testid="adm-tab-front-end">Front End</TabsTrigger>
        <TabsTrigger value="payments" data-testid="adm-tab-payments">Payments</TabsTrigger>
        <TabsTrigger value="integrations" data-testid="adm-tab-integrations">API &amp; Integrations</TabsTrigger>
        <TabsTrigger value="legal" data-testid="adm-tab-legal">Legal</TabsTrigger>
        <TabsTrigger value="support" data-testid="adm-tab-support">Support</TabsTrigger>
        <TabsTrigger value="devlicense" data-testid="adm-tab-devlicense">Dev License</TabsTrigger>
        {/* v2.7.0 batch DM · Developer sub-pages */}
        <TabsTrigger value="developer-portal" data-testid="adm-tab-developer-portal">Developer Portal</TabsTrigger>
        <TabsTrigger value="developer-coding" data-testid="adm-tab-developer-coding">Developer Coding</TabsTrigger>
        <TabsTrigger value="developer-branding" data-testid="adm-tab-developer-branding">Developer Branding</TabsTrigger>
        <TabsTrigger value="api-keys" data-testid="adm-tab-api-keys">API Keys</TabsTrigger>
        <TabsTrigger value="audio-test" data-testid="adm-tab-audio-test">Audio Test Panel</TabsTrigger>
        <TabsTrigger value="role-pins" data-testid="adm-tab-role-pins">Role PINs</TabsTrigger>
        <TabsTrigger value="tex-notify" data-testid="adm-tab-tex-notify">TEX Notification Manager</TabsTrigger>
      </TabsList>
      {allowAccounting && <TabsContent value="accounting"><AccountingTab/></TabsContent>}
      {subsEnabled && <TabsContent value="subscriptions"><SubscriptionsAdminTab/></TabsContent>}
      <TabsContent value="pricing"><PricingTab/></TabsContent>
      <TabsContent value="rider-price-page">{sub === "rider-price-page" && <RiderPricePageTab/>}</TabsContent>
      <TabsContent value="front-end">{sub === "front-end" && <FrontEndTab/>}</TabsContent>
      <TabsContent value="payments"><PaymentsTab onSubsToggle={onSubsToggle}/></TabsContent>
      <TabsContent value="integrations"><IntegrationsTab/></TabsContent>
      <TabsContent value="legal"><LegalTab/></TabsContent>
      <TabsContent value="support"><SupportTab adminRole={adminRole}/></TabsContent>
      {/* v2.7.0 batch DM · Developer sub-page bodies. Lazy-rendered when active. */}
      <TabsContent value="developer-portal">{sub === "developer-portal" && <DeveloperPortalTab/>}</TabsContent>
      <TabsContent value="developer-coding">{sub === "developer-coding" && <DeveloperCodingTab/>}</TabsContent>
      <TabsContent value="developer-branding">{sub === "developer-branding" && <DeveloperBrandingTab/>}</TabsContent>
      <TabsContent value="api-keys">{sub === "api-keys" && <ApiKeysTab/>}</TabsContent>
      <TabsContent value="audio-test">{sub === "audio-test" && <AudioTestPanelTab/>}</TabsContent>
      <TabsContent value="role-pins">{sub === "role-pins" && <RolePinsTab/>}</TabsContent>
      <TabsContent value="tex-notify">{sub === "tex-notify" && <TexNotificationManagerTab/>}</TabsContent>
    </Tabs>
  );
}

function OverviewTab({ onJumpToTab }) {
  const [snap, setSnap] = useState(null);
  const reload = useCallback(() => {
    adminApi.get("/admin/overview-snapshot")
      .then((r) => setSnap(r.data))
      .catch((err) => console.warn("overview snapshot failed", err?.response?.status));
  }, []);
  useEffect(() => { reload(); const t = setInterval(reload, 15_000); return () => clearInterval(t); }, [reload]);

  if (!snap) {
    return <Card className="bg-zinc-900 border-zinc-800 p-8 text-center text-zinc-500" data-testid="overview-loading">Loading snapshot…</Card>;
  }
  const fmtTime = (s) => { try { return new Date(s).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }); } catch { return ""; } };

  return (
    <div className="space-y-4" data-testid="overview-snapshot">
      {/* v2.7.18 · STRICT WIDGET ZONE — the 5-tile "Active Drivers / Active
          Riders / Active Rides / Recent Card $ / Cash Today" strip that
          previously sat here has been RELOCATED to the user-managed
          draggable widget grid above the menu bar. The lower workspace must
          stay free of widgets so dispatchers see the live data lists
          (Dispatch History, Chat Insights, Driver / Rider / Ride / Payment
          rosters) without obstruction.

          v2.7.21 · `DispatchHistory` moved to the BOTTOM of the page
          (after the Cash Today card) per the user's request — gives
          dispatchers the live action lists first, with the historical
          ledger as a scroll-to follow-up. */}

      {/* v2.7.0 batch CX · Chat Insights — top customer intents from chatbot */}
      <ChatInsightsCard onJumpToTab={onJumpToTab}/>

      {/* v2.7.65+E136 · CCVAP · Onboarding Ops badges — pending applications
          + pending CCVAP applications + orphan contractor packets, so
          nothing slips through the cracks. Each tile deep-links to the
          exact admin surface where the review happens. */}
      <OpsBadgesWidget/>

      {/* v2.7.65+E171 · STOP Rate Ledger — surfaces "opt-outs in the last
          30 days" so any spike triggers investigation. Click-through jumps
          to the Consent Audit filtered by twilio_stop_reply. */}
      <StopRateLedgerCard/>

      {/* v2.7.45+E13 · Important Mail · Supervisor Triage (one-click ACK
          card showing the 5 most recent un-acknowledged escalations). The
          widget is intentionally placed high on the Overview so morning
          supervisors see un-cleared important mail before they triage
          anything else. Polls every 30s + listens to the dispatch WS
          for `email.important` push events. */}
      <ImportantInboxCard/>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Active Drivers */}
        <Card className="bg-zinc-900 border-zinc-800 p-4" data-testid="overview-drivers">
          <h3 className="font-bold mb-2 text-sm flex items-center gap-2"><Users className="h-4 w-4 text-amber-400"/> Active Drivers <Badge variant="outline" className="text-[10px] border-zinc-700">{snap.drivers.count}</Badge></h3>
          <div className="space-y-1 max-h-64 overflow-auto">
            {snap.drivers.items.length === 0 && <div className="text-xs text-zinc-500 py-3 text-center">No active drivers right now.</div>}
            {snap.drivers.items.map((d) => (
              <div key={d.id} className="bg-zinc-950 border border-zinc-800 rounded p-2 text-xs flex items-center justify-between" data-testid={`ov-drv-${d.id}`}>
                <div className="min-w-0">
                  <div className="font-semibold truncate">{d.name}</div>
                  <div className="text-[10px] text-zinc-500 uppercase tracking-wider">{d.role}{d.plate_number ? ` · ${d.plate_number}` : ""}</div>
                </div>
                <Badge variant="outline" className={d.on_trip ? "border-orange-400/40 text-orange-300 text-[10px] uppercase" : "border-green-400/40 text-green-400 text-[10px] uppercase"}>{d.on_trip ? "On Trip" : "Idle"}</Badge>
              </div>
            ))}
          </div>
        </Card>

        {/* Active Riders */}
        <Card className="bg-zinc-900 border-zinc-800 p-4" data-testid="overview-riders">
          <h3 className="font-bold mb-2 text-sm flex items-center gap-2"><User className="h-4 w-4 text-amber-400"/> Active Riders <Badge variant="outline" className="text-[10px] border-zinc-700">{snap.riders.count}</Badge></h3>
          <div className="space-y-1 max-h-64 overflow-auto">
            {snap.riders.items.length === 0 && <div className="text-xs text-zinc-500 py-3 text-center">No riders to show.</div>}
            {snap.riders.items.map((c) => (
              <div key={c.id} className="bg-zinc-950 border border-zinc-800 rounded p-2 text-xs flex items-center justify-between" data-testid={`ov-rider-${c.id}`}>
                <div className="min-w-0">
                  <div className="font-semibold truncate">{c.name || "—"}</div>
                  <div className="text-[10px] text-zinc-500 truncate">{c.email} · {c.phone || "—"}</div>
                </div>
                <div className="text-right flex-shrink-0">
                  <div className="text-amber-400 font-bold">{c.ride_count || 0}</div>
                  <div className="text-[9px] uppercase tracking-wider text-zinc-500">rides</div>
                </div>
              </div>
            ))}
          </div>
        </Card>

        {/* Active/Booked Rides */}
        <Card className="bg-zinc-900 border-zinc-800 p-4" data-testid="overview-rides">
          <h3 className="font-bold mb-2 text-sm flex items-center gap-2"><Zap className="h-4 w-4 text-amber-400"/> Rides · 10 active/booked</h3>
          <div className="space-y-1 max-h-64 overflow-auto">
            {snap.active_rides.items.length === 0 && <div className="text-xs text-zinc-500 py-3 text-center">No rides currently in dispatch.</div>}
            {snap.active_rides.items.map((r) => (
              <div key={r.id} className="bg-zinc-950 border border-zinc-800 rounded p-2 text-xs" data-testid={`ov-ride-${r.id}`}>
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <span className="font-mono text-[10px] text-zinc-500">{r.waybill_no || r.id?.slice(0,8).toUpperCase()}</span>
                  <Badge variant="outline" className="text-[9px] uppercase border-amber-400/40 text-amber-300">{(r.status || "").replace(/_/g," ")}</Badge>
                  <span className="font-bold text-amber-400">${(r.total || 0).toFixed(2)}</span>
                </div>
                <div className="truncate text-zinc-200 mt-0.5">{r.origin} → {r.destination}</div>
                <div className="text-[10px] text-zinc-500">{r.customer_name || "—"} · {r.driver_name || "(unassigned)"} · {r.passengers || 1} pax</div>
              </div>
            ))}
          </div>
        </Card>

        {/* Recent Payments */}
        <Card className="bg-zinc-900 border-zinc-800 p-4" data-testid="overview-payments">
          <h3 className="font-bold mb-2 text-sm flex items-center gap-2"><CreditCard className="h-4 w-4 text-amber-400"/> Recent Card Payments</h3>
          <div className="space-y-1 max-h-64 overflow-auto">
            {snap.payments.items.length === 0 && <div className="text-xs text-zinc-500 py-3 text-center">No card payments yet.</div>}
            {snap.payments.items.map((p) => (
              <div key={p.id} className="bg-zinc-950 border border-zinc-800 rounded p-2 text-xs flex items-center justify-between" data-testid={`ov-pay-${p.id}`}>
                <div className="min-w-0">
                  <div className="font-semibold truncate">{p.customer_name || p.waybill_no || "—"}</div>
                  <div className="text-[10px] text-zinc-500 truncate">{p.card_brand ? `${p.card_brand.toUpperCase()} •••• ${p.card_last4 || "----"}` : "Card"} · {fmtTime(p.completed_at || p.created_at)}</div>
                </div>
                <div className="text-right flex-shrink-0">
                  <div className="text-green-400 font-bold">${(p.total || 0).toFixed(2)}</div>
                  <div className="text-[9px] uppercase tracking-wider text-green-400/70">{p.payment_status}</div>
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* Cash total banner */}
      <Card className="bg-zinc-900 border-emerald-400/30 p-4" data-testid="overview-cash">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <h3 className="font-bold text-sm flex items-center gap-2"><DollarSign className="h-4 w-4 text-emerald-400"/> Cash collected · today</h3>
            <p className="text-[11px] text-zinc-500 mt-0.5">Reset every midnight UTC · combines completed rides + Live Meter trips with cash payment</p>
          </div>
          <div className="text-right">
            <div className="text-2xl font-bold meter-display text-emerald-400" data-testid="overview-cash-total">${snap.cash.total.toFixed(2)}</div>
            <div className="text-[10px] uppercase tracking-wider text-zinc-500">{snap.cash.count} transaction{snap.cash.count === 1 ? "" : "s"}</div>
          </div>
        </div>
      </Card>

      {/* v2.7.21 · Dispatch History — relocated from the TOP of the page
          (where it sat directly under the navigation bar) to the BOTTOM
          per user request. Dispatchers now see live action lists first
          (Active Drivers / Riders / Rides / Payments / Cash) and the
          historical ledger after. */}
      <DispatchHistory onJumpToLiveMap={onJumpToTab}/>
    </div>
  );
}

function DriversTab({ adminRole, roleFilter = null }) {
  const [drivers, setDrivers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ email: "", name: "", password: "", pin: "", phone: "", role: "driver", license_number: "", notes: "" });
  // v2.7.63+E48 · Print Badge modal state
  const [badgePrintOpen, setBadgePrintOpen] = useState(false);
  // v2.7.0 batch DO+ · Multi-role manager dialog state
  const [rolesEditing, setRolesEditing] = useState(null);
  const [callerRoles, setCallerRoles] = useState([]);
  useEffect(() => {
    adminApi.get("/admin/me/roles").then((r) => setCallerRoles(r.data?.roles || [])).catch(() => setCallerRoles([]));
  }, []);
  const isSuper = callerRoles.some((r) => ["operating_partner", "developer"].includes(r));
  const isFieldSupervisor = callerRoles.includes("field_supervisor");
  // v2.6.8 batch M · driver dossier viewer
  const [dossier, setDossier] = useState(null);
  const [dossierBusy, setDossierBusy] = useState(false);
  const openDossier = async (id) => {
    setDossierBusy(true);
    try { const { data } = await adminApi.get(`/admin/drivers/${id}/dossier`); setDossier(data); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not load dossier"); }
    finally { setDossierBusy(false); }
  };
  const printDossier = (id) => {
    const base = process.env.REACT_APP_BACKEND_URL || "";
    const token = localStorage.getItem("rtg_admin_token");
    // The export.html endpoint needs the admin Bearer token. We do a fetch
    // with auth and open the resulting HTML in a new tab via a blob URL —
    // browser print dialog handles PDF/printer routing from there.
    fetch(`${base}/api/admin/drivers/${id}/dossier/export.html`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.text() : Promise.reject(r.statusText))
      .then(html => {
        const blob = new Blob([html], { type: "text/html" });
        window.open(URL.createObjectURL(blob), "_blank");
      })
      .catch(err => toast.error(`Print failed: ${err}`));
  };

  const reload = useCallback(() => {
    const url = roleFilter ? `/admin/drivers?role=${encodeURIComponent(roleFilter)}` : "/admin/drivers";
    return adminApi.get(url).then(r => setDrivers(r.data)).catch(()=>{});
  }, [roleFilter]);
  useEffect(() => { reload(); }, [reload]);

  const open_new = () => { setEditing(null); setForm({ email: "", name: "", password: "", pin: "", phone: "", role: "driver", license_number: "", notes: "", gender: "unspecified", first_name: "", last_name: "", employee_id: "" }); setOpen(true); };
  const open_edit = (d) => { setEditing(d); setForm({ email: d.email, name: d.name, password: "", pin: "", phone: d.phone || "", role: d.role || "driver", license_number: d.license_number || "", notes: d.notes || "", gender: d.gender || "unspecified", first_name: d.first_name || "", last_name: d.last_name || "", employee_id: d.employee_id || "" }); setOpen(true); };

  const save = async () => {
    try {
      if (editing) {
        const payload = { name: form.name, phone: form.phone, role: form.role, license_number: form.license_number, notes: form.notes, gender: form.gender, first_name: form.first_name || null, last_name: form.last_name || null, employee_id: form.employee_id || null };
        if (form.password) payload.password = form.password;
        if (form.pin) payload.pin = form.pin;
        await adminApi.patch(`/admin/drivers/${editing.id}`, payload);
        toast.success("Driver updated");
      } else {
        if (!form.email || !form.password || !form.pin || !form.name) return toast.error("Email, name, password, PIN required");
        await adminApi.post("/admin/drivers", form);
        toast.success("Driver onboarded");
      }
      setOpen(false); reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const toggleActive = async (d) => {
    try { await adminApi.patch(`/admin/drivers/${d.id}`, { active: !d.active }); reload(); }
    catch { toast.error("Update failed"); }
  };

  // v2.7.0 batch DO+++ · One-click Promote / Demote helpers.
  // Builds a roles[] mutation that ADDS team_leader (promote) or REMOVES
  // every elevated role and leaves just "driver" (demote). For super callers
  // the backend skips the PIN check; otherwise we prompt for an OP/Dev PIN
  // and forward it as `op_pin` so /admin/drivers/{id}/roles can validate.
  const promote = async (d) => {
    const current = new Set([...(d.roles || []), d.role].filter(Boolean));
    if (current.has("team_leader")) {
      toast.info("Already a Team Leader");
      return;
    }
    current.add("team_leader");
    current.add("driver");
    const next = [...current].filter((r) => r !== "supervisor");
    let body = { roles: next };
    if (!isSuper) {
      const pin = window.prompt("Enter Operating Partner / Developer PIN to promote to Team Leader:");
      if (!pin) return;
      body.op_pin = pin;
    }
    try {
      await adminApi.put(`/admin/drivers/${d.id}/roles`, body);
      toast.success(`Promoted ${d.name || d.email} → Team Leader`);
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Promote failed"); }
  };

  const demote = async (d) => {
    const current = new Set([...(d.roles || []), d.role].filter(Boolean));
    if (!current.has("team_leader")) {
      toast.info("Not currently a Team Leader");
      return;
    }
    const next = [...current].filter((r) => r !== "team_leader" && r !== "supervisor");
    if (!next.includes("driver")) next.push("driver");
    let body = { roles: next };
    if (!isSuper) {
      const pin = window.prompt("Enter Operating Partner / Developer PIN to demote back to Driver:");
      if (!pin) return;
      body.op_pin = pin;
    }
    if (!window.confirm(`Demote ${d.name || d.email} back to Driver?`)) return;
    try {
      await adminApi.put(`/admin/drivers/${d.id}/roles`, body);
      toast.success(`Demoted ${d.name || d.email} → Driver`);
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Demote failed"); }
  };

  const remove = async (d) => {
    if (!window.confirm(`Delete driver ${d.email}?`)) return;
    // v2.7.0 batch DO+ · Hierarchical guard. Operating Partner / Developer
    // (and the master admin) delete without a PIN; everyone else must enter
    // the OP/Dev PIN — the backend enforces this regardless of what we send.
    let body = {};
    if (!isSuper && adminRole !== "admin" && adminRole !== "supervisor") {
      const pin = window.prompt("Enter Operating Partner / Developer PIN to authorize this delete:");
      if (!pin) return;
      body = { op_pin: pin };
    }
    try { await adminApi.delete(`/admin/drivers/${d.id}`, { data: body }); toast.success("Deleted"); reload(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  const resetCreds = async (d) => {
    const newPwd = window.prompt(`Reset password for ${d.name} (${d.email})?\n\nLeave blank to skip. New password (6+ chars):`);
    const newPin = window.prompt(`New PIN for ${d.name} (4–8 digits)?\n\nLeave blank to skip:`);
    const payload = {};
    if (newPwd && newPwd.length >= 6) payload.new_password = newPwd;
    if (newPin && /^\d{4,8}$/.test(newPin)) payload.new_pin = newPin;
    if (Object.keys(payload).length === 0) return;
    if (!window.confirm(`Apply reset to ${d.email}? They'll need to log in with the new credentials immediately.`)) return;
    try {
      const { data } = await adminApi.post(`/admin/drivers/${d.id}/reset-credentials`, payload);
      toast.success(`Reset · ${(data.reset || []).join(", ").replace(/_hash/g,"")}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Reset failed"); }
  };

  // v2.7.0 batch DO++ · FEATURE 3 · The same DriversTab component renders 3
  // segregated views (Regular Drivers · Team Leaders · Field Supervisors).
  // Backend now applies the role filter server-side via `?role=`, so we just
  // show what the API returned.
  const filteredDrivers = drivers;

  const tabTitle = roleFilter === "team_leader" ? "Team Leaders"
    : roleFilter === "field_supervisor" ? "Field Supervisors"
    : roleFilter === "driver" ? "Regular Drivers"
    : roleFilter === "dispatcher" ? "Dispatchers"
    : roleFilter === "supervisor" ? "Supervisors"
    : roleFilter === "developer" ? "Super Users (Developers)"
    : roleFilter === "admin" ? "Admins"
    : "All Drivers";

  return (
    <Card className="bg-zinc-900 border-zinc-800 p-5">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-bold" data-testid={`drv-list-title-${roleFilter || "all"}`}>{tabTitle} ({filteredDrivers.length})</h3>
        <Button data-testid="add-driver-btn" onClick={open_new} className="bg-amber-400 hover:bg-amber-300 text-zinc-950 font-bold"><Plus className="h-4 w-4 mr-1"/>Onboard Driver</Button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-[10px] uppercase tracking-wider text-zinc-500 border-b border-zinc-800">
            <tr><th className="text-left p-2">Name</th><th className="text-left p-2">Email</th><th className="text-left p-2">Phone</th><th className="text-left p-2">Role</th><th className="text-left p-2">Gender</th><th className="text-left p-2">Active</th><th className="text-right p-2">Actions</th></tr>
          </thead>
          <tbody>
            {filteredDrivers
              .filter((d) => {
                // v2.7.0 batch DO+ · Field Supervisor cannot SEE privileged
                // accounts (dispatch / operating_partner / developer).
                if (!isFieldSupervisor || isSuper) return true;
                const rs = new Set([...(d.roles || []), d.role]);
                return !["dispatcher", "operating_partner", "developer", "supervisor"].some((r) => rs.has(r));
              })
              .map(d => (
              <tr key={d.id} className="border-b border-zinc-800 hover:bg-zinc-950/50" data-testid={`drv-${d.id}`}>
                <td className="p-2 font-semibold">
                  <span className="inline-flex items-center gap-1.5">
                    <UserPresenceDot username={d.email || d.username || d.id}/>
                    <span>{d.name}</span>
                    <GoogleAuthBadge authProvider={d.auth_provider}/>
                  </span>
                </td>
                <td className="p-2 text-zinc-400">{d.email}</td>
                <td className="p-2 text-zinc-400">{d.phone || "—"}</td>
                <td className="p-2"><Badge variant="outline" className={
                  (d.role === "supervisor" || d.role === "operating_partner") ? "border-amber-400/40 text-amber-400" :
                  d.role === "developer" ? "border-emerald-400/40 text-emerald-300" :
                  d.role === "dispatcher" ? "border-blue-400/40 text-blue-300" :
                  d.role === "team_leader" ? "border-purple-400/40 text-purple-300" :
                  d.role === "field_supervisor" ? "border-indigo-400/40 text-indigo-300" :
                  "border-zinc-700"
                }>{(d.role === "supervisor" ? "operating partner" : (d.role || "driver")).replace(/_/g," ")}</Badge></td>
                <td className="p-2">
                  <Badge variant="outline" className={
                    d.gender === "female" ? "border-pink-400/40 text-pink-300 text-[10px] uppercase" :
                    d.gender === "male" ? "border-sky-400/40 text-sky-300 text-[10px] uppercase" :
                    "border-zinc-700 text-zinc-500 text-[10px] uppercase"
                  } data-testid={`drv-gender-${d.id}`}>
                    {d.gender === "female" ? "F" : d.gender === "male" ? "M" : "—"}
                  </Badge>
                </td>
                <td className="p-2"><Switch checked={d.active !== false} onCheckedChange={() => toggleActive(d)} data-testid={`drv-active-${d.id}`}/></td>
                <td className="p-2 text-right">
                  <Button size="sm" variant="ghost" onClick={() => openDossier(d.id)} className="text-amber-400 text-[10px]" data-testid={`drv-dossier-${d.id}`} title="View internal docs + permits"><FileBadge2 className="h-4 w-4 mr-0.5"/>Dossier</Button>
                  <Button size="sm" variant="ghost" onClick={() => open_edit(d)} data-testid={`drv-edit-${d.id}`}>Edit</Button>
                  {/* v2.7.0 batch DO+++ · one-click hierarchy shortcut */}
                  {(() => {
                    const rs = new Set([...(d.roles || []), d.role].filter(Boolean));
                    const isTL = rs.has("team_leader");
                    // Don't show promote/demote on privileged accounts to keep
                    // the row tidy (use the full Roles dialog instead).
                    if (rs.has("operating_partner") || rs.has("developer") || rs.has("supervisor") || rs.has("dispatcher")) return null;
                    return isTL ? (
                      <Button size="sm" variant="ghost" onClick={() => demote(d)} className="text-purple-300 text-[10px]" data-testid={`drv-demote-${d.id}`} title="Demote to regular Driver">
                        ↓ Demote
                      </Button>
                    ) : (
                      <Button size="sm" variant="ghost" onClick={() => promote(d)} className="text-emerald-300 text-[10px]" data-testid={`drv-promote-${d.id}`} title="Promote to Team Leader">
                        ↑ Promote
                      </Button>
                    );
                  })()}
                  <Button size="sm" variant="ghost" onClick={() => setRolesEditing(d)} className="text-emerald-300 text-[10px]" data-testid={`drv-roles-${d.id}`} title="Manage multi-role array"><ShieldCheck className="h-4 w-4 mr-0.5"/>Roles</Button>
                  <Button size="sm" variant="ghost" onClick={() => resetCreds(d)} className="text-amber-400 text-[10px]" data-testid={`drv-reset-${d.id}`} title="Reset password / PIN">Reset</Button>
                  {!d.is_protected && (isSuper || adminRole === "admin" || adminRole === "supervisor" || adminRole === "dispatcher" || (d.role !== "supervisor" && d.role !== "operating_partner" && d.role !== "developer")) &&
                    <Button size="sm" variant="ghost" onClick={() => remove(d)} className="text-red-400" data-testid={`drv-del-${d.id}`}><Trash2 className="h-4 w-4"/></Button>
                  }
                  {d.is_protected && <span className="ml-1 text-[10px] uppercase tracking-widest text-amber-400 border border-amber-400/40 rounded px-1.5 py-0.5" data-testid={`drv-protected-${d.id}`}>SYSTEM</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-700 text-zinc-100 max-w-lg">
          <DialogHeader><DialogTitle>{editing ? `Edit · ${editing.name}` : "Onboard New Driver"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label className="text-xs">Full Name</Label><Input data-testid="form-name" value={form.name} onChange={e=>setForm({...form, name: e.target.value})} className="bg-zinc-950 border-zinc-700 mt-1"/></div>
            <div className="col-span-2"><Label className="text-xs">Email</Label><Input data-testid="form-email" type="email" disabled={!!editing} value={form.email} onChange={e=>setForm({...form, email: e.target.value})} className="bg-zinc-950 border-zinc-700 mt-1"/></div>
            <div><Label className="text-xs">{editing ? "New Password (optional)" : "Password"}</Label><Input data-testid="form-password" type="password" value={form.password} onChange={e=>setForm({...form, password: e.target.value})} className="bg-zinc-950 border-zinc-700 mt-1"/></div>
            <div><Label className="text-xs">{editing ? "New PIN (optional)" : "PIN (4-6 digits)"}</Label><Input data-testid="form-pin" maxLength={6} value={form.pin} onChange={e=>setForm({...form, pin: e.target.value.replace(/\D/g,"")})} className="bg-zinc-950 border-zinc-700 mt-1"/></div>
            <div><Label className="text-xs">Phone</Label><Input data-testid="form-phone" value={form.phone} onChange={e=>setForm({...form, phone: e.target.value})} className="bg-zinc-950 border-zinc-700 mt-1"/></div>
            <div><Label className="text-xs">Role</Label>
              <Select value={form.role} onValueChange={v=>setForm({...form, role: v})}>
                <SelectTrigger data-testid="form-role" className="bg-zinc-950 border-zinc-700 mt-1"><SelectValue/></SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-700 text-zinc-100">
                  <SelectItem value="driver">Driver (limited)</SelectItem>
                  <SelectItem value="dispatcher">Dispatcher (admin · no payments/pricing/legal)</SelectItem>
                  <SelectItem value="team_leader">Team Leader (admin · no payments/pricing/legal)</SelectItem>
                  <SelectItem value="field_supervisor">Field Supervisor (limited admin, no privileged-acct ops)</SelectItem>
                  <SelectItem value="operating_partner" disabled={adminRole !== "admin" && adminRole !== "supervisor" && adminRole !== "operating_partner"}>Operating Partner (full admin — SUPER USER)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2"><Label className="text-xs">License Number</Label><Input value={form.license_number} onChange={e=>setForm({...form, license_number: e.target.value})} className="bg-zinc-950 border-zinc-700 mt-1"/></div>
            {/* v2.7.63+E47 · Operator identity fields used by the 4-Window
                Command Console overlay + external displays. Dispatch,
                Supervisor, and General Partner tiers can all edit these. */}
            <div className="col-span-1">
              <Label className="text-xs">First Name</Label>
              <Input
                value={form.first_name || ""}
                onChange={e=>setForm({...form, first_name: e.target.value})}
                placeholder="Jane"
                className="bg-zinc-950 border-zinc-700 mt-1"
                data-testid="driver-edit-first-name"
              />
            </div>
            <div className="col-span-1">
              <Label className="text-xs">Last Name</Label>
              <Input
                value={form.last_name || ""}
                onChange={e=>setForm({...form, last_name: e.target.value})}
                placeholder="Doe"
                className="bg-zinc-950 border-zinc-700 mt-1"
                data-testid="driver-edit-last-name"
              />
            </div>
            <div className="col-span-2">
              <div className="flex items-center justify-between">
                <Label className="text-xs">Employee ID <span className="text-zinc-500">· shown on external command consoles</span></Label>
                {editing && (
                  <button
                    type="button"
                    onClick={() => setBadgePrintOpen(true)}
                    className="text-[10px] uppercase tracking-widest font-bold text-amber-300 hover:text-amber-200 flex items-center gap-1"
                    data-testid="driver-edit-print-badge-btn"
                    title="Print CR80 employee ID card"
                  >
                    <Printer className="h-3 w-3"/>Print Badge
                  </button>
                )}
              </div>
              <Input
                value={form.employee_id || ""}
                onChange={e=>setForm({...form, employee_id: e.target.value.toUpperCase()})}
                placeholder="EMP-1024"
                className="bg-zinc-950 border-zinc-700 mt-1 meter-display font-mono"
                data-testid="driver-edit-employee-id"
              />
            </div>
            <div className="col-span-2">
              <Label className="text-xs">Gender (driver-roster routing)</Label>
              <select
                data-testid="driver-edit-gender"
                value={form.gender || "unspecified"}
                onChange={(e) => setForm({...form, gender: e.target.value})}
                className="w-full bg-zinc-950 border border-zinc-700 rounded mt-1 px-3 py-2 text-sm text-zinc-100"
              >
                <option value="unspecified">Unspecified / Any</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
              </select>
            </div>
            <div className="col-span-2"><Label className="text-xs">Notes</Label><Input value={form.notes} onChange={e=>setForm({...form, notes: e.target.value})} className="bg-zinc-950 border-zinc-700 mt-1"/></div>
          </div>
          <Button data-testid="form-save-btn" onClick={save} className="bg-amber-400 hover:bg-amber-300 text-zinc-950 font-bold">{editing ? "Save Changes" : "Onboard"}</Button>
        </DialogContent>
      </Dialog>

      {/* v2.6.8 batch M · Driver Dossier viewer */}
      <DriverDossierDialog
        dossier={dossier}
        busy={dossierBusy}
        onClose={() => setDossier(null)}
        onPrint={() => dossier && printDossier(dossier.id)}
        onToggleVerification={async (field, value) => {
          const pin = window.prompt("Enter your Operating Partner / admin PIN to update attestation:");
          if (!pin) return;
          try {
            await adminApi.put(`/admin/drivers/${dossier.id}/verifications`, { pin, [field]: value });
            setDossier(d => ({ ...d, [field]: value }));
            toast.success(`${field === "verified_osbi" ? "OSBI" : "DMV"} attestation ${value ? "set" : "cleared"}`);
          } catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
        }}
        onSaveIdentity={async (patch) => {
          // v2.7.65+E190 · Update Emp ID / Positions from the dossier.
          // PATCH endpoint enforces Platform Owner lock server-side.
          try {
            const clean = {};
            if (Object.prototype.hasOwnProperty.call(patch, "employee_id")) {
              clean.employee_id = (patch.employee_id || "").toUpperCase().trim();
            }
            if (Object.prototype.hasOwnProperty.call(patch, "positions")) {
              clean.positions = Array.isArray(patch.positions) ? patch.positions : [];
            }
            await adminApi.patch(`/admin/drivers/${dossier.id}`, clean);
            setDossier(d => ({ ...d, ...clean }));
            reload();
            toast.success("Identity updated");
          } catch (e) {
            toast.error(e?.response?.data?.detail || "Save failed");
            throw e;
          }
        }}
        adminRole={adminRole}
      />
      <RoleManagerDialog
        open={!!rolesEditing}
        onOpenChange={(o) => !o && setRolesEditing(null)}
        driver={rolesEditing}
        onSaved={reload}
      />

      {/* v2.7.63+E48 · Printable Employee ID Badge modal */}
      {badgePrintOpen && editing && (
        <PrintableEmployeeBadge
          driver={{ ...editing, ...form }}
          onClose={() => setBadgePrintOpen(false)}
        />
      )}
    </Card>
  );
}

// v2.6.8 batch M · Driver Dossier dialog — visible to supervisor/dispatch.
// Shows internal docs (OSBI, taxi license, permits) + attestations toggles.
// v2.6.8 batch O · adds a "Send Notification" panel so dispatch can fire an
// in-app reminder (e.g. missing TAXIcab Permit) without leaving the dossier.
function DriverDossierDialog({ dossier, busy, onClose, onPrint, onToggleVerification, onSaveIdentity, adminRole, autoSave = false }) {
  if (!dossier && !busy) return null;
  const permits = dossier?.taxi_permits || [];
  const taxiLic = dossier?.taxi_license_url
    || (dossier?.taxi_license_file_id ? `${process.env.REACT_APP_BACKEND_URL || ""}/api/uploads/${dossier.taxi_license_file_id}` : null);
  const osbi = dossier?.osbi_doc_url
    || (dossier?.osbi_doc_file_id ? `${process.env.REACT_APP_BACKEND_URL || ""}/api/uploads/${dossier.osbi_doc_file_id}` : null);
  const canToggle = adminRole === "admin" || adminRole === "supervisor" || adminRole === "dispatcher" || adminRole === "operating_partner" || adminRole === "developer" || adminRole === "team_leader" || adminRole === "field_supervisor";
  // v2.7.65+E190 · Same role gate as `canToggle` reused for identity edits
  // (Emp ID / Positions). Save actually round-trips through the existing
  // `PATCH /admin/drivers/{id}` endpoint which already enforces the
  // Platform-Owner lock server-side, so no client-side privilege leak.
  const canEditIdentity = canToggle && !!onSaveIdentity;
  return (
    <Dialog open={!!dossier || busy} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-zinc-900 border-zinc-700 text-zinc-100 max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="driver-dossier-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between gap-2">
            <span className="flex items-center gap-2"><FileBadge2 className="h-5 w-5 text-amber-400"/>Driver Dossier</span>
            <Button size="sm" onClick={onPrint} className="bg-amber-400 hover:bg-amber-300 text-zinc-950 font-bold" data-testid="dossier-print-btn">
              <Printer className="h-4 w-4 mr-1"/>Print / PDF
            </Button>
          </DialogTitle>
        </DialogHeader>
        {busy && <div className="py-8 text-center text-zinc-400"><Loader2 className="h-5 w-5 animate-spin inline mr-2"/>Loading dossier…</div>}
        {dossier && (
          <div className="space-y-4 text-sm">
            <div>
              <div className="text-lg font-bold">{dossier.name}</div>
              <div className="text-xs text-zinc-400">{dossier.email} · {dossier.phone || "—"} · {(dossier.role || "driver").replace("_"," ")}</div>
            </div>

            {/* v2.7.65+E190 · Identity card — inline edit for Emp ID + Positions
                (the same values shown in the left-sidebar profile footer).
                Uses PATCH /admin/drivers/{id} which is Platform-Owner-locked
                on the backend so lower roles can't demote a supreme account. */}
            <DossierIdentityCard
              dossier={dossier}
              canEdit={canEditIdentity}
              onSave={onSaveIdentity}
              autoSave={autoSave}
            />

            <Card className="bg-zinc-950 border-zinc-800 p-3 space-y-2" data-testid="dossier-attestations">
              <div className="text-[10px] uppercase tracking-wider text-amber-400 font-bold">Attestations</div>
              <AttestRow
                label="OSBI Fingerprint Background Check"
                verified={!!dossier.verified_osbi}
                canToggle={canToggle}
                onToggle={() => onToggleVerification("verified_osbi", !dossier.verified_osbi)}
                testid="dossier-osbi-toggle"
              />
              <AttestRow
                label="Oklahoma DMV Driving Record Verified"
                verified={!!dossier.verified_dmv}
                canToggle={canToggle}
                onToggle={() => onToggleVerification("verified_dmv", !dossier.verified_dmv)}
                testid="dossier-dmv-toggle"
              />
              {dossier.verifications_updated_at && (
                <div className="text-[10px] text-zinc-500">Last updated {new Date(dossier.verifications_updated_at).toLocaleString()} by {dossier.verifications_updated_by}</div>
              )}
            </Card>

            <Card className="bg-zinc-950 border-zinc-800 p-3" data-testid="dossier-vehicle">
              <div className="text-[10px] uppercase tracking-wider text-amber-400 font-bold mb-1">Vehicle</div>
              <div className="text-sm font-semibold">{dossier.vehicle_year || ""} {dossier.vehicle_make || "—"} {dossier.vehicle_model || ""}</div>
              <div className="text-xs text-zinc-400">{dossier.vehicle_color || ""}{dossier.plate_number ? ` · ${dossier.plate_number}` : ""}{dossier.vin ? ` · VIN ${dossier.vin}` : ""}</div>
              {dossier.current_odometer ? <div className="text-[11px] text-zinc-500 mt-1">Odometer: {dossier.current_odometer.toLocaleString()} mi</div> : null}
            </Card>

            <Card className="bg-zinc-950 border-zinc-800 p-3 space-y-2" data-testid="dossier-internal-docs">
              <div className="text-[10px] uppercase tracking-wider text-amber-400 font-bold">Internal Documents</div>
              <DocRow label="Insurance" url={dossier.insurance_doc_url}/>
              <DocRow label="Vehicle Registration" url={dossier.registration_doc_url}/>
              <DocRow label="OSBI Background Check" url={osbi} uploadedAt={dossier.osbi_doc_uploaded_at}/>
              <DocRow label="Taxi Cab License (taxi-permit-photo)" url={taxiLic} uploadedAt={dossier.taxi_license_uploaded_at}/>
            </Card>

            <Card className="bg-zinc-950 border-zinc-800 p-3 space-y-2" data-testid="dossier-permits">
              <div className="text-[10px] uppercase tracking-wider text-amber-400 font-bold">City Taxi Permits · {permits.length}</div>
              {permits.length === 0 && <div className="text-[11px] text-zinc-600 italic">None on file.</div>}
              {permits.map(p => (
                <div key={p.id} className="flex items-center gap-2 border border-zinc-800 rounded p-2" data-testid={`dossier-permit-${p.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-semibold">{p.permit_number || "—"} · {p.city || "—"}</div>
                    <div className="text-[10px] text-zinc-500">{p.valid_from || "—"} → {p.valid_to || "—"}{p.notes ? " · " + p.notes : ""}</div>
                  </div>
                  {p.file_url && <Button size="sm" variant="ghost" onClick={() => window.open(p.file_url, "_blank")} className="text-amber-400 text-[10px]"><ImageIcon className="h-3.5 w-3.5 mr-0.5"/>View</Button>}
                </div>
              ))}
            </Card>

            <NotifyDriverSection driverId={dossier.id} driverName={dossier.name}/>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

/* v2.7.65+E190 · Dossier Identity edit card — Emp ID + Positions
   (mirrors the two fields shown on the left-sidebar profile footer). Save
   round-trips through PATCH /admin/drivers/{id}, which enforces the
   Platform-Owner lock server-side. */
function DossierIdentityCard({ dossier, canEdit, onSave, autoSave = false }) {
  const [emp, setEmp] = useState(dossier?.employee_id || "");
  const [positions, setPositions] = useState(Array.isArray(dossier?.positions) ? dossier.positions : []);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState({});
  useEffect(() => {
    setEmp(dossier?.employee_id || "");
    setPositions(Array.isArray(dossier?.positions) ? dossier.positions : []);
  }, [dossier?.id, dossier?.employee_id, dossier?.positions]);
  const dirty =
    (emp || "").toUpperCase().trim() !== (dossier?.employee_id || "").toUpperCase().trim() ||
    JSON.stringify([...positions].sort()) !== JSON.stringify([...(dossier?.positions || [])].sort());
  const save = async () => {
    setSaving(true);
    try {
      await onSave({ employee_id: emp, positions });
      if (autoSave) setSaved((s) => ({ ...s, emp: true, positions: true }));
    } catch {
      /* handled upstream via toast */
    } finally {
      setSaving(false);
    }
  };
  // v2.8+E349 · own-profile auto-save: Emp ID when the field is left,
  // Positions the moment they change. A refused change snaps back.
  const autoCommit = async (field, patch, revert) => {
    if (!autoSave || !canEdit) return;
    setSaving(true);
    try {
      await onSave(patch);
      setSaved((s) => ({ ...s, [field]: true }));
    } catch { revert(); } finally { setSaving(false); }
  };
  const onEmpBlur = () => {
    if ((emp || "").toUpperCase().trim() === (dossier?.employee_id || "").toUpperCase().trim()) return;
    autoCommit("emp", { employee_id: emp }, () => setEmp(dossier?.employee_id || ""));
  };
  const onPositionsChange = (next) => {
    setPositions(next);
    autoCommit("positions", { positions: next }, () => setPositions(Array.isArray(dossier?.positions) ? dossier.positions : []));
  };
  return (
    <Card className="bg-zinc-950 border-zinc-800 p-3 space-y-2" data-testid="dossier-identity">
      <div className="flex items-center justify-between gap-2">
        <div className="text-[10px] uppercase tracking-wider text-amber-400 font-bold">Identity</div>
        {!canEdit && <span className="text-[9px] uppercase tracking-widest text-zinc-500">Read only</span>}
      </div>
      {autoSave && <ProfilePhotoUploader testIdPrefix="dossier-photo"/>}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <div className="flex items-center gap-2">
            <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Emp ID</Label>
            <SavedTick show={saved.emp} testId="dossier-emp-id-saved"/>
          </div>
          <Input
            value={emp}
            onChange={(e) => setEmp(e.target.value.toUpperCase())}
            onBlur={onEmpBlur}
            onKeyDown={(e) => { if (autoSave && e.key === "Enter") e.currentTarget.blur(); }}
            disabled={!canEdit || saving}
            placeholder="e.g. BT-0142"
            className="bg-zinc-900 border-zinc-700 text-zinc-100 mt-1"
            data-testid="dossier-emp-id-input"
          />
        </div>
        <div>
          <div className="flex items-center gap-2">
            <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Positions</Label>
            <SavedTick show={saved.positions} testId="dossier-positions-saved"/>
          </div>
          <div className="mt-1" data-testid="dossier-positions">
            <PositionMultiSelect
              value={positions}
              onChange={autoSave ? onPositionsChange : setPositions}
              disabled={!canEdit || saving}
            />
          </div>
        </div>
      </div>
      {canEdit && (
        <div className="flex items-center gap-2 pt-1">
          <Button
            size="sm"
            onClick={save}
            disabled={!dirty || saving}
            data-testid="dossier-identity-save"
            className="bg-amber-400 hover:bg-amber-300 text-zinc-950 font-extrabold"
          >
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1"/> : null}
            Save Identity
          </Button>
          {dirty && !saving && (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                setEmp(dossier?.employee_id || "");
                setPositions(Array.isArray(dossier?.positions) ? dossier.positions : []);
              }}
              data-testid="dossier-identity-cancel"
              className="text-zinc-300 hover:text-zinc-100 hover:bg-zinc-800"
            >
              Cancel
            </Button>
          )}
        </div>
      )}
    </Card>
  );
}

function AttestRow({ label, verified, canToggle, onToggle, testid }) {
  return (
    <div className="flex items-center gap-2 text-xs">
      <ShieldCheck className={`h-4 w-4 ${verified ? "text-green-400" : "text-zinc-600"}`}/>
      <span className={verified ? "text-zinc-200" : "text-zinc-500"}>{label}</span>
      <div className="ml-auto">
        {canToggle ? (
          <Switch checked={verified} onCheckedChange={onToggle} data-testid={testid}/>
        ) : (
          <span className={`text-[9px] uppercase tracking-wider font-bold ${verified ? "text-green-400" : "text-zinc-500"}`}>{verified ? "Verified" : "Pending"}</span>
        )}
      </div>
    </div>
  );
}

function DocRow({ label, url, uploadedAt }) {
  return (
    <div className="flex items-center gap-2 text-xs">
      <FileText className="h-3.5 w-3.5 text-zinc-500 flex-shrink-0"/>
      <span className="text-zinc-300 flex-1 truncate">{label}</span>
      {url ? (
        <Button size="sm" variant="ghost" onClick={() => window.open(url, "_blank")} className="text-amber-400 text-[10px]">
          <ImageIcon className="h-3.5 w-3.5 mr-0.5"/>View
        </Button>
      ) : (
        <span className="text-[10px] text-zinc-600 italic">not uploaded</span>
      )}
      {uploadedAt && <span className="text-[9px] text-zinc-600 ml-1">{new Date(uploadedAt).toLocaleDateString()}</span>}
    </div>
  );
}

// v2.6.8 batch O · NotifyDriverSection — dispatch sends an in-app notification
// to the driver from inside the dossier dialog. Two modes: 1-click "Permit
// Reminder" preset, or free-form message via the textarea below.
const NOTIFY_PRESETS = [
  { kind: "missing_taxi_permit", label: "TAXIcab Permit missing", icon: FileBadge2 },
  { kind: "missing_osbi_doc",    label: "OSBI Background needed", icon: ShieldCheck },
  { kind: "missing_dmv_record",  label: "DMV record review",      icon: ShieldCheck },
  { kind: "vehicle_maintenance_due", label: "Vehicle maintenance", icon: Gauge },
];
function NotifyDriverSection({ driverId, driverName }) {
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(null);
  const send = async (kind, customBody) => {
    setBusy(kind);
    try {
      await adminApi.post(`/admin/drivers/${driverId}/notify`, { kind, body: customBody || "" });
      toast.success(`${kind === "general" ? "Message" : "Reminder"} sent to ${driverName}`);
      if (kind === "general") setBody("");
    } catch (e) { toast.error(e?.response?.data?.detail || "Send failed"); }
    finally { setBusy(null); }
  };
  return (
    <Card className="bg-zinc-950 border-amber-400/30 p-3 space-y-2" data-testid="dossier-notify">
      <div className="flex items-center justify-between gap-2">
        <div className="text-[10px] uppercase tracking-wider text-amber-400 font-bold flex items-center gap-1"><Bell className="h-3.5 w-3.5"/>Send in-app reminder</div>
        <span className="text-[9px] text-zinc-500">Driver sees it on their dashboard immediately</span>
      </div>
      <div className="grid grid-cols-2 gap-2">
        {NOTIFY_PRESETS.map(p => (
          <Button
            key={p.kind}
            size="sm"
            variant="outline"
            disabled={!!busy}
            onClick={() => send(p.kind)}
            data-testid={`notify-preset-${p.kind}`}
            className="bg-zinc-900 border-zinc-700 hover:border-amber-400/60 text-[11px] justify-start"
          >
            {busy === p.kind ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1"/> : <p.icon className="h-3.5 w-3.5 mr-1 text-amber-400"/>}
            {p.label}
          </Button>
        ))}
      </div>
      <div className="pt-1">
        <textarea
          rows={2}
          placeholder="Custom message (optional)…"
          value={body}
          onChange={e => setBody(e.target.value)}
          data-testid="notify-custom-body"
          className="w-full bg-zinc-900 border border-zinc-700 rounded px-2 py-1.5 text-xs text-zinc-100 placeholder:text-zinc-600 focus:border-amber-400/60 focus:outline-none resize-y"
        />
        <Button
          size="sm"
          onClick={() => send("general", body)}
          disabled={!body.trim() || !!busy}
          data-testid="notify-send-custom"
          className="mt-2 w-full bg-amber-400 hover:bg-amber-300 text-zinc-950 font-bold h-9 disabled:bg-zinc-700 disabled:text-zinc-500"
        >
          {busy === "general" ? <Loader2 className="h-4 w-4 animate-spin"/> : <>Send Message</>}
        </Button>
      </div>
    </Card>
  );
}

function LiveTab() {
  const [drivers, setDrivers] = useState([]);
  const [activeTrips, setActiveTrips] = useState([]);
  const [panicEvents, setPanicEvents] = useState([]);
  const { cfg } = useSiteConfig();
  const gpsLogo = cfg?.global?.taxi_commander_gps_logo_url;
  // v2.6.9 batch BI · pull admin JWT from local storage for the WS upgrade.
  const adminToken = typeof window !== "undefined" ? localStorage.getItem("rtg_admin_token") : null;
  useEffect(() => {
    const load = () => {
      adminApi.get("/admin/live").then(r => setDrivers(r.data)).catch(()=>{});
      adminApi.get("/admin/active-trips").then(r => setActiveTrips(r.data)).catch(()=>{});
      adminApi.get("/admin/panic-events", { params: { only_active: true } }).then(r => setPanicEvents(r.data?.events || [])).catch(()=>{});
    };
    load(); const t = setInterval(load, 8000); return () => clearInterval(t);
  }, []);

  const resolvePanic = async (eventId, name) => {
    if (!window.confirm(`Mark panic event from ${name} as RESOLVED?`)) return;
    try {
      await adminApi.post(`/panic/${eventId}/resolve`);
      toast.success("Panic resolved");
      // v2.7.5 Phase 4 · stop the SOS audio loop
      try { window.dispatchEvent(new CustomEvent("rtg:panic_acknowledged", { detail: { id: eventId }})); } catch { /* noop */ }
    }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  // Build a Google Maps embed showing all driver pings as a single layered map
  const center = drivers[0] ? { lat: drivers[0].lat, lng: drivers[0].lng } : { lat: 35.5276, lng: -98.7073 };
  const onlineCount = drivers.filter(d => d.is_online).length;
  const onTripCount = drivers.filter(d => d.on_trip).length;
  const totalActiveFare = activeTrips.reduce((s, t) => s + (t.current_fare || 0), 0);

  return (
    <div className="space-y-4">
      {/* v2.6.9 batch CF · TAXI COMMANDER GPS brand mark — RELOCATED in
          batch CH. Moved from a centered position above the map to inline
          beside the "Live Dispatch Map" h3 (scaled down 50% to size="xs"). */}
      {panicEvents.length > 0 && (
        <Card className="bg-red-950/40 border-red-500/60 p-4 panic-pulse" data-testid="panic-banner">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-bold text-red-300 flex items-center gap-2 text-base"><AlertTriangle className="h-5 w-5"/>SOS · {panicEvents.length} ACTIVE PANIC EVENT{panicEvents.length !== 1 ? "S" : ""}</h3>
          </div>
          <div className="space-y-2">
            {panicEvents.map(p => (
              <div key={p.id} className="bg-zinc-950 border border-red-500/40 rounded p-3 flex items-center justify-between gap-3 flex-wrap" data-testid={`panic-event-${p.id}`}>
                <div className="min-w-0">
                  <div className="text-sm font-bold text-red-300">{p.driver_name}</div>
                  <div className="text-[11px] text-zinc-400">{new Date(p.created_at).toLocaleString()}</div>
                  {p.note && <div className="text-[11px] text-amber-400 mt-0.5">⚠ {p.note}</div>}
                  <div className="text-[11px] text-zinc-500 mt-0.5">SMS: {p.sms_sent_to?.length || 0} sent · {p.sms_skipped_to?.length || 0} skipped</div>
                </div>
                <div className="flex gap-2">
                  {p.gps_link && (
                    <a href={p.gps_link} target="_blank" rel="noreferrer" className="text-xs px-3 py-1.5 rounded border border-amber-400/40 text-amber-400 hover:bg-amber-400/10" data-testid={`panic-gps-${p.id}`}>OPEN GPS</a>
                  )}
                  {p.driver_phone && (
                    <a href={`tel:${p.driver_phone.replace(/[^0-9+]/g,"")}`} className="text-xs px-3 py-1.5 rounded border border-red-400/40 text-red-300 hover:bg-red-500/10" data-testid={`panic-call-${p.id}`}>CALL DRIVER</a>
                  )}
                  <Button size="sm" onClick={() => resolvePanic(p.id, p.driver_name)} className="h-7 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 text-xs" data-testid={`panic-resolve-${p.id}`}>RESOLVE</Button>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="bg-zinc-900 border-zinc-800 p-5 lg:col-span-2 min-h-[400px]">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-bold flex items-center gap-2" data-testid="live-dispatch-map-header">
              {/* v2.6.9 batch CH · GPS logo placed directly to the LEFT of the
                  "Live Dispatch Map" text. Scaled to xs (48 px) — about 50%
                  of its previous md (96 px) size. */}
              {gpsLogo && (
                <BrandLogo src={gpsLogo} alt="TAXIcommander Live Map GPS" size="xs" testid="live-gps-brand-mark"/>
              )}
              <Map className="h-4 w-4 text-amber-400"/>Live Dispatch Map
            </h3>
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider">
              <Badge variant="outline" className="border-green-400/50 text-green-400" data-testid="live-online-count">{onlineCount} LIVE</Badge>
              <Badge variant="outline" className="border-amber-400/50 text-amber-400" data-testid="live-on-trip-count">{onTripCount} ON TRIP</Badge>
              {/* v2.7.63+E47 · 4-Window Command Console launcher */}
              <CommandConsoleLauncher/>
            </div>
          </div>
          {/* v2.6.9 batch BI · ALWAYS render the live WebSocket map (so the
              UI shows the dispatch grid + the streaming pill even when no
              drivers have pinged via the old HTTP poll yet).
              v2.7.63+E47 · Removed the "No drivers have pinged GPS" empty-
              state overlay per operator directive — it was distracting during
              off-peak hours when 0 online is normal. Empty-state signaling
              now lives in the LIVE / ON TRIP pill badges (0 / 0) which are
              already visible top-right of the header. */}
          <div className="relative">
            <LiveDispatchMap
              token={adminToken}
              active={true}
              height="420px"
              fullscreenEnabled={true}
            />
          </div>
        </Card>
        <Card className="bg-zinc-900 border-zinc-800 p-5">
          <h3 className="font-bold mb-3">Active Drivers ({drivers.length})</h3>
          <div className="space-y-2 max-h-[460px] overflow-auto">
            {drivers.length === 0 && <div className="text-zinc-500 text-sm">No active drivers.</div>}
            {drivers.map(d => (
              <div key={d.driver_id} className="bg-zinc-950 border border-zinc-800 rounded p-3 text-xs" data-testid={`live-${d.driver_id}`}>
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className={`relative flex h-2 w-2 flex-shrink-0`}>
                      {d.is_online && <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>}
                      <span className={`relative inline-flex rounded-full h-2 w-2 ${d.is_online ? "bg-green-400" : "bg-zinc-600"}`}/>
                    </span>
                    <span className="font-bold text-amber-400 truncate">{d.driver?.name || "Unknown"}</span>
                  </div>
                  <Badge variant="outline" className={d.on_trip ? "border-amber-400/50 text-amber-400" : (d.is_online ? "border-green-400/50 text-green-400" : "border-zinc-700")}>
                    {d.on_trip ? "ON TRIP" : (d.is_online ? "LIVE" : "OFFLINE")}
                  </Badge>
                </div>
                <div className="text-[10px] text-zinc-500 mt-1 truncate">GPS: {d.lat?.toFixed(5)}, {d.lng?.toFixed(5)}</div>
                {d.driver?.phone && <a href={`tel:${d.driver.phone}`} className="text-zinc-400 hover:text-amber-400 flex items-center gap-1 mt-1"><Phone className="h-3 w-3"/>{d.driver.phone}</a>}
                {d.on_trip && (
                  <div className="mt-1 text-zinc-400 space-y-0.5">
                    <div className="truncate"><MapPin className="h-3 w-3 inline mr-1"/>{d.origin || "—"}</div>
                    <div className="truncate"><MapPin className="h-3 w-3 inline mr-1 text-amber-400"/>{d.destination || "—"}</div>
                    {d.current_fare != null && <div className="text-amber-400 meter-display font-bold">{fmtUsd(d.current_fare)}</div>}
                  </div>
                )}
                <div className="text-[10px] text-zinc-600 mt-1">Last ping: {new Date(d.ts).toLocaleTimeString()}</div>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* Active Trips panel — shows every running trip with its live fare meter */}
      <Card className="bg-zinc-900 border-zinc-800 p-5" data-testid="active-trips-panel">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-bold flex items-center gap-2"><Gauge className="h-4 w-4 text-amber-400"/>Active Trips ({activeTrips.length})</h3>
          {activeTrips.length > 0 && (
            <div className="text-xs text-zinc-400">Combined active fare: <span className="meter-display font-bold text-amber-400 ml-1" data-testid="combined-active-fare">{fmtUsd(totalActiveFare)}</span></div>
          )}
        </div>
        {activeTrips.length === 0 ? (
          <div className="bg-zinc-950 border border-zinc-800 rounded p-6 text-center text-zinc-500 text-sm">No active trips right now.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-[10px] uppercase tracking-wider text-zinc-500 border-b border-zinc-800">
                <tr>
                  <th className="text-left p-2">Driver</th>
                  <th className="text-left p-2">Origin → Destination</th>
                  <th className="text-left p-2">GPS</th>
                  <th className="text-right p-2">Active Fare</th>
                  <th className="text-right p-2">Last Ping</th>
                </tr>
              </thead>
              <tbody>
                {activeTrips.map(t => (
                  <tr key={t.driver_id} className="border-b border-zinc-800" data-testid={`active-trip-${t.driver_id}`}>
                    <td className="p-2 font-bold text-amber-400">{t.driver?.name || t.driver_id?.slice(0, 8)}</td>
                    <td className="p-2 text-zinc-300 max-w-md truncate">{t.origin || "—"} → {t.destination || "—"}</td>
                    <td className="p-2 text-zinc-500 font-mono text-[11px]">{t.lat?.toFixed(4)}, {t.lng?.toFixed(4)}</td>
                    <td className="p-2 text-right meter-display font-extrabold text-amber-400" data-testid={`active-fare-${t.driver_id}`}>{fmtUsd(t.current_fare || 0)}</td>
                    <td className="p-2 text-right text-zinc-500 text-[11px]">{new Date(t.ts).toLocaleTimeString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

/**
 * v2.6.9 batch X · Trip detail modal · click any row in Rides/Waybills
 * (customer bookings or driver trips) to open this. Surfaces customer info,
 * full fare totals, AND the tax/fee breakdown in one panel — no PDF download
 * required.
 */
function TripDetailDialog({ trip, onClose }) {
  if (!trip) return null;
  const bd = trip.fare_breakdown || trip.breakdown || trip.quoted_breakdown || {};
  const taxAmt = trip.sales_tax ?? bd.sales_tax ?? 0;
  const tax_pct = bd.sales_tax_pct ?? trip.sales_tax_pct;
  const fee_lines = [
    ["Base Fare", bd.base_fare ?? bd.fare_fee],
    ["Per-Mile", bd.miles_fare ?? bd.distance_fare ?? bd.mileage_fare],
    ["Per-Passenger", bd.passenger_fare],
    ["Wait / Stops", bd.wait_fare ?? bd.stop_fare],
    ["Airport Fee", bd.airport_fee],
    ["Toll Fee", bd.toll_fee],
    ["Tip", trip.tip ?? trip.tip_amount ?? bd.tip],
    ["Technology Fee", bd.tech_fee ?? trip.tech_fee],
    ["Local Franchise Fee", bd.local_franchise_fee ?? trip.local_franchise_fee],
    [`County Tax${tax_pct ? ` (${tax_pct}%)` : ""}`, taxAmt],
    ["Federal Tax", bd.federal_tax],
    ["State Tax", bd.state_tax],
    ["City Tax", bd.city_tax],
    ["CC Surcharge", bd.cc_surcharge],
    ["CC Processing Fee", bd.cc_processing_fee],
  ].filter(([, v]) => typeof v === "number" && v > 0);

  // v2.6.9 batch BB-2 · cash-register close-out data captured on driver trips.
  // Tightened (batch BC) to skip back-filled legacy rows where every closeout
  // value is null OR a stored 0 — otherwise old trips render a near-empty card.
  const hasCloseout = (Number(trip.rating || 0) > 0) ||
                      (Number(trip.tip_amount || 0) > 0) ||
                      (Number(trip.cash_tendered || 0) > 0);
  const ratingNum = Number(trip.rating || 0);

  return (
    <Dialog open={!!trip} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="bg-zinc-900 border-zinc-700 text-zinc-100 max-w-3xl max-h-[88vh] overflow-y-auto" data-testid="trip-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between pr-8">
            <span>Trip Detail · {trip.mode?.toUpperCase()}</span>
            <span className="text-[10px] font-mono text-zinc-500" data-testid="trip-detail-id">{trip.id?.slice(0, 12)}</span>
          </DialogTitle>
        </DialogHeader>

        {/* Customer + Driver + Status */}
        <div className="grid grid-cols-2 gap-4 mb-3">
          <Card className="bg-zinc-950 border-zinc-800 p-3">
            <h4 className="text-[10px] uppercase text-zinc-500 mb-1">Customer</h4>
            <div className="text-sm font-bold" data-testid="trip-detail-customer-name">{trip.rider_name || trip.customer_name || "—"}</div>
            <div className="text-xs text-zinc-400" data-testid="trip-detail-customer-email">{trip.customer_email || trip.rider_email || "—"}</div>
            <div className="text-xs text-zinc-400" data-testid="trip-detail-customer-phone">{trip.customer_phone || trip.rider_phone || "—"}</div>
            {trip.customer_address && (
              <div className="text-xs text-zinc-500 mt-0.5" data-testid="trip-detail-customer-address">{trip.customer_address}</div>
            )}
          </Card>
          <Card className="bg-zinc-950 border-zinc-800 p-3">
            <h4 className="text-[10px] uppercase text-zinc-500 mb-1">Driver</h4>
            <div className="text-sm font-bold">{trip.driver_name || "—"}</div>
            <div className="text-xs text-zinc-400">{trip.driver_email || "—"}</div>
            <div className="text-[10px] font-mono text-zinc-500">{trip.driver_id || "—"}</div>
          </Card>
        </div>

        {/* Route + meta */}
        <Card className="bg-zinc-950 border-zinc-800 p-3 mb-3">
          <h4 className="text-[10px] uppercase text-zinc-500 mb-1">Route</h4>
          <div className="text-xs text-zinc-400"><span className="font-bold text-zinc-200">From:</span> {trip.origin || trip.pickup_address || "—"}</div>
          <div className="text-xs text-zinc-400"><span className="font-bold text-zinc-200">To:</span> {trip.destination || trip.dropoff_address || "—"}</div>
          <div className="text-xs text-zinc-500 mt-1 flex flex-wrap gap-3">
            <span>{trip.miles ?? trip.distance_miles ?? "—"} mi</span>
            <span>{trip.passengers ?? 1} pax</span>
            <span>{trip.duration_min ? `${trip.duration_min} min` : ""}</span>
            <span className="ml-auto">
              <Badge variant="outline" className="border-zinc-600 text-[10px]">{trip.status || "—"}</Badge>
              <Badge variant="outline" className="border-emerald-500/40 text-emerald-300 text-[10px] ml-1">{trip.payment_status || trip.payment_method || "—"}</Badge>
            </span>
          </div>
        </Card>

        {/* Fare breakdown */}
        <Card className="bg-zinc-950 border-zinc-800 p-3 mb-3">
          <h4 className="text-[10px] uppercase text-zinc-500 mb-1.5">Fare Breakdown</h4>
          <table className="w-full text-sm" data-testid="trip-detail-fare-table">
            <tbody>
              {fee_lines.length === 0 && (
                <tr><td className="text-xs text-zinc-500 italic py-1">No breakdown stored for this trip — pre-v2.1 record.</td></tr>
              )}
              {fee_lines.map(([label, val]) => (
                <tr key={label} className="border-b border-zinc-800/50">
                  <td className="py-1 text-zinc-400">{label}</td>
                  <td className="py-1 text-right meter-display">{fmtUsd(val)}</td>
                </tr>
              ))}
              <tr className="border-t-2 border-amber-400/30">
                <td className="py-2 font-bold uppercase text-xs text-amber-400">Trip Total</td>
                <td className="py-2 text-right meter-display font-extrabold text-amber-400 text-base" data-testid="trip-detail-total">{fmtUsd(trip.total)}</td>
              </tr>
            </tbody>
          </table>
        </Card>

        {/* v2.6.9 batch BB-2 · Cash-register close-out · rating + tip + cash */}
        {hasCloseout && (
          <Card className="bg-zinc-950 border-emerald-500/30 p-3 mb-3" data-testid="trip-detail-closeout">
            <h4 className="text-[10px] uppercase text-emerald-400 mb-2 flex items-center gap-2">
              Close-Out · Rating &amp; Cash Register
              {ratingNum > 0 && (
                <span className="ml-auto inline-flex items-center gap-0.5" data-testid="trip-detail-rating-stars">
                  {[1,2,3,4,5].map(s => (
                    <span key={s} className={s <= ratingNum ? "text-amber-400" : "text-zinc-700"}>★</span>
                  ))}
                  <span className="text-zinc-400 text-[10px] ml-1">{ratingNum}/5</span>
                </span>
              )}
            </h4>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
              {trip.tip_amount != null && (
                <div>
                  <div className="text-[10px] uppercase text-zinc-500">Tip</div>
                  <div className="meter-display font-bold text-emerald-400" data-testid="trip-detail-tip">{fmtUsd(trip.tip_amount)}</div>
                </div>
              )}
              {trip.cash_tendered != null && (
                <div>
                  <div className="text-[10px] uppercase text-zinc-500">Cash Tendered</div>
                  <div className="meter-display font-bold" data-testid="trip-detail-cash-tendered">{fmtUsd(trip.cash_tendered)}</div>
                </div>
              )}
              {trip.change_due != null && (
                <div>
                  <div className="text-[10px] uppercase text-zinc-500">Change Given</div>
                  <div className="meter-display font-bold text-amber-300" data-testid="trip-detail-change-due">{fmtUsd(trip.change_due)}</div>
                </div>
              )}
              {trip.payment_method && (
                <div>
                  <div className="text-[10px] uppercase text-zinc-500">Method</div>
                  <div className="text-sm font-bold uppercase" data-testid="trip-detail-pay-method">{trip.payment_method}</div>
                </div>
              )}
            </div>
            {trip.rating_comment && (
              <div className="mt-2 pt-2 border-t border-emerald-500/20">
                <div className="text-[10px] uppercase text-zinc-500 mb-0.5">Driver Comment</div>
                <div className="text-xs text-zinc-300 italic" data-testid="trip-detail-rating-comment">&ldquo;{trip.rating_comment}&rdquo;</div>
              </div>
            )}
          </Card>
        )}

        <div className="flex items-center gap-2 pt-2 border-t border-zinc-800">
          <Button variant="ghost" onClick={onClose} className="ml-auto"><XIcon className="h-4 w-4 mr-1"/>Close</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// v2.6.6: per-tab PIN gates removed — the single AdminGate at the route level
// handles unlock once for the whole console. PricingTab/PaymentsTab/LegalTab/
// AccountingTab now render their content directly.
function PricingTab() {
  return <PricingPanel/>;
}

function PricingPanel() {
  const [pricing, setPricing] = useState(null);
  const [busy, setBusy] = useState(false);
  // v2.6.9 batch BF-3 · After saving pricing we force a /api/config refresh
  // so other admin tabs / open driver dashboards pick up the new rates without
  // a hard page reload. Hook must be declared at top of component.
  const { reload: reloadConfig } = useConfig();
  useEffect(() => { adminApi.get("/admin/pricing").then(r => setPricing(r.data)).catch(()=>{}); }, []);
  if (!pricing) return <div className="text-zinc-500">Loading…</div>;

  const updateBracket = (i, key, val) => {
    const next = [...(pricing.group_brackets || [])];
    next[i] = { ...next[i], [key]: parseFloat(val) || 0 };
    setPricing({ ...pricing, group_brackets: next });
  };
  // v2.7.65+E253 · Group-ride add-ons (surcharge / booking fee / round-trip
  // multiplier) + volume discount tiers — editable live from the Pricing tab.
  const gp = pricing.group_pricing || {};
  const updateGroupPricing = (key, val) => setPricing({ ...pricing, group_pricing: { ...(pricing.group_pricing || {}), [key]: parseFloat(val) || 0 } });
  const addGroupTier = () => setPricing({ ...pricing, group_pricing: { ...(pricing.group_pricing || {}), discount_tiers: [ ...((pricing.group_pricing || {}).discount_tiers || []), { min_riders: 6, price_per_rider: 0 } ] } });
  const updateGroupTier = (i, key, val) => { const tiers = [ ...((pricing.group_pricing || {}).discount_tiers || []) ]; tiers[i] = { ...tiers[i], [key]: parseFloat(val) || 0 }; setPricing({ ...pricing, group_pricing: { ...(pricing.group_pricing || {}), discount_tiers: tiers } }); };
  const removeGroupTier = (i) => { const tiers = [ ...((pricing.group_pricing || {}).discount_tiers || []) ]; tiers.splice(i, 1); setPricing({ ...pricing, group_pricing: { ...(pricing.group_pricing || {}), discount_tiers: tiers } }); };
  const updateCity = (key, field, val) => {
    setPricing({ ...pricing, city_to_city_group: { ...pricing.city_to_city_group, [key]: { ...pricing.city_to_city_group[key], [field]: parseFloat(val) || val } } });
  };
  const updateSingle = (key, field, val) => {
    setPricing({ ...pricing, single_fares: { ...pricing.single_fares, [key]: { ...pricing.single_fares[key], [field]: parseFloat(val) || val } } });
  };
  // v2.6.9 batch BE-3 (fix) · auto-save when a route's enabled toggle is flipped.
  // Other rate fields still rely on the global "Save Pricing" button so the admin
  // can stage many edits before committing — but on/off is binary and shouldn't
  // require a separate save step.
  const toggleSingleEnabled = async (key, enabled) => {
    const next = { ...pricing, single_fares: { ...pricing.single_fares, [key]: { ...pricing.single_fares[key], enabled } } };
    setPricing(next);
    try {
      await adminApi.put("/admin/pricing", next);
      toast.success(enabled ? "Route ENABLED · drivers will see it" : "Route HIDDEN from drivers");
    } catch (e) {
      console.error("toggleSingleEnabled failed", e);  // surface in browser console for live debugging
      toast.error(e?.response?.data?.detail || "Could not save toggle — try again");
      // revert the optimistic flip
      setPricing({ ...pricing, single_fares: { ...pricing.single_fares, [key]: { ...pricing.single_fares[key], enabled: !enabled } } });
    }
  };
  const updateDefault = (key, val) => setPricing({ ...pricing, default_calculated: { ...pricing.default_calculated, [key]: parseFloat(val) || 0 } });
  const updateTax = (key, val) => setPricing({ ...pricing, taxes: { ...(pricing.taxes || {}), [key]: parseFloat(val) || 0 } });
  const updateGeofence = (key, val) => setPricing({ ...pricing, geofence: { ...(pricing.geofence || {}), [key]: typeof pricing.geofence?.[key] === "boolean" ? !!val : (parseFloat(val) || val) } });
  // v2.6.9 batch BG · time-based surcharge editor (late-night + holiday)
  const updateSurcharge = (key, val) => setPricing({
    ...pricing,
    surcharges: {
      ...(pricing.surcharges || {}),
      [key]: typeof (pricing.surcharges || {})[key] === "boolean" ? !!val : (typeof val === "string" ? val : (parseFloat(val) || val)),
    },
  });
  const updateSurchargeHolidays = (csvList) => setPricing({
    ...pricing,
    surcharges: {
      ...(pricing.surcharges || {}),
      holiday_dates: csvList.split(",").map((s) => s.trim()).filter(Boolean),
    },
  });

  // v2.6.9 batch BF-3 · After save, force a /api/config refresh so every
  // other admin tab/window/driver dashboard picks up the new rates without
  // requiring a full page reload.
  const save = async () => {
    setBusy(true);
    try {
      await adminApi.put("/admin/pricing", pricing);
      // refresh the cached /api/config so the meter page reflects new rates on next visit
      await reloadConfig?.();
      toast.success("Pricing saved · effective immediately");
    } catch { toast.error("Save failed"); }
    finally { setBusy(false); }
  };

  // v2.6.9 batch W · Download a printable 8.5"x11" Price Sheet PDF
  const downloadPriceSheet = () => {
    const base = process.env.REACT_APP_BACKEND_URL || "";
    const token = localStorage.getItem("rtg_admin_token");
    toast.info("Building price sheet…");
    fetch(`${base}/api/admin/pricing/price-sheet.html`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => (r.ok ? r.text() : Promise.reject(r.statusText)))
      .then((html) => {
        const blob = new Blob([html], { type: "text/html" });
        window.open(URL.createObjectURL(blob), "_blank");
      })
      .catch((err) => toast.error(`Print failed: ${err}`));
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-end gap-2">
        <Button onClick={downloadPriceSheet} variant="outline" className="border-amber-400/40 text-amber-300 hover:bg-amber-400/10" data-testid="pricing-print-sheet-btn"><Printer className="h-4 w-4 mr-1"/>Download Price Sheet (PDF)</Button>
      </div>
      <Card className="bg-zinc-900 border-zinc-800 p-5">
        <h3 className="font-bold mb-3">Group Ride Brackets</h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {(pricing.group_brackets || []).map((b, i) => (
            <div key={`bracket-${b.max_miles}-${i}`} className="bg-zinc-950 border border-zinc-800 rounded p-3" data-testid={`bracket-${i}`}>
              <Label className="text-[10px] uppercase text-zinc-500">Max miles</Label>
              <Input type="number" value={b.max_miles} onChange={e=>updateBracket(i, "max_miles", e.target.value)} className="bg-zinc-900 border-zinc-700 h-9 mt-1 meter-display"/>
              <Label className="text-[10px] uppercase text-zinc-500 mt-2 block">$ / passenger</Label>
              <Input type="number" value={b.price_per_passenger} onChange={e=>updateBracket(i, "price_per_passenger", e.target.value)} className="bg-zinc-900 border-zinc-700 h-9 mt-1 meter-display"/>
            </div>
          ))}
        </div>
      </Card>

      <Card className="bg-zinc-900 border-zinc-800 p-5" data-testid="group-addons-card">
        <h3 className="font-bold mb-1">Group Ride Add-Ons &amp; Volume Discounts</h3>
        <p className="text-[11px] text-zinc-500 mb-3">Per-rider price = the mileage bracket above + surcharge (unless a volume tier matches). The booking fee is charged once per group booking. Round trips multiply the per-rider price (booking fee stays once).</p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <Label className="text-[10px] uppercase text-zinc-500">Surcharge / rider ($)</Label>
            <Input type="number" step="0.5" value={gp.surcharge_per_rider ?? 0} onChange={e=>updateGroupPricing("surcharge_per_rider", e.target.value)} className="bg-zinc-950 border-zinc-700 h-9 mt-1 meter-display" data-testid="group-surcharge-input"/>
          </div>
          <div>
            <Label className="text-[10px] uppercase text-zinc-500">Booking fee ($ / booking)</Label>
            <Input type="number" step="0.5" value={gp.booking_fee ?? 0} onChange={e=>updateGroupPricing("booking_fee", e.target.value)} className="bg-zinc-950 border-zinc-700 h-9 mt-1 meter-display" data-testid="group-booking-fee-input"/>
          </div>
          <div>
            <Label className="text-[10px] uppercase text-zinc-500">Round-trip &times;</Label>
            <Input type="number" step="0.1" value={gp.round_trip_multiplier ?? 2} onChange={e=>updateGroupPricing("round_trip_multiplier", e.target.value)} className="bg-zinc-950 border-zinc-700 h-9 mt-1 meter-display" data-testid="group-rt-mult-input"/>
          </div>
        </div>
        <div className="mt-4">
          <div className="flex items-center justify-between mb-1.5">
            <Label className="text-xs uppercase tracking-wider text-zinc-400">Volume Discount Tiers</Label>
            <Button size="sm" variant="outline" onClick={addGroupTier} className="border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 h-8" data-testid="group-tier-add"><Plus className="h-3.5 w-3.5 mr-1"/>Add tier</Button>
          </div>
          <p className="text-[11px] text-zinc-500 mb-2">When a group reaches "Min riders", every rider pays the tier's per-rider price (overrides the bracket + surcharge). The highest matching tier wins. Leave empty for standard pricing at all sizes.</p>
          {((gp.discount_tiers) || []).length === 0 && (
            <div className="text-[11px] text-zinc-600 italic" data-testid="group-tiers-empty">No tiers configured — standard bracket pricing applies at every group size.</div>
          )}
          {((gp.discount_tiers) || []).map((t, i) => (
            <div key={`gtier-${i}`} className="grid grid-cols-[1fr_1fr_auto] gap-2 items-end mb-2" data-testid={`group-tier-${i}`}>
              <div><Label className="text-[10px] uppercase text-zinc-500">Min riders</Label><Input type="number" value={t.min_riders} onChange={e=>updateGroupTier(i, "min_riders", e.target.value)} className="bg-zinc-950 border-zinc-700 h-9 mt-1 meter-display" data-testid={`group-tier-${i}-min`}/></div>
              <div><Label className="text-[10px] uppercase text-zinc-500">$ / rider</Label><Input type="number" step="0.5" value={t.price_per_rider} onChange={e=>updateGroupTier(i, "price_per_rider", e.target.value)} className="bg-zinc-950 border-zinc-700 h-9 mt-1 meter-display" data-testid={`group-tier-${i}-price`}/></div>
              <Button variant="ghost" size="icon" onClick={()=>removeGroupTier(i)} className="text-red-400 hover:text-red-300 h-9 w-9" data-testid={`group-tier-remove-${i}`}><Trash2 className="h-4 w-4"/></Button>
            </div>
          ))}
        </div>
      </Card>

      <Card className="bg-zinc-900 border-zinc-800 p-5">
        <h3 className="font-bold mb-3">City-to-City Group Routes</h3>
        <div className="space-y-2">
          {Object.entries(pricing.city_to_city_group || {}).map(([key, r]) => (
            <div key={key} className="grid grid-cols-[2fr_1fr_1fr_1fr] gap-2 items-end">
              <div className="text-sm font-semibold">{r.name}</div>
              <div><Label className="text-[10px] uppercase text-zinc-500">Miles</Label><Input type="number" step="0.1" value={r.miles} onChange={e=>updateCity(key, "miles", e.target.value)} className="bg-zinc-950 border-zinc-700 h-9 mt-1 meter-display"/></div>
              <div><Label className="text-[10px] uppercase text-zinc-500">$ / pax</Label><Input type="number" value={r.price_per_passenger} onChange={e=>updateCity(key, "price_per_passenger", e.target.value)} className="bg-zinc-950 border-zinc-700 h-9 mt-1 meter-display"/></div>
              <div><Label className="text-[10px] uppercase text-zinc-500">Max pax</Label><Input type="number" value={r.max_passengers} onChange={e=>updateCity(key, "max_passengers", e.target.value)} className="bg-zinc-950 border-zinc-700 h-9 mt-1 meter-display"/></div>
            </div>
          ))}
        </div>
      </Card>

      <Card className="bg-zinc-900 border-zinc-800 p-5">
        <h3 className="font-bold mb-1">Single Fares (Flat & Calculated)</h3>
        {/* v2.6.9 batch BE-3 · per-preset on/off toggle. Disabled presets are hidden
            from the driver City Taxi UI but stay editable here. Per spec only
            'Weatherford Inside City Limits (Single)' is ON by default. */}
        <p className="text-[11px] text-zinc-500 mb-3">Use the toggle to show/hide each preset on the driver's City Taxi page.</p>
        <div className="space-y-2 text-sm">
          {Object.entries(pricing.single_fares || {}).map(([key, r]) => {
            const isOn = r?.enabled !== false;
            return (
              <div key={key} className={`bg-zinc-950 border rounded p-3 ${isOn ? "border-emerald-500/30" : "border-zinc-800 opacity-70"}`} data-testid={`single-row-${key}`}>
                <div className="flex items-center justify-between gap-3 mb-2">
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold truncate">{r.name} <span className="text-[10px] text-zinc-500 uppercase">· {r.type}</span></div>
                    <div className={`text-[10px] uppercase tracking-wider font-bold ${isOn ? "text-emerald-400" : "text-red-400/80"}`} data-testid={`single-status-${key}`}>
                      {isOn ? "✓ Active — visible to drivers" : "✗ Hidden from drivers"}
                    </div>
                  </div>
                  <Switch
                    data-testid={`single-toggle-${key}`}
                    checked={isOn}
                    onCheckedChange={(v) => updateSingle(key, "enabled", v)}
                    className="data-[state=checked]:bg-emerald-500"
                  />
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  <div><Label className="text-[10px] uppercase text-zinc-500">Miles</Label><Input type="number" step="0.1" value={r.miles ?? 0} onChange={e=>updateSingle(key, "miles", e.target.value)} className="bg-zinc-900 border-zinc-700 h-8 mt-1 meter-display"/></div>
                  {r.type !== "calculated" ? (
                    <div><Label className="text-[10px] uppercase text-zinc-500">$ Fare</Label><Input type="number" value={r.fare ?? 0} onChange={e=>updateSingle(key, "fare", e.target.value)} className="bg-zinc-900 border-zinc-700 h-8 mt-1 meter-display"/></div>
                  ) : (
                    <>
                      <div><Label className="text-[10px] uppercase text-zinc-500">$ Base</Label><Input type="number" value={r.fare_fee ?? 0} onChange={e=>updateSingle(key, "fare_fee", e.target.value)} className="bg-zinc-900 border-zinc-700 h-8 mt-1 meter-display"/></div>
                      <div><Label className="text-[10px] uppercase text-zinc-500">$ / pax</Label><Input type="number" value={r.per_passenger ?? 0} onChange={e=>updateSingle(key, "per_passenger", e.target.value)} className="bg-zinc-900 border-zinc-700 h-8 mt-1 meter-display"/></div>
                      <div><Label className="text-[10px] uppercase text-zinc-500">$ / mi</Label><Input type="number" step="0.01" value={r.per_mile ?? 0} onChange={e=>updateSingle(key, "per_mile", e.target.value)} className="bg-zinc-900 border-zinc-700 h-8 mt-1 meter-display"/></div>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </Card>

      <Card className="bg-zinc-900 border-zinc-800 p-5">
        <h3 className="font-bold mb-3">Default Calculated Rates (used when no preset matches)</h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {["fare_fee","per_passenger","per_mile","per_minute","per_stop_per_min","airport_fee","toll_fee","cc_processing_fee_flat"].map(k => (
            <div key={k}><Label className="text-[10px] uppercase text-zinc-500">{k === "cc_processing_fee_flat" ? "credit card processing fee $" : k.replace(/_/g," ")}</Label><Input type="number" step="0.01" value={pricing.default_calculated?.[k] ?? (k === "cc_processing_fee_flat" ? 4.00 : 0)} onChange={e=>updateDefault(k, e.target.value)} className="bg-zinc-950 border-zinc-700 h-9 mt-1 meter-display" data-testid={`def-${k}`}/></div>
          ))}
        </div>
      </Card>

      <Card className="bg-zinc-900 border-zinc-800 p-5" data-testid="taxes-fees-card">
        <h3 className="font-bold mb-3">Taxes & Per-Ride Fees</h3>
        <p className="text-[11px] text-zinc-500 mb-4">All values apply to every ride from rider, business, and driver-side bookings. Tax % is added on top of the subtotal; flat fees are added before tax.</p>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          <div><Label className="text-[10px] uppercase text-zinc-500">County Tax %</Label><Input data-testid="tax-sales" type="number" step="0.1" value={pricing.taxes?.sales_pct ?? 0} onChange={e=>updateTax("sales_pct", e.target.value)} className="bg-zinc-950 border-zinc-700 h-9 mt-1 meter-display"/></div>
          <div><Label className="text-[10px] uppercase text-zinc-500">Federal Tax %</Label><Input data-testid="tax-federal" type="number" step="0.1" value={pricing.taxes?.federal_pct ?? 0} onChange={e=>updateTax("federal_pct", e.target.value)} className="bg-zinc-950 border-zinc-700 h-9 mt-1 meter-display"/></div>
          <div><Label className="text-[10px] uppercase text-zinc-500">State Tax %</Label><Input data-testid="tax-state" type="number" step="0.1" value={pricing.taxes?.state_pct ?? 0} onChange={e=>updateTax("state_pct", e.target.value)} className="bg-zinc-950 border-zinc-700 h-9 mt-1 meter-display"/></div>
          <div><Label className="text-[10px] uppercase text-zinc-500">City Tax %</Label><Input data-testid="tax-city" type="number" step="0.1" value={pricing.taxes?.city_pct ?? 0} onChange={e=>updateTax("city_pct", e.target.value)} className="bg-zinc-950 border-zinc-700 h-9 mt-1 meter-display"/></div>
          <div><Label className="text-[10px] uppercase text-zinc-500">Local Franchise Fee $</Label><Input data-testid="fee-franchise" type="number" step="0.01" value={pricing.taxes?.local_franchise_fee_flat ?? 0} onChange={e=>updateTax("local_franchise_fee_flat", e.target.value)} className="bg-zinc-950 border-zinc-700 h-9 mt-1 meter-display"/></div>
          <div><Label className="text-[10px] uppercase text-zinc-500">Technology Fee · per passenger $</Label><Input data-testid="fee-tech" type="number" step="0.01" value={pricing.taxes?.tech_fee_per_passenger ?? pricing.taxes?.maintenance_fee_flat ?? 0} onChange={e=>updateTax("tech_fee_per_passenger", e.target.value)} className="bg-zinc-950 border-zinc-700 h-9 mt-1 meter-display"/></div>
        </div>
      </Card>

      <Card className="bg-zinc-900 border-zinc-800 p-5" data-testid="geofence-card">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-bold">Weatherford City Geofence</h3>
          <Switch data-testid="geofence-enabled" checked={!!pricing.geofence?.enabled} onCheckedChange={v=>updateGeofence("enabled", v)}/>
        </div>
        <p className="text-[11px] text-zinc-500 mb-3">When BOTH pickup and drop-off are inside this radius, the computed base fare is replaced with the flat city base.</p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div><Label className="text-[10px] uppercase text-zinc-500">Center Lat</Label><Input type="number" step="0.0001" value={pricing.geofence?.center_lat ?? 0} onChange={e=>updateGeofence("center_lat", e.target.value)} className="bg-zinc-950 border-zinc-700 h-9 mt-1 meter-display"/></div>
          <div><Label className="text-[10px] uppercase text-zinc-500">Center Lng</Label><Input type="number" step="0.0001" value={pricing.geofence?.center_lng ?? 0} onChange={e=>updateGeofence("center_lng", e.target.value)} className="bg-zinc-950 border-zinc-700 h-9 mt-1 meter-display"/></div>
          <div><Label className="text-[10px] uppercase text-zinc-500">Radius (mi)</Label><Input type="number" step="0.1" value={pricing.geofence?.radius_miles ?? 0} onChange={e=>updateGeofence("radius_miles", e.target.value)} className="bg-zinc-950 border-zinc-700 h-9 mt-1 meter-display"/></div>
          <div><Label className="text-[10px] uppercase text-zinc-500">Flat Base $/pax</Label><Input type="number" step="0.01" value={pricing.geofence?.flat_base_fare ?? 0} onChange={e=>updateGeofence("flat_base_fare", e.target.value)} className="bg-zinc-950 border-zinc-700 h-9 mt-1 meter-display"/></div>
        </div>
      </Card>


      {/* v2.6.9 batch BG · Time-based surcharges (late-night + holiday).
          v2.7.0 batch DJ · Master ON/OFF toggle per surcharge type. */}
      <Card className="bg-zinc-900 border-zinc-800 p-5" data-testid="surcharges-card">
        <h3 className="font-bold mb-1">Time-Based Surcharges</h3>
        <p className="text-[11px] text-zinc-500 mb-4">
          Toggle each surcharge ON/OFF independently. When ON, the system applies a % surcharge during the configured window and shows a banner on the public Quick Booking form. Applied to the pre-tax subtotal (after the franchise + tech fee, before taxes). All times in America/Chicago.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {/* LATE-NIGHT */}
          <div className="bg-zinc-950 border border-indigo-400/40 rounded p-4" data-testid="surcharge-late-night-card">
            <div className="flex items-center justify-between mb-3">
              <div className="font-semibold text-sm uppercase tracking-wider text-indigo-300">Late-Night</div>
              <Switch
                data-testid="surcharge-late-night-enabled"
                checked={!!pricing.surcharges?.late_night_enabled}
                onCheckedChange={(v)=>updateSurcharge("late_night_enabled", v)}
              />
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <Label className="text-[10px] uppercase text-zinc-500">Start · Hour</Label>
                <Input data-testid="surcharge-late-start" type="number" min="0" max="23" step="1" value={pricing.surcharges?.late_night_start_hour ?? 23} onChange={(e)=>updateSurcharge("late_night_start_hour", e.target.value)} className="bg-zinc-900 border-zinc-700 h-9 mt-1 meter-display"/>
              </div>
              <div>
                <Label className="text-[10px] uppercase text-zinc-500">Start · Min</Label>
                <Input data-testid="surcharge-late-start-min" type="number" min="0" max="59" step="1" value={pricing.surcharges?.late_night_start_minute ?? 55} onChange={(e)=>updateSurcharge("late_night_start_minute", e.target.value)} className="bg-zinc-900 border-zinc-700 h-9 mt-1 meter-display"/>
              </div>
              <div>
                <Label className="text-[10px] uppercase text-zinc-500">Surcharge %</Label>
                <Input data-testid="surcharge-late-pct" type="number" min="0" step="0.1" value={pricing.surcharges?.late_night_pct ?? 15} onChange={(e)=>updateSurcharge("late_night_pct", e.target.value)} className="bg-zinc-900 border-zinc-700 h-9 mt-1 meter-display"/>
              </div>
              <div>
                <Label className="text-[10px] uppercase text-zinc-500">End · Hour</Label>
                <Input data-testid="surcharge-late-end" type="number" min="0" max="23" step="1" value={pricing.surcharges?.late_night_end_hour ?? 5} onChange={(e)=>updateSurcharge("late_night_end_hour", e.target.value)} className="bg-zinc-900 border-zinc-700 h-9 mt-1 meter-display"/>
              </div>
              <div>
                <Label className="text-[10px] uppercase text-zinc-500">End · Min</Label>
                <Input data-testid="surcharge-late-end-min" type="number" min="0" max="59" step="1" value={pricing.surcharges?.late_night_end_minute ?? 0} onChange={(e)=>updateSurcharge("late_night_end_minute", e.target.value)} className="bg-zinc-900 border-zinc-700 h-9 mt-1 meter-display"/>
              </div>
              <div></div>
              <div className="col-span-3">
                <Label className="text-[10px] uppercase text-zinc-500">Label (shown on receipt)</Label>
                <Input data-testid="surcharge-late-label" type="text" value={pricing.surcharges?.late_night_label ?? "Late Night (11:55p–5a)"} onChange={(e)=>updateSurcharge("late_night_label", e.target.value)} className="bg-zinc-900 border-zinc-700 h-9 mt-1"/>
              </div>
            </div>
            <p className="text-[10px] text-zinc-500 mt-2">If end ≤ start, the window crosses midnight (e.g. 23:55 → 05:00 means 11:55 PM through 5:00 AM next day). Toggle OFF to disable the entire late-night system.</p>
          </div>

          {/* HOLIDAY / SEASONAL */}
          <div className="bg-zinc-950 border border-rose-400/40 rounded p-4" data-testid="surcharge-holiday-card">
            <div className="flex items-center justify-between mb-3">
              <div className="font-semibold text-sm uppercase tracking-wider text-rose-300">Holiday / Seasonal</div>
              <Switch
                data-testid="surcharge-holiday-enabled"
                checked={!!pricing.surcharges?.holiday_enabled}
                onCheckedChange={(v)=>updateSurcharge("holiday_enabled", v)}
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-[10px] uppercase text-zinc-500">Surcharge %</Label>
                <Input data-testid="surcharge-holiday-pct" type="number" min="0" step="0.1" value={pricing.surcharges?.holiday_pct ?? 20} onChange={(e)=>updateSurcharge("holiday_pct", e.target.value)} className="bg-zinc-900 border-zinc-700 h-9 mt-1 meter-display"/>
              </div>
              <div>
                <Label className="text-[10px] uppercase text-zinc-500">Label (on receipt)</Label>
                <Input data-testid="surcharge-holiday-label" type="text" value={pricing.surcharges?.holiday_label ?? "Holiday Surcharge"} onChange={(e)=>updateSurcharge("holiday_label", e.target.value)} className="bg-zinc-900 border-zinc-700 h-9 mt-1"/>
              </div>
              <div className="col-span-2">
                <Label className="text-[10px] uppercase text-zinc-500">Holiday Dates · comma-separated ISO YYYY-MM-DD (or MM-DD for recurring)</Label>
                <Input
                  data-testid="surcharge-holiday-dates"
                  type="text"
                  value={(pricing.surcharges?.holiday_dates || []).join(", ")}
                  onChange={(e)=>updateSurchargeHolidays(e.target.value)}
                  placeholder="2026-12-25, 2026-01-01, 07-04"
                  className="bg-zinc-900 border-zinc-700 h-9 mt-1 text-xs"
                />
              </div>
            </div>
            <p className="text-[10px] text-zinc-500 mt-2">Use MM-DD (e.g. 12-25) for holidays that recur every year. Compared against today's date in America/Chicago.</p>
          </div>
        </div>
      </Card>


      {/* v2.6.9 batch BF-1 · Live Meter rate presets — Weatherford City Limits + Statewide */}
      <Card className="bg-zinc-900 border-zinc-800 p-5">
        <h3 className="font-bold mb-1">Live Meter Rates (per variant)</h3>
        <p className="text-[11px] text-zinc-500 mb-4">
          Each meter on the driver dashboard pulls its rates from here. "Weatherford" is the locked $5 in-city meter; "Statewide" handles every trip outside Weatherford limits.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {["weatherford", "statewide"].map((variant) => {
            const r = pricing.meter_rates?.[variant] || {};
            const label = variant === "weatherford" ? "Weatherford City Limits" : "Statewide";
            const colorClass = variant === "weatherford" ? "border-amber-400/40" : "border-blue-400/40";
            const updateMeter = (field, val) => {
              setPricing((p) => ({
                ...p,
                meter_rates: {
                  ...(p.meter_rates || {}),
                  [variant]: { ...((p.meter_rates || {})[variant] || {}), [field]: parseFloat(val) || 0 },
                },
              }));
            };
            const updateMeterInt = (field, val) => {
              setPricing((p) => ({
                ...p,
                meter_rates: {
                  ...(p.meter_rates || {}),
                  [variant]: { ...((p.meter_rates || {})[variant] || {}), [field]: Math.max(1, parseInt(val, 10) || 1) },
                },
              }));
            };
            return (
              <div key={variant} className={`bg-zinc-950 border ${colorClass} rounded p-4`} data-testid={`meter-rates-${variant}`}>
                <div className="font-semibold text-sm mb-3 uppercase tracking-wider">{label}</div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-[10px] uppercase text-zinc-500">Base $</Label><Input type="number" step="0.01" data-testid={`meter-${variant}-base`} value={r.base_fare ?? 0} onChange={(e)=>updateMeter("base_fare", e.target.value)} className="bg-zinc-900 border-zinc-700 h-9 mt-1 meter-display"/></div>
                  <div><Label className="text-[10px] uppercase text-zinc-500">$ / Mile</Label><Input type="number" step="0.01" data-testid={`meter-${variant}-per-mile`} value={r.per_mile ?? 0} onChange={(e)=>updateMeter("per_mile", e.target.value)} className="bg-zinc-900 border-zinc-700 h-9 mt-1 meter-display"/></div>
                  <div><Label className="text-[10px] uppercase text-zinc-500">$ / Minute</Label><Input type="number" step="0.01" data-testid={`meter-${variant}-per-min`} value={r.per_minute ?? 0} onChange={(e)=>updateMeter("per_minute", e.target.value)} className="bg-zinc-900 border-zinc-700 h-9 mt-1 meter-display"/></div>
                  <div><Label className="text-[10px] uppercase text-zinc-500">$ / Stop-Min</Label><Input type="number" step="0.01" data-testid={`meter-${variant}-per-stop-min`} value={r.per_stop_per_minute ?? 0} onChange={(e)=>updateMeter("per_stop_per_minute", e.target.value)} className="bg-zinc-900 border-zinc-700 h-9 mt-1 meter-display"/></div>
                  <div className="col-span-2"><Label className="text-[10px] uppercase text-zinc-500">$ / Passenger</Label><Input type="number" step="0.01" data-testid={`meter-${variant}-per-pax`} value={r.per_passenger ?? 0} onChange={(e)=>updateMeter("per_passenger", e.target.value)} className="bg-zinc-900 border-zinc-700 h-9 mt-1 meter-display"/></div>
                  {/* v2.6.9 batch BT · Multi-rider flat pricing controls. When pax ≥ threshold,
                       base_fare is REPLACED by `flat × passengers`. Per-mile + per-minute
                       still apply on top. Defaults to $8/pax kicking in at 2 riders. */}
                  <div className="col-span-2 mt-2 pt-3 border-t border-zinc-800">
                    <div className="text-[10px] uppercase text-blue-300 font-bold tracking-wider mb-2">Multi-Rider Flat Pricing</div>
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase text-zinc-500">$ / Rider (flat)</Label>
                    <Input
                      type="number" step="0.01"
                      data-testid={`meter-${variant}-multi-flat`}
                      value={r.multi_rider_flat_per_pax ?? 8.0}
                      onChange={(e)=>updateMeter("multi_rider_flat_per_pax", e.target.value)}
                      className="bg-zinc-900 border-zinc-700 h-9 mt-1 meter-display"
                    />
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase text-zinc-500">Engages at # Riders</Label>
                    <Input
                      type="number" step="1" min="1"
                      data-testid={`meter-${variant}-multi-threshold`}
                      value={r.multi_rider_threshold ?? 2}
                      onChange={(e)=>updateMeterInt("multi_rider_threshold", e.target.value)}
                      className="bg-zinc-900 border-zinc-700 h-9 mt-1 meter-display"
                    />
                  </div>
                  <div className="col-span-2 text-[10px] text-zinc-500 italic leading-snug">
                    When passenger count ≥ {r.multi_rider_threshold ?? 2}, the meter replaces the base with <b className="text-blue-300">{(r.multi_rider_flat_per_pax ?? 8).toFixed(2)} × N riders</b>. All other variable fees (per-mile, per-minute, taxes) stack on top.
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </Card>

      <Button data-testid="save-pricing-btn" onClick={save} disabled={busy} className="w-full h-12 bg-amber-400 hover:bg-amber-300 text-zinc-950 font-bold">SAVE ALL PRICING</Button>

      {/* v2.7.58+E39 · Driver Meter Discount catalogue — admin CRUD for
          coupon-style deductions that surface on the driver's live meter
          "Apply Discount" dropdown. */}
      <DiscountsAdminCard/>
    </div>
  );
}

function PendingTab() {
  const [drivers, setDrivers] = useState([]);
  const [rides, setRides] = useState([]);
  const [tab, setTab] = useState("rides");
  const reload = useCallback(() => {
    adminApi.get("/admin/pending-drivers").then(r => setDrivers(r.data)).catch(()=>{});
    adminApi.get("/admin/pending-rides").then(r => setRides(r.data)).catch(()=>{});
  }, []);
  useEffect(() => { reload(); const t = setInterval(reload, 8000); return () => clearInterval(t); }, [reload]);

  const approve = async (d) => {
    try { await adminApi.post(`/admin/drivers/${d.id}/approve`); toast.success(`${d.name} approved & verified`); reload(); }
    catch { toast.error("Approve failed"); }
  };
  const cancelRide = async (r) => {
    if (!window.confirm(`Cancel ${r.waybill_no}?`)) return;
    try { await adminApi.post(`/admin/rides/${r.id}/cancel`); toast.success("Cancelled"); reload(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Cancel failed"); }
  };
  const endRide = async (r) => {
    if (!window.confirm(`Mark ${r.waybill_no} as DONE?`)) return;
    try { await adminApi.post(`/admin/rides/${r.id}/end`); toast.success("Marked done"); reload(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const broadcast = async (r) => {
    try { await adminApi.post(`/dispatch/broadcast/${r.id}`); toast.success("Broadcast to all online drivers"); reload(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Broadcast failed"); }
  };
  const removeRide = async (r) => {
    if (!window.confirm(`PERMANENTLY DELETE ${r.waybill_no}? This cannot be undone.`)) return;
    try { await adminApi.post(`/admin/rides/delete`, { ride_ids: [r.id] }); toast.success("Removed"); reload(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  const STATUS_TINT = {
    booked: "border-amber-400/50 text-amber-400",
    dispatching: "border-amber-400/50 text-amber-400 animate-pulse",
    accepted: "border-green-400/50 text-green-400",
    in_progress: "border-green-400/50 text-green-400",
    scheduled: "border-blue-400/50 text-blue-300",
    cancelled: "border-zinc-600 text-zinc-400",
  };

  return (
    <Tabs value={tab} onValueChange={setTab}>
      <TabsList className="bg-zinc-900 border border-zinc-800 grid grid-cols-2 mb-4">
        <TabsTrigger value="rides" data-testid="pending-rides-tab">Pending Rides ({rides.length})</TabsTrigger>
        <TabsTrigger value="drivers" data-testid="pending-drivers-tab">Driver Apps ({drivers.length})</TabsTrigger>
      </TabsList>

      <TabsContent value="rides">
        <Card className="bg-zinc-900 border-zinc-800 p-5" data-testid="pending-rides-card">
          {rides.length === 0 ? <div className="text-zinc-500 text-sm py-4">No pending rides.</div> : (
            <div className="space-y-3">
              {rides.map(r => (
                <div key={r.id} className="bg-zinc-950 border border-zinc-800 rounded p-3" data-testid={`pending-ride-${r.id}`}>
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge variant="outline" className={r.kind === "business" ? "border-blue-400/40 text-blue-300 text-[10px] uppercase" : "border-zinc-700 text-[10px] uppercase"}>{r.kind === "business" ? "BUSINESS" : "RIDER"}</Badge>
                        <Badge variant="outline" className={`text-[10px] uppercase ${STATUS_TINT[r.status] || "border-zinc-700"}`}>{r.status}</Badge>
                        <span className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono">{r.waybill_no}</span>
                        {r.pickup_at && <span className="text-[10px] text-blue-300">📅 {new Date(r.pickup_at).toLocaleString()}</span>}
                      </div>
                      <div className="text-sm font-semibold mt-1 truncate">{r.business_name || r.customer_name} · {r.origin} → {r.destination}</div>
                      <div className="text-[11px] text-zinc-500">{new Date(r.created_at).toLocaleString()} · {r.passengers} pax · {r.payment_method?.toUpperCase()} · driver: {r.driver_name || "—"}</div>
                    </div>
                    <div className="flex gap-1.5 flex-shrink-0 flex-wrap">
                      <span className="meter-display text-lg font-bold text-amber-400 mr-2 self-center">{fmtUsd(r.total)}</span>
                      {r.status === "booked" && <Button size="sm" onClick={() => broadcast(r)} className="bg-amber-400 hover:bg-amber-300 text-zinc-950 font-bold h-8 text-xs" data-testid={`pr-broadcast-${r.id}`}>📢 Dispatch</Button>}
                      {(r.status === "accepted" || r.status === "in_progress" || r.status === "dispatching") && <Button size="sm" onClick={() => endRide(r)} variant="outline" className="bg-zinc-950 border-green-400/40 text-green-400 h-8 text-xs" data-testid={`pr-end-${r.id}`}>End</Button>}
                      <Button size="sm" onClick={() => cancelRide(r)} variant="outline" className="bg-zinc-950 border-zinc-700 h-8 text-xs" data-testid={`pr-cancel-${r.id}`}>Cancel</Button>
                      <Button size="sm" onClick={() => removeRide(r)} variant="outline" className="bg-red-500/10 border-red-500/40 text-red-300 h-8 text-xs" data-testid={`pr-remove-${r.id}`}><Trash2 className="h-3 w-3"/></Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </TabsContent>

      <TabsContent value="drivers">
        <Card className="bg-zinc-900 border-zinc-800 p-5">
          {drivers.length === 0 ? <div className="text-zinc-500 text-sm">No pending applications.</div> : (
            <div className="space-y-3">
              {drivers.map(d => (
                <div key={d.id} className="bg-zinc-950 border border-zinc-800 rounded p-4" data-testid={`pending-${d.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="font-bold flex items-center gap-2">{d.name} <VerifiedBadge verified={d.verified} size="sm"/> <GoogleAuthBadge authProvider={d.auth_provider}/></div>
                      <div className="text-xs text-zinc-400 mt-0.5">{d.email} · {d.phone}</div>
                      <div className="text-xs text-zinc-500 mt-1">License: {d.license_number || "—"} · {d.address || "—"}</div>
                    </div>
                    <Button data-testid={`approve-${d.id}`} onClick={() => approve(d)} className="bg-green-500 hover:bg-green-400 text-zinc-950 font-bold whitespace-nowrap"><ShieldCheck className="h-4 w-4 mr-1"/>Approve & Verify</Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </TabsContent>
    </Tabs>
  );
}

function CustomersTab() {
  const [customers, setCustomers] = useState([]);
  const [businesses, setBusinesses] = useState([]);
  const [filter, setFilter] = useState("all"); // all | rider | business
  const [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState(new Set());

  const reload = useCallback(() => {
    adminApi.get("/admin/customers").then(r => setCustomers(r.data)).catch(()=>{});
    adminApi.get("/admin/businesses").then(r => setBusinesses(r.data)).catch(()=>{});
    setSelected(new Set());
  }, []);
  useEffect(() => { reload(); }, [reload]);

  const STATUSES = [
    { value: "active", label: "Active" },
    { value: "inactive", label: "Inactive" },
    { value: "banned", label: "Banned" },
    { value: "deactivation", label: "Deactivation (rider request)" },
    { value: "under_investigation", label: "Under Investigation" },
    { value: "property_damage_review", label: "Property Damage Review" },
  ];

  const setStatus = async (kind, id, current) => {
    const list = STATUSES.map((s, i) => `${i + 1}. ${s.label}`).join("\n");
    const sel = window.prompt(`Set status for this ${kind}\nCurrent: ${(current || "active").toUpperCase()}\n\n${list}\n\nEnter number 1–${STATUSES.length}:`, "1");
    if (!sel) return;
    const idx = parseInt(sel.trim(), 10) - 1;
    if (isNaN(idx) || idx < 0 || idx >= STATUSES.length) { toast.error("Invalid choice"); return; }
    const status = STATUSES[idx].value;
    let reason = null;
    if (["banned", "under_investigation", "property_damage_review"].includes(status)) {
      reason = window.prompt("Note / reason (visible in audit log):") || "";
    }
    setBusy(true);
    try {
      await adminApi.patch(`/admin/${kind === "business" ? "businesses" : "customers"}/${id}/status`, { status, ban_reason: reason });
      toast.success(`Status → ${STATUSES[idx].label}`);
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const remove = async (kind, id, name) => {
    if (!window.confirm(`PERMANENTLY DELETE ${kind === "business" ? "business" : "rider"} "${name || id}" and all their saved cards?\n\nThis cannot be undone.`)) return;
    setBusy(true);
    try {
      await adminApi.delete(`/admin/${kind === "business" ? "businesses" : "customers"}/${id}`);
      toast.success("Deleted");
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
    finally { setBusy(false); }
  };

  const verify = async (kind, id, currentlyVerified) => {
    try {
      await adminApi.patch(`/admin/${kind === "business" ? "businesses" : "customers"}/${id}/verify`, { verified: !currentlyVerified });
      toast.success(currentlyVerified ? "Unverified" : "Verified ✓");
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  // v2.7.7 · Gender-Preferred Dispatch · Inline rider gender + preferred-gender editor.
  const setRiderGenderField = async (rider, field, value) => {
    try {
      await adminApi.patch(`/admin/customers/${rider.id}/profile`, { [field]: value });
      toast.success(`Updated ${field.replace(/_/g, " ")}`);
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
  };

  // v2.6.9 batch BB-2 · Clear a rider's active flags (PIN-gated)
  const clearFlag = async (rider) => {
    const pin = window.prompt(
      `Clear ALL active flags on ${rider.name || rider.email || rider.id}?\n\n` +
      `Current: ${(rider.flag_severity || "").toUpperCase()} · ${(rider.rider_flags || []).length} flag(s)\n\n` +
      `Enter your Operating Partner PIN to confirm:`,
      "",
    );
    if (!pin) return;
    const reason = window.prompt("Optional reason / note for the audit trail (leave blank to skip):", "") || "";
    setBusy(true);
    try {
      const { data } = await adminApi.post(`/admin/customers/${rider.id}/clear-flag`, { pin: pin.trim(), reason: reason.trim() || null });
      toast.success(`Cleared ${data.cleared || 0} flag(s) on ${rider.name || rider.email}`);
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Clear flag failed"); }
    finally { setBusy(false); }
  };

  const rows = [
    ...(filter === "business" ? [] : customers.map(c => ({ ...c, _kind: "rider" }))),
    ...(filter === "rider" ? [] : businesses.map(b => ({ ...b, _kind: "business", name: b.business_name }))),
  ].sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));

  // Only riders can be bulk-deleted (business deletion has its own flow + cascade)
  const selectableIds = rows.filter(r => r._kind === "rider").map(r => r.id);
  const allSelected = selectableIds.length > 0 && selectableIds.every(id => selected.has(id));
  const toggleAll = () => {
    if (allSelected) setSelected(new Set());
    else setSelected(new Set(selectableIds));
  };
  const toggleOne = (id) => {
    const s = new Set(selected);
    if (s.has(id)) s.delete(id); else s.add(id);
    setSelected(s);
  };

  const bulkDelete = async () => {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    if (!window.confirm(`PERMANENTLY DELETE ${ids.length} rider account(s) and ALL their saved cards?\n\nThis cannot be undone.`)) return;
    setBusy(true);
    try {
      const { data } = await adminApi.post("/admin/customers/bulk-delete", { customer_ids: ids });
      toast.success(`${data.deleted} rider${data.deleted === 1 ? "" : "s"} deleted`);
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Bulk delete failed"); }
    finally { setBusy(false); }
  };

  const STATUS_TINT = {
    active: "border-green-400/50 text-green-400",
    inactive: "border-zinc-600 text-zinc-400",
    banned: "border-red-500/60 text-red-400",
    deactivation: "border-orange-400/50 text-orange-400",
    under_investigation: "border-yellow-400/60 text-yellow-400",
    property_damage_review: "border-purple-400/60 text-purple-400",
  };

  return (
    <div data-testid="customers-tab-wrap">
      {filter !== "business" && <DuplicateCustomersTool onMerged={reload} />}
      <Card className="bg-zinc-900 border-zinc-800 p-5" data-testid="customers-tab">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <h3 className="font-bold">Riders & Businesses ({rows.length})</h3>
        <div className="flex gap-1 items-center flex-wrap">
          {selected.size > 0 && (
            <Button size="sm" onClick={bulkDelete} disabled={busy} className="bg-red-500 hover:bg-red-400 text-white font-bold h-8 text-xs" data-testid="bulk-delete-customers">
              <Trash2 className="h-3 w-3 mr-1"/>Delete {selected.size} selected
            </Button>
          )}
          <Button size="sm" variant={filter === "all" ? "default" : "outline"} onClick={()=>setFilter("all")} className={filter === "all" ? "bg-amber-400 text-zinc-950" : "bg-zinc-950 border-zinc-700"} data-testid="cust-filter-all">All</Button>
          <Button size="sm" variant={filter === "rider" ? "default" : "outline"} onClick={()=>setFilter("rider")} className={filter === "rider" ? "bg-amber-400 text-zinc-950" : "bg-zinc-950 border-zinc-700"} data-testid="cust-filter-rider">Riders ({customers.length})</Button>
          <Button size="sm" variant={filter === "business" ? "default" : "outline"} onClick={()=>setFilter("business")} className={filter === "business" ? "bg-amber-400 text-zinc-950" : "bg-zinc-950 border-zinc-700"} data-testid="cust-filter-business">Business ({businesses.length})</Button>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-[10px] uppercase tracking-wider text-zinc-500 border-b border-zinc-800">
            <tr>
              <th className="text-left p-2 w-8"><input type="checkbox" checked={allSelected} onChange={toggleAll} disabled={selectableIds.length === 0} data-testid="select-all-riders" className="accent-amber-400"/></th>
              <th className="text-left p-2">Type</th>
              <th className="text-left p-2">Name</th>
              <th className="text-left p-2">Email</th>
              <th className="text-left p-2">Phone</th>
              <th className="text-left p-2">Gender / Pref</th>
              <th className="text-left p-2">Status</th>
              <th className="text-left p-2">Verified</th>
              <th className="text-right p-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={9} className="text-center p-6 text-zinc-500">No accounts.</td></tr>}
            {rows.map(c => (
              <tr key={`${c._kind}-${c.id}`} className="border-b border-zinc-800" data-testid={`cust-row-${c.id}`}>
                <td className="p-2">{c._kind === "rider" && <input type="checkbox" checked={selected.has(c.id)} onChange={() => toggleOne(c.id)} data-testid={`select-rider-${c.id}`} className="accent-amber-400"/>}</td>
                <td className="p-2"><Badge variant="outline" className={c._kind === "business" ? "border-blue-400/40 text-blue-300 text-[10px] uppercase" : "border-zinc-700 text-[10px] uppercase"}>{c._kind === "business" ? "BUSINESS" : "RIDER"}</Badge></td>
                <td className="p-2 font-semibold">
                  <div className="flex items-center gap-1.5">
                    <span>{c.name}</span>
                    {c.flag_severity && c._kind === "rider" && (
                      <RiderFlagBanner
                        flag={{
                          severity: c.flag_severity,
                          flag_count: (c.rider_flags || []).length,
                          latest_reason: (c.rider_flags || []).slice(-1)[0]?.reason,
                          latest_incident_number: (c.rider_flags || []).slice(-1)[0]?.incident_number,
                          flagged_at: c.flagged_at,
                          customer_name: c.name,
                        }}
                        compact
                      />
                    )}
                  </div>
                </td>
                <td className="p-2 text-zinc-400">{c.email}</td>
                <td className="p-2 text-zinc-400">{c.phone}</td>
                <td className="p-2">
                  {c._kind === "rider" ? (
                    <div className="flex flex-col gap-1 min-w-[140px]" data-testid={`cust-gender-cell-${c.id}`}>
                      <select
                        value={c.gender || "unspecified"}
                        onChange={(e) => setRiderGenderField(c, "gender", e.target.value)}
                        className="bg-zinc-950 border border-zinc-700 rounded text-[10px] text-zinc-100 px-1.5 py-0.5"
                        data-testid={`cust-gender-${c.id}`}
                        title="Rider self-identified gender"
                      >
                        <option value="unspecified">U</option>
                        <option value="male">M</option>
                        <option value="female">F</option>
                      </select>
                      <select
                        value={c.preferred_driver_gender || "any"}
                        onChange={(e) => setRiderGenderField(c, "preferred_driver_gender", e.target.value)}
                        className="bg-zinc-950 border border-zinc-700 rounded text-[10px] text-amber-300 px-1.5 py-0.5"
                        data-testid={`cust-pref-${c.id}`}
                        title="Preferred driver gender for routing"
                      >
                        <option value="any">Pref: Any</option>
                        <option value="male">Pref: M</option>
                        <option value="female">Pref: F</option>
                      </select>
                    </div>
                  ) : (
                    <span className="text-zinc-600 text-[10px]">—</span>
                  )}
                </td>
                <td className="p-2"><Badge variant="outline" className={`text-[10px] uppercase ${STATUS_TINT[c.status || "active"]}`}>{(c.status || "active").replace(/_/g, " ").toUpperCase()}</Badge>{c.ban_reason && <div className="text-[10px] text-zinc-400 mt-0.5">⚠ {c.ban_reason}</div>}</td>
                <td className="p-2"><VerifiedBadge verified={c.verified} size="sm"/></td>
                <td className="p-2 text-right">
                  <div className="flex gap-1 justify-end">
                    {c.flag_severity && c._kind === "rider" && (
                      <Button size="sm" variant="ghost" onClick={() => clearFlag(c)} disabled={busy} className="text-emerald-300 hover:text-emerald-200 h-7 text-[10px] px-2 border border-emerald-400/30 hover:bg-emerald-500/10" data-testid={`clear-flag-${c.id}`} title="Clear active rider flags (PIN required)">
                        <Flag className="h-3 w-3 mr-1"/>Clear Flag
                      </Button>
                    )}
                    <Button size="sm" variant="ghost" onClick={() => verify(c._kind, c.id, c.verified)} className="text-amber-400 h-7 text-[10px] px-2" data-testid={`verify-${c.id}`}><ShieldCheck className="h-3 w-3 mr-1"/>{c.verified ? "Unverify" : "Verify"}</Button>
                    <Button size="sm" variant="ghost" onClick={() => setStatus(c._kind, c.id, c.status)} disabled={busy} className="text-zinc-300 h-7 text-[10px] px-2" data-testid={`status-${c.id}`}>Status</Button>
                    <Button size="sm" variant="ghost" onClick={() => remove(c._kind, c.id, c.name)} disabled={busy} className="text-red-400 hover:text-red-300 h-7 px-2" data-testid={`delete-cust-${c.id}`} title="Delete account"><Trash2 className="h-3 w-3"/></Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
    </div>
  );
}

function RidesTab() {
  const [rides, setRides] = useState([]);
  const [trips, setTrips] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [tripSelected, setTripSelected] = useState(new Set());
  const [busy, setBusy] = useState(false);
  // v2.6.9 batch X · click-to-view detail dialog
  const [viewing, setViewing] = useState(null);
  // v2.7.0 batch DC · edit-trip dialog (universal edit + override + receipt + audit)
  const [editingTripId, setEditingTripId] = useState(null);
  // v2.7.5 Phase 3 · search filter + 25-rides-per-page pagination
  // (v2.7.18: switched from 7-days-per-page → 25-items-per-page so the
  // active view never overwhelms dispatch).
  const [searchQuery, setSearchQuery] = useState("");
  const [searchFrom, setSearchFrom] = useState("");  // YYYY-MM-DD
  const [searchTo, setSearchTo] = useState("");      // YYYY-MM-DD
  // v2.9.0 · "Booked By" dispatcher filter — narrows the ledger + CSV export
  // to a single dispatcher's rides. "" = all dispatchers.
  const [dispatcherFilter, setDispatcherFilter] = useState("");
  const [pageIdx, setPageIdx] = useState(0);
  const RIDES_PER_PAGE = 25;

  // v2.7.13 · Completed Rides surface — the sidebar link is named
  // "Completed Rides", so default the data load to completed-only and group
  // by `completed_at` (the date the trip actually ended), not booking
  // creation time. The toggle below lets staff peek at the full list when
  // debugging dispatch.
  const [completedOnly, setCompletedOnly] = useState(true);
  // v2.7.65+E205 · Historical trip-total repair dialog
  const [repairOpen, setRepairOpen] = useState(false);
  const [restoreOpen, setRestoreOpen] = useState(false);
  // v2.7.20 · Live-ledger arrival flash — `rtg:trip_completed` adds a row's
  // id here and a 1.5s timer removes it; matching rows get the
  // .ledger-row-flash CSS animation so dispatchers see new trips pulse in.
  const [recentlyArrived, setRecentlyArrived] = useState(() => new Set());

  const reload = useCallback(() => {
    // v2.8.9 · Raise the fetch ceiling (was 1000/500 — older days fell off
    // the ledger) and pass the searched date window so historical trips load.
    let dp = "";
    if (searchFrom) dp += `&date_from=${searchFrom}`;
    if (searchTo) dp += `&date_to=${searchTo}`;
    const ridesUrl = completedOnly ? `/admin/rides?completed_only=1&limit=50000${dp}` : `/admin/rides?limit=50000${dp}`;
    adminApi.get(ridesUrl).then(r => setRides(r.data)).catch(()=>{});
    adminApi.get(`/admin/trips?limit=50000${dp}`).then(r => setTrips(r.data)).catch(()=>{});
  }, [completedOnly, searchFrom, searchTo]);
  useEffect(() => { reload(); }, [reload]);

  // v2.7.18 · LIVE LEDGER STREAM — every time a driver closes a trip,
  // useFleetStream re-broadcasts `rtg:trip_completed` as a window event.
  // We listen here and refetch so the new row drops into the right day
  // block + bumps the "Total Rides" badge without a page refresh.
  // v2.7.20 · Also stamp the arriving trip_id into `recentlyArrived` so
  // the matching ledger row plays the amber-flash animation when it
  // (almost certainly) appears in the next reload.
  useEffect(() => {
    const onCompleted = (e) => {
      const detail = e?.detail || {};
      const tripId = detail.trip_id;
      try {
        toast.success(`New completed trip · ${detail.name || "driver"} · ${fmtUsd(detail.total || 0)}`, {
          duration: 2500,
          id: `trip-completed-${tripId || Date.now()}`,
        });
      } catch { /* noop */ }
      if (tripId) {
        setRecentlyArrived((prev) => {
          const next = new Set(prev);
          next.add(tripId);
          return next;
        });
        // Drop the id after the animation completes + buffer
        setTimeout(() => {
          setRecentlyArrived((prev) => {
            if (!prev.has(tripId)) return prev;
            const next = new Set(prev);
            next.delete(tripId);
            return next;
          });
        }, 1500);
      }
      reload();
    };
    window.addEventListener("rtg:trip_completed", onCompleted);
    return () => window.removeEventListener("rtg:trip_completed", onCompleted);
  }, [reload]);

  // Color tier by fare — matches user spec: <$10 green · ≥$10 orange · ≥$50 yellow
  const fareTier = (total) => {
    const t = Number(total || 0);
    if (t >= 50) return "border-l-4 border-l-yellow-400 bg-yellow-400/[0.04]";
    if (t >= 10) return "border-l-4 border-l-orange-400 bg-orange-400/[0.03]";
    return "border-l-4 border-l-green-400 bg-green-400/[0.03]";
  };
  const tripTier = (total) => {
    const t = Number(total || 0);
    if (t >= 50) return "border-l-4 border-l-yellow-400 bg-yellow-400/[0.04]";
    if (t >= 10) return "border-l-4 border-l-orange-400 bg-orange-400/[0.03]";
    return "border-l-4 border-l-green-400 bg-green-400/[0.03]";
  };

  const toggleAll = () => {
    if (selected.size === rides.length) setSelected(new Set());
    else setSelected(new Set(rides.map(r => r.id)));
  };
  const toggleOne = (id) => {
    const s = new Set(selected); s.has(id) ? s.delete(id) : s.add(id); setSelected(s);
  };
  const toggleAllTrips = () => {
    if (tripSelected.size === trips.length) setTripSelected(new Set());
    else setTripSelected(new Set(trips.map(t => t.id)));
  };
  const toggleOneTrip = (id) => {
    const s = new Set(tripSelected); s.has(id) ? s.delete(id) : s.add(id); setTripSelected(s);
  };

  const deleteSelected = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`PERMANENTLY DELETE ${selected.size} ride${selected.size === 1 ? "" : "s"}?`)) return;
    setBusy(true);
    try {
      const { data } = await adminApi.post("/admin/rides/delete", { ride_ids: Array.from(selected) });
      toast.success(`Deleted ${data.deleted}`);
      setSelected(new Set()); reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
    finally { setBusy(false); }
  };
  const deleteSelectedTrips = async () => {
    if (tripSelected.size === 0) return;
    if (!window.confirm(`PERMANENTLY DELETE ${tripSelected.size} driver trip${tripSelected.size === 1 ? "" : "s"}?`)) return;
    setBusy(true);
    try {
      const { data } = await adminApi.post("/admin/trips/delete", { trip_ids: Array.from(tripSelected) });
      toast.success(`Deleted ${data.deleted}`);
      setTripSelected(new Set()); reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
    finally { setBusy(false); }
  };

  // Mass action: mark/unmark selected rides as TEST (excluded from fees rollup).
  // v2.6.6: reuses the Admin Gate PIN — no second prompt needed.
  const markTest = async (isTest) => {
    const ride_ids = Array.from(selected);
    const trip_ids = Array.from(tripSelected);
    if (ride_ids.length === 0 && trip_ids.length === 0) {
      toast.error("Select at least one ride or trip first");
      return;
    }
    const pin = (typeof window !== "undefined" && window.__adminGatePin) || "";
    if (!pin) {
      toast.error("Session expired — refresh and re-enter your Operating Partner PIN");
      return;
    }
    setBusy(true);
    try {
      const { data } = await adminApi.post("/admin/mark-test", { pin, ride_ids, trip_ids, is_test: isTest });
      toast.success(`${isTest ? "Marked" : "Cleared"}: ${data.rides_modified} ride${data.rides_modified === 1 ? "" : "s"} + ${data.trips_modified} trip${data.trips_modified === 1 ? "" : "s"}`);
      setSelected(new Set()); setTripSelected(new Set()); reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Mark-test failed"); }
    finally { setBusy(false); }
  };

  const exportCsv = (idsOnly = false) => {
    // v2.9.0 · "Export ALL" now mirrors the on-screen filters (completed-only
    // + date window + Booked By dispatcher + text search) so the CSV matches
    // exactly what the operator is looking at. "Export Selected" still sends
    // only the checked ride ids.
    const params = new URLSearchParams();
    if (idsOnly) {
      params.set("ids", Array.from(selected).join(","));
    } else {
      if (completedOnly) params.set("completed_only", "1");
      if (searchFrom) params.set("date_from", searchFrom);
      if (searchTo) params.set("date_to", searchTo);
      if (dispatcherFilter) params.set("dispatcher", dispatcherFilter);
      if (searchQuery.trim()) params.set("q", searchQuery.trim());
    }
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/admin/rides/export.csv?${params.toString()}`;
    const tok = localStorage.getItem("rtg_admin_token");
    fetch(url, { headers: { Authorization: `Bearer ${tok}` } })
      .then(r => r.blob())
      .then(blob => {
        const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = "buller-rides.csv"; a.click();
      });
  };
  // v2.7.20 · Bulk Receipt Export — uses the date pickers above the
  // ledger. When BOTH `searchFrom` and `searchTo` are blank, the backend
  // defaults to the last 30 days. Streams as a ZIP.
  const exportReceiptsZip = async () => {
    setBusy(true);
    try {
      const qs = new URLSearchParams();
      if (searchFrom) qs.set("from", searchFrom);
      if (searchTo) qs.set("to", searchTo);
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/admin/rides/export.zip${qs.toString() ? `?${qs.toString()}` : ""}`;
      const tok = localStorage.getItem("rtg_admin_token");
      const res = await fetch(url, { headers: { Authorization: `Bearer ${tok}` } });
      if (!res.ok) {
        const msg = await res.text().catch(() => "");
        throw new Error(`HTTP ${res.status}: ${msg || res.statusText}`);
      }
      const count = res.headers.get("x-bulk-receipt-count") || "?";
      const blob = await res.blob();
      const disposition = res.headers.get("content-disposition") || "";
      const fname = (disposition.match(/filename="?([^";]+)"?/) || [])[1] || "buller-receipts.zip";
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = fname;
      a.click();
      toast.success(`ZIP downloaded · ${count} receipt${count === "1" ? "" : "s"}`);
    } catch (e) {
      toast.error(`Bulk export failed: ${e?.message || e}`);
    } finally {
      setBusy(false);
    }
  };
  // v2.7.55+E32 · Bulk Push to Dropbox · uses the SAME date pickers
  // as the ZIP export and uploads each rendered PDF to
  // /Receipts/{YYYY-MM}/. Idempotent: re-running the same window
  // overwrites existing files rather than duplicating.
  const saveReceiptsToDropbox = async () => {
    if (busy) return;
    const win = (searchFrom || searchTo)
      ? `${searchFrom || "earliest"} → ${searchTo || "now"}`
      : "the last 30 days";
    if (!window.confirm(
      `Push every completed-ride receipt for ${win} into Dropbox at /Receipts/YYYY-MM/?\n\n` +
      `This is idempotent — re-running overwrites existing files in place. Capped at 5,000 rides per call.`
    )) return;
    setBusy(true);
    try {
      const body = {};
      if (searchFrom) body.from = searchFrom;
      if (searchTo) body.to = searchTo;
      const tok = localStorage.getItem("rtg_admin_token");
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/dropbox/files/save-bulk-trip-receipts`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${tok}` },
        body: JSON.stringify(body),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data?.detail || `HTTP ${res.status}`);
      }
      const { succeeded = 0, failed = 0, total_found = 0, truncated = false, folders = [] } = data;
      const folderHint = folders.length === 1 ? folders[0]
        : folders.length > 1 ? `${folders.length} monthly folders`
        : "/Receipts/";
      if (failed > 0) {
        toast.warning(`Pushed ${succeeded} · ${failed} failed · → ${folderHint}${truncated ? " (truncated to 5,000)" : ""}`);
      } else if (succeeded === 0) {
        toast.info(`No completed rides in ${win}.`);
      } else {
        toast.success(`Pushed ${succeeded} receipt${succeeded === 1 ? "" : "s"} → ${folderHint}${truncated ? ` (${total_found} found · truncated to 5,000)` : ""}`);
      }
    } catch (e) {
      const msg = String(e?.message || e);
      if (msg.includes("restricted to staff") || msg.includes("Read-only")) {
        toast.error(msg);
      } else if (msg.toLowerCase().includes("not connected") || msg.toLowerCase().includes("dropbox")) {
        toast.error(`Dropbox push failed: ${msg}`);
      } else {
        toast.error(`Dropbox push failed: ${msg}`);
      }
    } finally {
      setBusy(false);
    }
  };
  const exportTripsCsv = (idsOnly = false) => {
    // v2.9.0 · Driver-trips CSV mirrors the on-screen filters (date window +
    // Booked By dispatcher + text search) unless exporting a checked selection.
    const params = new URLSearchParams();
    if (idsOnly) {
      params.set("ids", Array.from(tripSelected).join(","));
    } else {
      if (searchFrom) params.set("date_from", searchFrom);
      if (searchTo) params.set("date_to", searchTo);
      if (dispatcherFilter) params.set("dispatcher", dispatcherFilter);
      if (searchQuery.trim()) params.set("q", searchQuery.trim());
    }
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/admin/trips/export.csv?${params.toString()}`;
    const tok = localStorage.getItem("rtg_admin_token");
    fetch(url, { headers: { Authorization: `Bearer ${tok}` } })
      .then(r => r.blob())
      .then(blob => {
        const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = "buller-driver-trips.csv"; a.click();
      });
  };
  const exportTripPDF = (t) => {
    // Build a single-trip waybill-style PDF on the fly via the shared helper
    downloadWaybillPDF({
      id: t.id, waybill_no: t.id?.slice(0, 8) || "trip", origin: t.origin, destination: t.destination,
      created_at: t.created_at, customer_name: t.customer_name || "—", customer_phone: t.customer_phone || "—",
      driver_name: t.driver_name || t.driver_email || "—", passengers: t.passengers || 1,
      total: t.total, quoted_fare: t.subtotal, payment_method: t.payment_method,
      breakdown: t.breakdown,
    });
  };

  // Driver-trip detail dialog — clickable rows
  const [detailTrip, setDetailTrip] = useState(null);

  // v2.7.5 Phase 3 · group filtered rides into YYYY-MM-DD date buckets in
  // local time. The bucket order matches "most-recent day first".
  // v2.7.13 · For the Completed Rides surface we group by `completed_at`
  // (the moment the trip actually ended), then fall back to `created_at`
  // for any ride that's missing it. Driver-saved Live Meter trips are
  // ALSO bucketed into the same day blocks so the per-day "Total Rides"
  // count is the true count of completed activity for that day.
  const _dayKey = (iso) => {
    const d = new Date(iso || 0);
    if (Number.isNaN(d.getTime())) return "—";
    return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
  };
  const _rideDate = (r) => r.completed_at || r.created_at;
  const _tripDate = (t) => t.ended_at || t.completed_at || t.created_at;

  // Build the paginated view: flatten BOTH customer rides + driver trips
  // into a single time-sorted ledger, slice 25 per page, and group the
  // visible slice by day so the date header + Total Rides badge are
  // accurate for that day. The Total Rides badge intentionally reflects
  // the GLOBAL count for that day (across all pages) so dispatchers always
  // see the true total — even if only 5 of 30 fit on the current page.
  const totalRideCount = rides.length + trips.length;
  // v2.9.0 · Distinct dispatchers present in the loaded ledger, for the
  // "Booked By" filter dropdown. Sorted alphabetically.
  const dispatcherOptions = useMemo(() => {
    const s = new Set();
    for (const r of rides) if (r.booked_by_dispatcher) s.add(r.booked_by_dispatcher);
    for (const t of trips) if (t.booked_by_dispatcher) s.add(t.booked_by_dispatcher);
    return [...s].sort((a, b) => a.localeCompare(b));
  }, [rides, trips]);
  const { visibleBlocks, totalPages, safePage, visibleRangeStart, visibleRangeEnd, totalItemsAcrossAllPages } = useMemo(() => {
    const q = (searchQuery || "").trim().toLowerCase();
    const filteredRides = rides.filter((r) => {
      if (dispatcherFilter && (r.booked_by_dispatcher || "") !== dispatcherFilter) return false;
      if (q) {
        const blob = [r.waybill_no, r.customer_name, r.business_name, r.origin, r.destination, r.driver_name, r.driver_email, r.id].filter(Boolean).join(" ").toLowerCase();
        if (!blob.includes(q)) return false;
      }
      if (searchFrom || searchTo) {
        const key = _dayKey(_rideDate(r));
        if (key === "—") return false;
        if (searchFrom && key < searchFrom) return false;
        if (searchTo && key > searchTo) return false;
      }
      return true;
    });
    const filteredTrips = trips.filter((t) => {
      if (dispatcherFilter && (t.booked_by_dispatcher || "") !== dispatcherFilter) return false;
      if (q) {
        const blob = [t.customer_name, t.origin, t.destination, t.driver_name, t.id].filter(Boolean).join(" ").toLowerCase();
        if (!blob.includes(q)) return false;
      }
      if (searchFrom || searchTo) {
        const key = _dayKey(_tripDate(t));
        if (key === "—") return false;
        if (searchFrom && key < searchFrom) return false;
        if (searchTo && key > searchTo) return false;
      }
      return true;
    });
    // v2.8.9 · De-duplicate the Completed Rides ledger — same reliable-link
    // logic as the driver feed. A dispatch trip that also has a meter
    // `db.trips` row appears once (the booking/ride record wins → manual
    // dispatcher price). Only drops a trip when it RELIABLY links to a ride
    // already in the list (shared id, dispatch_trip_id, or booking backref).
    const rideIdSet = new Set(filteredRides.map((r) => r.id));
    const dedupTrips = filteredTrips.filter((t) => {
      if (t.id && rideIdSet.has(t.id)) return false;
      const link = t.dispatch_trip_id || (t.breakdown && (t.breakdown.booking_trip_id || t.breakdown.booked_trip_id));
      if (link && rideIdSet.has(link)) return false;
      return true;
    });
    const flat = [
      ...filteredRides.map((r) => ({ kind: "ride", item: r, when: _rideDate(r) })),
      ...dedupTrips.map((t) => ({ kind: "trip", item: t, when: _tripDate(t) })),
    ].sort((a, b) => (new Date(b.when || 0).getTime() || 0) - (new Date(a.when || 0).getTime() || 0));

    const pages = Math.max(1, Math.ceil(flat.length / RIDES_PER_PAGE));
    const sp = Math.min(pageIdx, pages - 1);
    const start = sp * RIDES_PER_PAGE;
    const end = Math.min(start + RIDES_PER_PAGE, flat.length);
    const slice = flat.slice(start, end);
    const groups = new globalThis.Map();
    for (const row of slice) {
      const key = _dayKey(row.when);
      if (!groups.has(key)) groups.set(key, { rides: [], trips: [] });
      if (row.kind === "ride") groups.get(key).rides.push(row.item);
      else groups.get(key).trips.push(row.item);
    }
    // Global per-day totals across ALL filtered items (not just visible slice)
    const globalGroups = new globalThis.Map();
    for (const r of filteredRides) {
      const k = _dayKey(_rideDate(r));
      if (!globalGroups.has(k)) globalGroups.set(k, { r: 0, t: 0 });
      globalGroups.get(k).r += 1;
    }
    for (const t of dedupTrips) {
      const k = _dayKey(_tripDate(t));
      if (!globalGroups.has(k)) globalGroups.set(k, { r: 0, t: 0 });
      globalGroups.get(k).t += 1;
    }
    const sortedKeys = [...groups.keys()].sort((a, b) => b.localeCompare(a));
    const blocks = sortedKeys.map((k) => {
      const g = groups.get(k);
      const day = globalGroups.get(k) || { r: 0, t: 0 };
      return {
        date: k,
        rides: g.rides,
        trips: g.trips,
        totalCount: day.r + day.t,  // GLOBAL day total
      };
    });
    return {
      visibleBlocks: blocks,
      totalPages: pages,
      safePage: sp,
      visibleRangeStart: flat.length === 0 ? 0 : start + 1,
      visibleRangeEnd: end,
      totalItemsAcrossAllPages: flat.length,
    };
  }, [rides, trips, searchQuery, searchFrom, searchTo, dispatcherFilter, pageIdx]);
  const prettyDay = (k) => {
    if (k === "—") return "Unknown Date";
    const [y, m, d] = k.split("-");
    const dt = new Date(Number(y), Number(m) - 1, Number(d));
    return dt.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric", year: "numeric" });
  };

  return (
    <div className="space-y-4" data-testid="rides-tab">
      <Card className="bg-zinc-900 border-zinc-800 p-5">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
          <h3 className="font-bold flex items-center gap-2"><FileText className="h-4 w-4 text-amber-400"/>{completedOnly ? "Completed Rides" : "Customer Bookings & Waybills"} ({totalRideCount})</h3>
          <div className="flex items-center gap-2 flex-wrap">
            {/* v2.7.13 · Toggle between "Completed only" (default) and "All rides"
                so dispatch can peek at booked/pending when debugging. */}
            <label className="inline-flex items-center gap-1.5 text-[11px] text-zinc-400 cursor-pointer select-none px-2 py-1 rounded border border-zinc-700 bg-zinc-950 hover:border-amber-400/40" data-testid="rides-completed-only-toggle">
              <input
                type="checkbox"
                checked={completedOnly}
                onChange={(e) => setCompletedOnly(e.target.checked)}
                className="accent-amber-400"
                data-testid="rides-completed-only-checkbox"
              />
              <span className="font-bold uppercase tracking-wider text-[10px]">Completed Only</span>
            </label>
            <span className="text-[10px] uppercase tracking-widest text-zinc-500 mr-2">FARE TIER</span>
            <span className="inline-flex items-center gap-1 text-[10px] text-green-400"><span className="w-2 h-2 bg-green-400"/>&lt;$10</span>
            <span className="inline-flex items-center gap-1 text-[10px] text-orange-400"><span className="w-2 h-2 bg-orange-400"/>≥$10</span>
            <span className="inline-flex items-center gap-1 text-[10px] text-yellow-400"><span className="w-2 h-2 bg-yellow-400"/>≥$50</span>
            <Badge variant="outline" className="border-amber-400/40 text-amber-400 ml-2" data-testid="rides-selected">{selected.size} selected</Badge>
            <Button size="sm" onClick={() => exportCsv(false)} variant="outline" className="bg-zinc-950 border-zinc-700 h-8 text-xs" data-testid="export-csv-all"><Download className="h-3 w-3 mr-1"/>Export ALL CSV</Button>
            <Button size="sm" onClick={() => exportCsv(true)} disabled={selected.size === 0} variant="outline" className="bg-zinc-950 border-amber-400/40 text-amber-400 h-8 text-xs" data-testid="export-csv-selected"><Download className="h-3 w-3 mr-1"/>Export Selected</Button>
            {/* v2.7.20 · Bulk Receipt Export · ZIP of 2-page waybills for the
                date-range below. Default: last 30 days when no dates set. */}
            <Button size="sm" onClick={exportReceiptsZip} disabled={busy} variant="outline" className="bg-emerald-500/10 border-emerald-400/50 text-emerald-300 h-8 text-xs" data-testid="export-receipts-zip" title="Bulk-export 2-page receipts as ZIP for the date range below (or last 30 days if blank)"><Download className="h-3 w-3 mr-1"/>Receipts ZIP</Button>
            {/* v2.7.55+E32 · Bulk push completed-ride receipts straight into
                Dropbox at /Receipts/YYYY-MM/. Idempotent overwrites. */}
            <Button size="sm" onClick={saveReceiptsToDropbox} disabled={busy} variant="outline" className="!bg-sky-500/15 !border-sky-400 !text-sky-300 hover:!bg-sky-500/25 h-8 text-xs" data-testid="save-receipts-dropbox" title="Push every completed-ride receipt in the selected date range to Dropbox /Receipts/YYYY-MM/"><svg viewBox="0 0 32 32" className="h-3 w-3 mr-1" fill="currentColor" aria-hidden="true"><path d="M8 4l8 5.2L8 14.4l-8-5.2L8 4zm16 0l8 5.2-8 5.2-8-5.2L24 4zM0 19.6l8-5.2 8 5.2-8 5.2-8-5.2zm24-5.2l8 5.2-8 5.2-8-5.2 8-5.2zM8 26.4l8-5.2 8 5.2L16 32 8 26.4z"/></svg>Save All to Dropbox</Button>
            <Button size="sm" onClick={deleteSelected} disabled={busy || selected.size === 0} variant="outline" className="bg-red-500/10 border-red-500/40 text-red-300 h-8 text-xs" data-testid="rides-delete-selected"><Trash2 className="h-3 w-3 mr-1"/>Delete Selected</Button>
            <Button size="sm" onClick={() => markTest(true)} disabled={busy || selected.size === 0} variant="outline" className="bg-purple-500/10 border-purple-400/40 text-purple-300 h-8 text-xs" data-testid="rides-mark-test"><FileText className="h-3 w-3 mr-1"/>Mark as TEST</Button>
            <Button size="sm" onClick={() => markTest(false)} disabled={busy || selected.size === 0} variant="outline" className="bg-zinc-950 border-zinc-700 h-8 text-xs" data-testid="rides-unmark-test">Un-mark TEST</Button>
            {/* v2.7.65+E205 · PIN-gated repair for trips left at $0.00 by
                the old completion mirror + rides hidden by a stamped
                dispatch-board status. Preview first, then Apply. */}
            <Button size="sm" onClick={() => setRepairOpen(true)} variant="outline" className="bg-amber-400/10 border-amber-400/50 text-amber-300 hover:bg-amber-400/20 h-8 text-xs" data-testid="rides-repair-totals-open" title="Find and restore trip totals that were reset to $0.00, and bring back completed rides missing from this ledger">
              <Wrench className="h-3 w-3 mr-1"/>Repair Totals
            </Button>
            {/* v2.8.9 · One-click restore for the 08/29-style gap: pulls
                driver-completed dispatch trips back into Completed Rides,
                links old meter trips to their booking (kills duplicates) and
                recovers $0 totals from the dispatcher's price. Dry-run first. */}
            <Button size="sm" onClick={() => setRestoreOpen(true)} variant="outline" className="bg-emerald-500/10 border-emerald-400/50 text-emerald-300 hover:bg-emerald-500/20 h-8 text-xs" data-testid="rides-restore-open" title="Preview & restore completed rides missing from this ledger, de-duplicate meter trips, and recover missing totals — nothing is deleted">
              <Wrench className="h-3 w-3 mr-1"/>Repair / Restore Rides
            </Button>
          </div>
        </div>

        {/* v2.7.5 Phase 3 · Date-time lookup search bar */}
        <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_auto_auto_auto] gap-2 mb-3 p-2 bg-zinc-950/60 rounded border border-zinc-800" data-testid="rides-search-bar">
          <Input
            value={searchQuery}
            onChange={(e) => { setSearchQuery(e.target.value); setPageIdx(0); }}
            placeholder="Search waybill / customer / route / driver…"
            className="bg-zinc-950 border-zinc-700 h-8 text-sm"
            data-testid="rides-search-text"
          />
          <Input
            type="date"
            value={searchFrom}
            onChange={(e) => { setSearchFrom(e.target.value); setPageIdx(0); }}
            className="bg-zinc-950 border-zinc-700 h-8 text-sm w-40"
            data-testid="rides-search-from"
            title="From date"
          />
          <Input
            type="date"
            value={searchTo}
            onChange={(e) => { setSearchTo(e.target.value); setPageIdx(0); }}
            className="bg-zinc-950 border-zinc-700 h-8 text-sm w-40"
            data-testid="rides-search-to"
            title="To date"
          />
          {/* v2.9.0 · "Booked By" dispatcher filter — auto-populated from the
              dispatchers present in the loaded ledger. "All" clears it. */}
          <Select
            value={dispatcherFilter || "__all__"}
            onValueChange={(v) => { setDispatcherFilter(v === "__all__" ? "" : v); setPageIdx(0); }}
          >
            <SelectTrigger className="bg-zinc-950 border-zinc-700 h-8 text-sm w-48" data-testid="rides-dispatcher-filter" title="Filter by the dispatcher who booked the ride">
              <SelectValue placeholder="Booked by · All dispatchers" />
            </SelectTrigger>
            <SelectContent className="bg-zinc-900 border-zinc-700 text-zinc-100 max-h-72">
              <SelectItem value="__all__" data-testid="rides-dispatcher-opt-all">Booked by · All dispatchers</SelectItem>
              {dispatcherOptions.map((name) => (
                <SelectItem key={name} value={name} data-testid={`rides-dispatcher-opt-${name}`}>{name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            size="sm"
            variant="outline"
            onClick={() => { setSearchQuery(""); setSearchFrom(""); setSearchTo(""); setDispatcherFilter(""); setPageIdx(0); }}
            className="bg-zinc-950 border-zinc-700 h-8 text-[11px]"
            data-testid="rides-search-clear"
          >
            <XIcon className="h-3 w-3 mr-1"/>Clear
          </Button>
        </div>

        <div className="space-y-4">
          {rides.length === 0 && <div className="text-zinc-500 text-sm">No customer bookings yet.</div>}
          {rides.length > 0 && (
            <div className="px-2 pb-2 border-b border-zinc-800 flex items-center justify-between text-xs text-zinc-400">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" data-testid="rides-select-all" checked={selected.size === rides.length} onChange={toggleAll} className="accent-amber-400"/>
                Select all
              </label>
              <span data-testid="rides-day-summary">
                Showing <span className="text-amber-400 font-bold">{visibleRangeStart}</span>–<span className="text-amber-400 font-bold">{visibleRangeEnd}</span> of <span className="text-amber-400 font-bold">{totalItemsAcrossAllPages}</span> completed trip{totalItemsAcrossAllPages === 1 ? "" : "s"} · page <span className="text-amber-400 font-bold">{safePage + 1}</span>/<span className="text-amber-400 font-bold">{totalPages}</span>
              </span>
            </div>
          )}

          {/* v2.7.5 Phase 3 · Date-grouped daily blocks (v2.7.18: 25-item
              pagination + driver-trip rows injected inline + extra
              breathing room on every row). */}
          {visibleBlocks.map((block) => (
            <div
              key={`day-${block.date}`}
              className="rounded-lg border border-zinc-800 bg-zinc-950/40 overflow-hidden"
              data-testid={`day-block-${block.date}`}
            >
              <div className="flex items-center justify-between gap-3 px-5 py-3 bg-gradient-to-r from-amber-400/10 to-transparent border-b border-zinc-800">
                <h4 className="font-bold text-xl tracking-tight" data-testid={`day-header-${block.date}`}>
                  {prettyDay(block.date)}
                </h4>
                <Badge
                  className="bg-amber-400/20 text-amber-300 border-amber-400/40 font-bold px-3 py-1"
                  data-testid={`day-count-${block.date}`}
                  title={`${block.totalCount} completed trip${block.totalCount === 1 ? "" : "s"} this day · across all pages`}
                >
                  Total Rides: {block.totalCount}
                </Badge>
              </div>
              <div className="space-y-3 p-5" data-testid={`day-rows-${block.date}`}>
                {block.rides.map(r => (
                  <div
                    key={r.id}
                    className={`bg-zinc-950 border border-zinc-800 rounded-lg p-4 flex items-start justify-between gap-4 cursor-pointer hover:border-amber-400/40 transition-colors ${selected.has(r.id) ? "ring-1 ring-amber-400/50" : ""} ${r.is_test ? "opacity-60 grayscale border-l-4 border-l-purple-400" : fareTier(r.total)} ${recentlyArrived.has(r.id) ? "ledger-row-flash" : ""}`}
                    data-testid={`adm-ride-${r.id}`}
                    onClick={(e) => {
                      if (e.target.closest && e.target.closest("input, button, [data-no-row-open]")) return;
                      setViewing(r);
                    }}
                    title="Click for full waybill detail"
                  >
                    <input type="checkbox" checked={selected.has(r.id)} onChange={()=>toggleOne(r.id)} className="accent-amber-400 mt-1.5 cursor-pointer flex-shrink-0" data-testid={`rides-select-${r.id}`}/>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge variant="outline" className={r.kind === "business" ? "border-blue-400/40 text-blue-300 text-[10px] uppercase" : "border-zinc-700 text-[10px] uppercase"}>{r.kind === "business" ? "BUSINESS" : (r.ride_type || "RIDER")}</Badge>
                        <span className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono">{r.waybill_no}</span>
                        <span className="text-[10px] tracking-wider text-zinc-600 font-mono">TRIP #{r.id?.slice(0, 8)}</span>
                        <Badge variant="outline" className={`text-[10px] uppercase ${r.status === "done" ? "border-green-400/50 text-green-400" : "border-zinc-700"}`}>{r.status}</Badge>
                        <Badge variant="outline" className="border-zinc-700 text-[10px]">{r.payment_method?.toUpperCase()}</Badge>
                        {r.booked_by_dispatcher && <Badge variant="outline" className="border-yellow-400/40 text-yellow-200 text-[10px] uppercase" data-testid={`ride-bookedby-${r.id}`}>Booked by {r.booked_by_dispatcher}</Badge>}
                        {r.dispatch_confirmed && <Badge variant="outline" className="border-emerald-400/50 text-emerald-300 text-[10px] uppercase" data-testid={`ride-confirmed-${r.id}`} title={`Dispatch-confirmed${r.dispatch_confirmed_by ? ` by ${r.dispatch_confirmed_by}` : ""}${r.dispatch_confirmed_at ? ` · ${new Date(r.dispatch_confirmed_at).toLocaleString()}` : ""}`}>✓ Dispatch-confirmed by Ashley</Badge>}
                        {r.is_test && <Badge variant="outline" className="border-purple-400/40 text-purple-300 text-[10px] uppercase" data-testid={`test-badge-${r.id}`}>TEST</Badge>}
                        {r.customer_type && <CustomerTypeBadge type={r.customer_type} short testid={`ride-customer-type-${r.id}`}/>}
                        {r.group_rider_count ? <span className="text-[10px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded bg-violet-500/15 text-violet-300 border border-violet-500/40" data-testid={`ride-group-riders-${r.id}`}>Group · {r.group_rider_count} riders</span> : null}
                      </div>
                      <div className="text-sm font-semibold mt-2 truncate">{r.business_name || r.customer_name} · {r.origin} → {r.destination}</div>
                      <div className="text-[11px] text-zinc-500 mt-1">{new Date(r.created_at).toLocaleString()} · {r.passengers} pax · payment: {r.payment_status} · driver: {r.driver_name || "—"}</div>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <div className="meter-display text-lg font-bold text-amber-400">{fmtUsd(r.total)}</div>
                      <div className="flex gap-1.5 mt-2">
                        <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); setEditingTripId(r.id); }} className="text-amber-300 text-xs h-7" data-testid={`adm-edit-${r.id}`} data-no-row-open>Edit</Button>
                        <Button size="sm" variant="ghost" onClick={() => downloadWaybillPDF(r)} className="text-amber-400 text-xs h-7" data-testid={`adm-dl-${r.id}`}><Download className="h-3 w-3 mr-1"/>PDF</Button>
                        <RefundButton ride={r} testid={`adm-refund-${r.id}`}/>
                      </div>
                    </div>
                  </div>
                ))}
                {/* v2.7.18 · Driver-saved Live Meter trips render INLINE with
                    customer rides in the same daily block — same color
                    coding + breathing room. The bottom "Driver Trips"
                    table is preserved below for power-user table view. */}
                {block.trips.map(t => (
                  <div
                    key={`trip-${t.id}`}
                    className={`bg-zinc-950 border border-zinc-800 rounded-lg p-4 flex items-start justify-between gap-4 cursor-pointer hover:border-amber-400/40 transition-colors ${tripSelected.has(t.id) ? "ring-1 ring-amber-400/50" : ""} ${t.is_test ? "opacity-60 grayscale border-l-4 border-l-purple-400" : tripTier(t.total)} ${recentlyArrived.has(t.id) ? "ledger-row-flash" : ""}`}
                    data-testid={`day-trip-${t.id}`}
                    onClick={(e) => {
                      if (e.target.closest && e.target.closest("input, button, [data-no-row-open]")) return;
                      setDetailTrip(t);
                    }}
                    title="Click for full driver trip detail"
                  >
                    <input type="checkbox" checked={tripSelected.has(t.id)} onChange={()=>toggleOneTrip(t.id)} className="accent-amber-400 mt-1.5 cursor-pointer flex-shrink-0" data-testid={`day-trip-select-${t.id}`}/>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge variant="outline" className="border-yellow-400/40 text-yellow-300 text-[10px] uppercase">DRIVER TRIP</Badge>
                        <span className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono">{t.id?.slice(0, 8)}</span>
                        <Badge variant="outline" className="border-amber-400/30 text-amber-400 uppercase text-[10px]">{t.mode}</Badge>
                        <Badge variant="outline" className="border-zinc-700 text-[10px]">{(t.payment_method || "—").toUpperCase()}</Badge>
                        {t.payment_status === "paid" && <Badge variant="outline" className="border-emerald-400/50 text-emerald-300 text-[10px] uppercase" data-testid={`trip-paid-${t.id}`}>PAID</Badge>}
                        {t.booked_by_dispatcher && <Badge variant="outline" className="border-yellow-400/40 text-yellow-200 text-[10px] uppercase" data-testid={`trip-bookedby-${t.id}`}>Booked by {t.booked_by_dispatcher}</Badge>}
                        {t.dispatch_confirmed && <Badge variant="outline" className="border-emerald-400/50 text-emerald-300 text-[10px] uppercase" data-testid={`trip-confirmed-${t.id}`} title={`Dispatch-confirmed${t.dispatch_confirmed_by ? ` by ${t.dispatch_confirmed_by}` : ""}${t.dispatch_confirmed_at ? ` · ${new Date(t.dispatch_confirmed_at).toLocaleString()}` : ""}`}>✓ Dispatch-confirmed by Ashley</Badge>}
                        {t.is_test && <Badge variant="outline" className="border-purple-400/40 text-purple-300 text-[10px] uppercase">TEST</Badge>}
                      </div>
                      <div className="text-sm font-semibold mt-2 truncate">{t.driver_name || t.driver_email || "Driver"} · {t.origin && t.destination ? `${t.origin} → ${t.destination}` : `${t.miles || 0} mi`}</div>
                      <div className="text-[11px] text-zinc-500 mt-1">{new Date(t.created_at).toLocaleString()} · {t.passengers || 1} pax · customer: {t.customer_name || "—"}</div>
                      {t.dispatch_notes && <div className="text-[11px] text-yellow-300/80 mt-1 truncate" data-testid={`trip-dispatch-notes-${t.id}`}>📝 {t.dispatch_notes}</div>}
                    </div>
                    <div className="text-right flex-shrink-0">
                      <div className="meter-display text-lg font-bold text-amber-400">{fmtUsd(t.total)}</div>
                      <div className="flex gap-1.5 mt-2">
                        <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); setEditingTripId(t.id); }} className="text-amber-300 text-xs h-7" data-testid={`day-trip-edit-${t.id}`} data-no-row-open>Edit</Button>
                        <Button size="sm" variant="ghost" onClick={() => exportTripPDF(t)} className="text-amber-400 text-xs h-7" data-testid={`day-trip-pdf-${t.id}`}><Download className="h-3 w-3 mr-1"/>PDF</Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}

          {/* v2.7.5 Phase 3 · Pagination controls */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-1 pt-2" data-testid="rides-pagination">
              <Button size="sm" variant="outline" disabled={safePage === 0} onClick={() => setPageIdx(safePage - 1)} className="bg-zinc-950 border-zinc-700 h-7 text-[11px]" data-testid="rides-page-prev">Prev</Button>
              {Array.from({ length: totalPages }).map((_, i) => {
                // Show up to 5 numbered + ellipsis logic.
                const showAll = totalPages <= 7;
                const nearStart = i < 4 && safePage <= 3;
                const nearEnd = i >= totalPages - 4 && safePage >= totalPages - 4;
                const isCurrent = i === safePage;
                const isEdge = i === 0 || i === totalPages - 1;
                if (showAll || nearStart || nearEnd || isCurrent || isEdge) {
                  return (
                    <Button
                      key={`page-${i}`}
                      size="sm"
                      variant={isCurrent ? "default" : "outline"}
                      onClick={() => setPageIdx(i)}
                      className={isCurrent
                        ? "bg-amber-400 text-zinc-950 hover:bg-amber-300 h-7 text-[11px] font-bold"
                        : "bg-zinc-950 border-zinc-700 h-7 text-[11px]"}
                      data-testid={`rides-page-${i + 1}`}
                    >
                      {i + 1}
                    </Button>
                  );
                }
                // Dots only once per side.
                if ((i === 4 && safePage > 3) || (i === totalPages - 5 && safePage < totalPages - 4)) {
                  return <span key={`dots-${i}`} className="text-zinc-600 px-1">…</span>;
                }
                return null;
              })}
              <Button size="sm" variant="outline" disabled={safePage >= totalPages - 1} onClick={() => setPageIdx(safePage + 1)} className="bg-zinc-950 border-zinc-700 h-7 text-[11px]" data-testid="rides-page-next">Next</Button>
            </div>
          )}
        </div>
      </Card>

      <Card className="bg-zinc-900 border-zinc-800 p-6" data-testid="driver-trips-card">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
          <h3 className="font-bold flex items-center gap-2"><FileText className="h-4 w-4 text-yellow-400"/>Driver Trips · table view ({trips.length})</h3>
          <div className="flex items-center gap-2 flex-wrap">
            <Badge variant="outline" className="border-amber-400/40 text-amber-400" data-testid="trips-selected">{tripSelected.size} selected</Badge>
            <Button size="sm" onClick={() => exportTripsCsv(false)} variant="outline" className="bg-zinc-950 border-zinc-700 h-8 text-xs" data-testid="trips-export-all"><Download className="h-3 w-3 mr-1"/>Export ALL CSV</Button>
            <Button size="sm" onClick={() => exportTripsCsv(true)} disabled={tripSelected.size === 0} variant="outline" className="bg-zinc-950 border-amber-400/40 text-amber-400 h-8 text-xs" data-testid="trips-export-selected"><Download className="h-3 w-3 mr-1"/>Export Selected</Button>
            <Button size="sm" onClick={deleteSelectedTrips} disabled={busy || tripSelected.size === 0} variant="outline" className="bg-red-500/10 border-red-500/40 text-red-300 h-8 text-xs" data-testid="trips-delete-selected"><Trash2 className="h-3 w-3 mr-1"/>Delete Selected</Button>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-[10px] uppercase tracking-wider text-zinc-500 border-b border-zinc-800">
              <tr>
                <th className="p-3 w-10 text-center"><input type="checkbox" data-testid="trips-select-all" checked={trips.length > 0 && tripSelected.size === trips.length} onChange={toggleAllTrips} className="accent-amber-400"/></th>
                <th className="text-left p-3">When</th>
                <th className="text-left p-3">Trip ID</th>
                <th className="text-left p-3">Driver</th>
                <th className="text-left p-3">Mode</th>
                <th className="text-left p-3">Route</th>
                <th className="text-right p-3">Fare</th>
                <th className="text-right p-3">Export</th>
              </tr>
            </thead>
            <tbody>
              {trips.map(t => (
                <tr
                  key={t.id}
                  className={`border-b border-zinc-800/80 cursor-pointer hover:bg-zinc-800/50 transition-colors ${tripSelected.has(t.id) ? "bg-amber-400/[0.06]" : tripTier(t.total)}`}
                  data-testid={`adm-trip-${t.id}`}
                  onClick={(e) => {
                    if (e.target.closest && e.target.closest("input, button, [data-no-row-open]")) return;
                    setViewing(t);
                  }}
                  title="Click for full trip detail"
                >
                  <td className="p-3 text-center"><input type="checkbox" checked={tripSelected.has(t.id)} onChange={()=>toggleOneTrip(t.id)} className="accent-amber-400 cursor-pointer" data-testid={`trip-select-${t.id}`}/></td>
                  <td className="p-3 text-zinc-400 whitespace-nowrap">{new Date(t.created_at).toLocaleString()}</td>
                  <td className="p-3 font-mono text-amber-400 text-xs whitespace-nowrap">{t.id?.slice(0, 8)}</td>
                  <td className="p-3 font-semibold whitespace-nowrap">{t.driver_name || t.driver_email}</td>
                  <td className="p-3"><Badge variant="outline" className="border-amber-400/30 text-amber-400 uppercase text-[10px]">{t.mode}</Badge></td>
                  <td className="p-3 text-zinc-400 max-w-md truncate">{t.origin && t.destination ? `${t.origin} → ${t.destination}` : `${t.miles}mi`}</td>
                  <td className="p-3 text-right meter-display font-bold text-amber-400 whitespace-nowrap">{fmtUsd(t.total)}</td>
                  <td className="p-3 text-right whitespace-nowrap">
                    <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); setEditingTripId(t.id); }} className="text-amber-300 text-[10px] h-7 px-2 mr-1" data-testid={`trip-edit-${t.id}`} data-no-row-open>Edit</Button>
                    <Button size="sm" variant="ghost" onClick={() => exportTripPDF(t)} className="text-amber-400 text-[10px] h-7 px-2" data-testid={`trip-pdf-${t.id}`}><Download className="h-3 w-3 mr-1"/>PDF</Button>
                  </td>
                </tr>
              ))}
              {trips.length === 0 && <tr><td colSpan={8} className="text-center p-8 text-zinc-500">No driver trips yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </Card>

      {/* v2.6.9 batch X · click-to-view detail dialog for any ride OR trip row */}
      <TripDetailDialog trip={viewing} onClose={() => setViewing(null)}/>
      {/* v2.7.0 batch DC · universal Edit Trip dialog (edit / override / receipt / audit) */}
      <EditTripDialog tripId={editingTripId} open={!!editingTripId} onClose={() => setEditingTripId(null)} onSaved={reload}/>
      {/* v2.7.65+E205 · Historical trip-total repair */}
      <RepairTotalsDialog open={repairOpen} onClose={() => setRepairOpen(false)} onApplied={reload}/>
      <RestoreCompletedRidesDialog open={restoreOpen} onClose={() => setRestoreOpen(false)} onApplied={reload}/>
    </div>
  );
}

function RefundButton({ ride, testid }) {
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState(ride.total || 0);
  const [reason, setReason] = useState("Overcharge");
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);
  const canRefund = !!ride.square_payment_id && ride.payment_status === "captured";

  const submit = async () => {
    if (!pin || pin.length < 4) { toast.error("PIN required to authorize refund"); return; }
    if (!amount || amount <= 0) { toast.error("Enter a positive amount"); return; }
    setBusy(true);
    try {
      const { data } = await adminApi.post(`/admin/rides/${ride.id}/refund`, {
        amount_cents: Math.round(parseFloat(amount) * 100),
        reason,
        pin,
      });
      toast.success(`Refunded ${fmtUsd(data.refund_amount_cents/100)}`);
      setOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Refund failed");
    } finally { setBusy(false); }
  };

  if (!canRefund) return null;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="ghost" className="text-yellow-400 hover:text-yellow-300 text-xs h-7" data-testid={testid}>
          <RotateCw className="h-3 w-3 mr-1"/>Refund
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-zinc-900 border-yellow-400/40 text-zinc-100">
        <DialogHeader><DialogTitle className="text-yellow-400">Refund · {ride.waybill_no}</DialogTitle></DialogHeader>
        <p className="text-xs text-zinc-400">Original charge: <span className="text-amber-400 meter-display">{fmtUsd(ride.total)}</span> · payment id <span className="font-mono text-zinc-500">{ride.square_payment_id}</span></p>
        <div className="space-y-2 mt-2">
          <div>
            <Label className="text-[10px] uppercase text-zinc-400">Amount $</Label>
            <Input data-testid="refund-amount" type="number" step="0.01" min="0.01" max={ride.total} value={amount} onChange={e=>setAmount(e.target.value)} className="bg-zinc-950 border-zinc-700 h-10 mt-1 meter-display"/>
          </div>
          <div>
            <Label className="text-[10px] uppercase text-zinc-400">Reason</Label>
            <select data-testid="refund-reason" value={reason} onChange={e=>setReason(e.target.value)} className="w-full bg-zinc-950 border border-zinc-700 text-zinc-100 rounded h-10 px-2 mt-1 text-sm">
              <option>Overcharge</option>
              <option>Dissatisfied</option>
              <option>Fraudulent Charge</option>
              <option>Full Refund</option>
            </select>
          </div>
          <div>
            <Label className="text-[10px] uppercase text-zinc-400">Operating Partner / Dispatcher PIN</Label>
            <Input data-testid="refund-pin" type="password" inputMode="numeric" value={pin} onChange={e=>setPin(e.target.value.replace(/[^0-9]/g,""))} maxLength={8} className="bg-zinc-950 border-zinc-700 h-10 mt-1 font-mono"/>
          </div>
        </div>
        <Button data-testid="refund-submit" onClick={submit} disabled={busy} className="w-full h-11 bg-yellow-400 hover:bg-yellow-300 text-zinc-950 font-bold">
          {busy ? <Loader2 className="h-4 w-4 animate-spin"/> : `REFUND ${fmtUsd(parseFloat(amount) || 0)}`}
        </Button>
      </DialogContent>
    </Dialog>
  );
}

function PaymentsTab({ onSubsToggle }) {
  return (
    <div className="space-y-4">
      {/* v2.6.7 batch I · Rider Subscription editor lives at the top of the
          Payments tab so admins can adjust plan pricing/credits/descriptions
          alongside the rest of the billing config. */}
      <RiderSubscriptionsEditor/>
      <PaymentsPanel onSubsToggle={onSubsToggle}/>
    </div>
  );
}

function PaymentsPanel({ onSubsToggle }) {
  const [cfg, setCfg] = useState(null);
  const [squareForm, setSquareForm] = useState({ application_id: "", access_token: "", location_id: "", environment: "production", enabled: true, capture_method: "manual", maintenance_application_id: "", maintenance_access_token: "", maintenance_location_id: "" });
  const [cashappForm, setCashappForm] = useState({ cashtag: "", client_id: "", enabled: false, via_square: true });
  const [dropboxForm, setDropboxForm] = useState({ app_key: "", app_secret: "", refresh_token: "", folder: "/buller-recordings", enabled: false });
  const [twilioForm, setTwilioForm] = useState({ account_sid: "", auth_token: "", from_number: "", enabled: false });
  const [squareUnlocked, setSquareUnlocked] = useState(false);
  const [passcode, setPasscode] = useState("");
  const [verifyResult, setVerifyResult] = useState(null);
  const [verifying, setVerifying] = useState(false);

  const unlockSquare = async () => {
    try { await adminApi.post("/admin/square-unlock", { passcode }); setSquareUnlocked(true); toast.success("Square unlocked"); }
    catch { toast.error("Wrong passcode"); }
  };

  useEffect(() => {
    adminApi.get("/admin/payment-config").then(r => {
      setCfg(r.data);
      setSquareForm(s => ({ ...s,
        application_id: r.data.square?.application_id || "", location_id: r.data.square?.location_id || "",
        environment: r.data.square?.environment || "production", enabled: r.data.square?.enabled !== false,
        capture_method: r.data.square?.capture_method || "manual",
        maintenance_application_id: r.data.square?.maintenance_application_id || "",
        maintenance_location_id: r.data.square?.maintenance_location_id || "",
      }));
      setCashappForm(s => ({ ...s,
        cashtag: r.data.cashapp?.cashtag || "",
        enabled: r.data.cashapp?.enabled === true,
        via_square: r.data.cashapp?.via_square !== false,
      }));
      setDropboxForm(s => ({ ...s, app_key: r.data.dropbox?.app_key || "", folder: r.data.dropbox?.folder || "/buller-recordings", enabled: r.data.dropbox?.enabled === true }));
      setTwilioForm(s => ({ ...s,
        account_sid: r.data.twilio?.account_sid || "",
        from_number: r.data.twilio?.from_number || "",
        enabled: r.data.twilio?.enabled === true,
      }));
    }).catch(()=>{});
  }, []);

  const verifySquare = async () => {
    setVerifying(true); setVerifyResult(null);
    try {
      const { data } = await adminApi.post("/admin/square/verify", {});
      setVerifyResult(data);
      if (data.valid) toast.success(`Square OK — ${data.locations?.length || 0} location(s)`);
      else toast.error(data.error || "Verification failed");
    } catch (e) { toast.error(e?.response?.data?.detail || "Verify failed"); }
    finally { setVerifying(false); }
  };
  const saveSquare = async () => {
    try {
      const payload = { square: { ...squareForm } };
      if (!squareForm.access_token) delete payload.square.access_token;
      if (!squareForm.maintenance_access_token) delete payload.square.maintenance_access_token;
      await adminApi.put("/admin/payment-config", payload);
      toast.success("Square config saved · effective immediately");
      setSquareForm(s => ({ ...s, access_token: "", maintenance_access_token: "" }));
    } catch { toast.error("Save failed"); }
  };
  const saveCashapp = async () => {
    try {
      const payload = { cashapp: { cashtag: cashappForm.cashtag, enabled: cashappForm.enabled, via_square: cashappForm.via_square } };
      if (cashappForm.client_id) payload.cashapp.client_id = cashappForm.client_id;
      await adminApi.put("/admin/payment-config", payload);
      toast.success("Cash App Pay config saved");
      setCashappForm(s => ({ ...s, client_id: "" }));
    } catch { toast.error("Save failed"); }
  };
  const saveDropbox = async () => {
    try {
      const payload = { dropbox: { app_key: dropboxForm.app_key, folder: dropboxForm.folder, enabled: dropboxForm.enabled } };
      if (dropboxForm.app_secret) payload.dropbox.app_secret = dropboxForm.app_secret;
      if (dropboxForm.refresh_token) payload.dropbox.refresh_token = dropboxForm.refresh_token;
      await adminApi.put("/admin/payment-config", payload);
      toast.success("Dropbox config saved");
      setDropboxForm(s => ({ ...s, app_secret: "", refresh_token: "" }));
    } catch { toast.error("Save failed"); }
  };

  const saveTwilio = async () => {
    try {
      const payload = { twilio: { from_number: twilioForm.from_number, enabled: twilioForm.enabled } };
      if (twilioForm.account_sid) payload.twilio.account_sid = twilioForm.account_sid;
      if (twilioForm.auth_token) payload.twilio.auth_token = twilioForm.auth_token;
      await adminApi.put("/admin/payment-config", payload);
      toast.success("Twilio config saved · SMS system-wide will go through");
      setTwilioForm(s => ({ ...s, auth_token: "" }));
    } catch { toast.error("Save failed"); }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      {/* v2.6.8 batch P-4 · global Rider Subscriptions feature toggle.
          Pulled out into its own card at the top of Payments so dispatch can
          flip the entire feature off when needed. */}
      <SubscriptionsFeatureToggle onChange={onSubsToggle}/>
      <Card className="bg-zinc-900 border-amber-400/30 p-5 lg:col-span-2" data-testid="square-primary-banner">
        <div className="flex items-start gap-3 flex-wrap">
          <div className="text-green-400 text-2xl">✓</div>
          <div className="flex-1 min-w-0">
            <h3 className="font-bold text-amber-400">Square is the Sole Card Processor</h3>
            <p className="text-xs text-zinc-400 mt-0.5">Stripe has been removed. All credit-card payments — fares, tips, Cash App Pay, and the maintenance fee — now process through your Square account.</p>
          </div>
          <Button data-testid="verify-square-btn" onClick={verifySquare} disabled={verifying} className="bg-amber-400 hover:bg-amber-300 text-zinc-950 font-bold">{verifying ? "VERIFYING…" : "PING SQUARE"}</Button>
        </div>
        {verifyResult && verifyResult.valid && (
          <div className="mt-3 text-xs bg-green-500/10 border border-green-400/40 rounded p-3" data-testid="verify-result-ok">
            <div className="font-bold text-green-300">✓ Square OK · {verifyResult.environment?.toUpperCase()} · {verifyResult.locations?.length} location(s)</div>
            {verifyResult.locations?.map(l => (
              <div key={l.id} className="mt-1 text-zinc-300">
                • <span className="font-mono text-amber-400">{l.id}</span> — {l.name} ({(l.capabilities || []).join(", ")})
              </div>
            ))}
          </div>
        )}
        {verifyResult && !verifyResult.valid && (
          <div className="mt-3 text-xs bg-red-500/10 border border-red-400/40 rounded p-3 text-red-300" data-testid="verify-result-err">{verifyResult.error}</div>
        )}
      </Card>

      <Card className="bg-zinc-900 border-zinc-800 p-5 lg:col-span-2">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold">Square</h3>
          <Switch data-testid="square-enabled" checked={squareForm.enabled} onCheckedChange={v=>setSquareForm({...squareForm, enabled: v})}/>
        </div>
        {!squareUnlocked ? (
          <div className="space-y-3">
            <p className="text-xs text-zinc-400">Square configuration is restricted. Enter the admin PIN to view & edit.</p>
            <Input data-testid="square-passcode" type="password" value={passcode} onChange={e=>setPasscode(e.target.value)} placeholder="Admin PIN" className="bg-zinc-950 border-zinc-700 font-mono"/>
            <Button data-testid="square-unlock" onClick={unlockSquare} className="w-full bg-amber-400 hover:bg-amber-300 text-zinc-950 font-bold">UNLOCK SQUARE SETTINGS</Button>
          </div>
        ) : (
        <div className="space-y-3">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500">Primary Square Account (Ride Fares)</div>
          <div><Label className="text-xs">Application ID</Label><Input data-testid="square-appid" value={squareForm.application_id} onChange={e=>setSquareForm({...squareForm, application_id: e.target.value})} className="bg-zinc-950 border-zinc-700 mt-1 font-mono text-xs"/></div>
          <div><Label className="text-xs">Location ID</Label><Input data-testid="square-loc" value={squareForm.location_id} onChange={e=>setSquareForm({...squareForm, location_id: e.target.value})} className="bg-zinc-950 border-zinc-700 mt-1 font-mono text-xs"/></div>
          <div><Label className="text-xs">Access Token <span className="text-zinc-500">— current: {cfg?.square?.access_token || "•••"}</span></Label><Input data-testid="square-token" type="password" placeholder="Leave blank to keep current" value={squareForm.access_token} onChange={e=>setSquareForm({...squareForm, access_token: e.target.value})} className="bg-zinc-950 border-zinc-700 mt-1 font-mono text-xs"/></div>
          <div><Label className="text-xs">Environment</Label>
            <Select value={squareForm.environment} onValueChange={v=>setSquareForm({...squareForm, environment: v})}>
              <SelectTrigger data-testid="square-env" className="bg-zinc-950 border-zinc-700 mt-1"><SelectValue/></SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-700 text-zinc-100">
                <SelectItem value="sandbox">Sandbox</SelectItem>
                <SelectItem value="production">Production</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 pt-3 border-t border-zinc-800">Maintenance Fee Account ($1.55/ride routed separately)</div>
          <div><Label className="text-xs">Maintenance Application ID</Label><Input data-testid="sq-maint-appid" value={squareForm.maintenance_application_id} onChange={e=>setSquareForm({...squareForm, maintenance_application_id: e.target.value})} className="bg-zinc-950 border-zinc-700 mt-1 font-mono text-xs"/></div>
          <div><Label className="text-xs">Maintenance Location ID</Label><Input data-testid="sq-maint-loc" value={squareForm.maintenance_location_id} onChange={e=>setSquareForm({...squareForm, maintenance_location_id: e.target.value})} className="bg-zinc-950 border-zinc-700 mt-1 font-mono text-xs"/></div>
          <div><Label className="text-xs">Maintenance Access Token <span className="text-zinc-500">— current: {cfg?.square?.maintenance_access_token || "•••"}</span></Label><Input data-testid="sq-maint-token" type="password" placeholder="Leave blank to keep current" value={squareForm.maintenance_access_token} onChange={e=>setSquareForm({...squareForm, maintenance_access_token: e.target.value})} className="bg-zinc-950 border-zinc-700 mt-1 font-mono text-xs"/></div>
          <Button data-testid="save-square" onClick={saveSquare} className="w-full bg-amber-400 hover:bg-amber-300 text-zinc-950 font-bold">SAVE SQUARE</Button>
        </div>
        )}
      </Card>

      <Card className="bg-zinc-900 border-zinc-800 p-5 lg:col-span-2" data-testid="cashapp-config-card">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold flex items-center gap-2"><span className="text-green-400 font-extrabold tracking-tight">$</span>Cash App Pay</h3>
          <Switch data-testid="cashapp-enabled" checked={cashappForm.enabled} onCheckedChange={v=>setCashappForm({...cashappForm, enabled: v})}/>
        </div>
        <p className="text-xs text-zinc-400 mb-3">
          Riders can pay with Cash App Pay at checkout. Cash App Pay is processed through your Square account (Square supports Cash App Pay natively as a payment method); the rider's $cashtag is captured for manual fallback if Square isn't yet enabled.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">Business $cashtag</Label>
            <Input data-testid="cashapp-tag" value={cashappForm.cashtag} onChange={e=>setCashappForm({...cashappForm, cashtag: e.target.value})} placeholder="$BullerTaxi" className="bg-zinc-950 border-zinc-700 mt-1 font-mono text-xs"/>
            <p className="text-[10px] text-zinc-500 mt-1">Shown to riders for manual fallback if Square Cash App Pay isn't capturing.</p>
          </div>
          <div>
            <Label className="text-xs">Cash App for Business Client ID <span className="text-zinc-500">— current: {cfg?.cashapp?.client_id || "•••"}</span></Label>
            <Input data-testid="cashapp-client-id" type="password" placeholder="Leave blank to keep current" value={cashappForm.client_id} onChange={e=>setCashappForm({...cashappForm, client_id: e.target.value})} className="bg-zinc-950 border-zinc-700 mt-1 font-mono text-xs"/>
            <p className="text-[10px] text-zinc-500 mt-1">Optional — only needed if integrating Cash App for Business directly (vs through Square).</p>
          </div>
          <label className="flex items-center gap-2 text-xs text-zinc-300 cursor-pointer md:col-span-2">
            <Switch data-testid="cashapp-via-square" checked={cashappForm.via_square} onCheckedChange={v=>setCashappForm({...cashappForm, via_square: v})}/>
            Process Cash App Pay through Square (recommended)
          </label>
        </div>
        <Button data-testid="save-cashapp" onClick={saveCashapp} className="w-full md:w-64 mt-4 bg-amber-400 hover:bg-amber-300 text-zinc-950 font-bold">SAVE CASH APP PAY</Button>
      </Card>

      <Card className="bg-zinc-900 border-zinc-800 p-5 lg:col-span-2">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold">Dropbox (Audio + Video Recording Storage)</h3>
          <Switch data-testid="dropbox-enabled" checked={dropboxForm.enabled} onCheckedChange={v=>setDropboxForm({...dropboxForm, enabled: v})}/>
        </div>
        <p className="text-xs text-zinc-400 mb-3">Trip recordings (audio + video) will upload to this Dropbox folder when enabled. Get credentials at <a className="text-amber-400" href="https://www.dropbox.com/developers/apps" target="_blank" rel="noreferrer">dropbox.com/developers/apps</a>.</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div><Label className="text-xs">App Key</Label><Input data-testid="dropbox-key" value={dropboxForm.app_key} onChange={e=>setDropboxForm({...dropboxForm, app_key: e.target.value})} className="bg-zinc-950 border-zinc-700 mt-1 font-mono text-xs"/></div>
          <div><Label className="text-xs">Folder Path</Label><Input data-testid="dropbox-folder" value={dropboxForm.folder} onChange={e=>setDropboxForm({...dropboxForm, folder: e.target.value})} className="bg-zinc-950 border-zinc-700 mt-1 font-mono text-xs"/></div>
          <div><Label className="text-xs">App Secret <span className="text-zinc-500">— current: {cfg?.dropbox?.app_secret || "•••"}</span></Label><Input data-testid="dropbox-secret" type="password" placeholder="Leave blank to keep current" value={dropboxForm.app_secret} onChange={e=>setDropboxForm({...dropboxForm, app_secret: e.target.value})} className="bg-zinc-950 border-zinc-700 mt-1 font-mono text-xs"/></div>
          <div><Label className="text-xs">Refresh Token <span className="text-zinc-500">— current: {cfg?.dropbox?.refresh_token || "•••"}</span></Label><Input data-testid="dropbox-token" type="password" placeholder="Leave blank to keep current" value={dropboxForm.refresh_token} onChange={e=>setDropboxForm({...dropboxForm, refresh_token: e.target.value})} className="bg-zinc-950 border-zinc-700 mt-1 font-mono text-xs"/></div>
        </div>
        <Button data-testid="save-dropbox" onClick={saveDropbox} className="w-full md:w-64 mt-4 bg-amber-400 hover:bg-amber-300 text-zinc-950 font-bold">SAVE DROPBOX</Button>
      </Card>

      <Card className="bg-zinc-900 border-zinc-800 p-5 lg:col-span-2" data-testid="twilio-card">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="font-bold flex items-center gap-2"><AlertTriangle className="h-4 w-4 text-amber-400"/>Twilio · System-wide SMS</h3>
            <p className="text-[11px] text-zinc-500 mt-0.5">Powers every outbound text the system sends. Without these credentials messages are recorded to the SMS Outbox but NOT delivered.</p>
            <ul className="text-[10px] text-zinc-500 mt-2 space-y-0.5 list-disc list-inside" data-testid="twilio-events-list">
              <li><span className="text-emerald-300 font-semibold">Ride confirmation</span> — customer text on public quick-booking</li>
              <li><span className="text-emerald-300 font-semibold">Driver assigned</span> — customer text + driver text on dispatch assignment</li>
              <li><span className="text-emerald-300 font-semibold">HOS rest reminder</span> — driver text at 10-min-remaining warning</li>
              <li><span className="text-emerald-300 font-semibold">Driver grounded</span> — driver text on 12h/14h auto-lockout</li>
              <li><span className="text-red-300 font-semibold">Panic SOS</span> — fan-out text to every other operating partner</li>
              <li><span className="text-zinc-400">Auto-account credentials, dispatcher blasts, dispatch alerts</span></li>
            </ul>
          </div>
          <Switch data-testid="twilio-enabled" checked={twilioForm.enabled} onCheckedChange={v=>setTwilioForm({...twilioForm, enabled: v})}/>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div><Label className="text-xs">Account SID</Label><Input data-testid="twilio-sid" value={twilioForm.account_sid} onChange={e=>setTwilioForm({...twilioForm, account_sid: e.target.value})} placeholder="AC..." className="bg-zinc-950 border-zinc-700 mt-1 font-mono text-xs"/></div>
          <div><Label className="text-xs">Auth Token</Label><Input data-testid="twilio-token" type="password" value={twilioForm.auth_token} onChange={e=>setTwilioForm({...twilioForm, auth_token: e.target.value})} placeholder="(leave blank to keep)" className="bg-zinc-950 border-zinc-700 mt-1 font-mono text-xs"/></div>
          <div><Label className="text-xs">From Number</Label><Input data-testid="twilio-from" value={twilioForm.from_number} onChange={e=>setTwilioForm({...twilioForm, from_number: e.target.value})} placeholder="+15555550100" className="bg-zinc-950 border-zinc-700 mt-1 font-mono text-xs"/></div>
        </div>
        <Button data-testid="save-twilio" onClick={saveTwilio} className="w-full md:w-64 mt-4 bg-amber-400 hover:bg-amber-300 text-zinc-950 font-bold">SAVE TWILIO</Button>
      </Card>

      {/* v2.7.65+E169 · Twilio inbound STOP webhook · info + copy button.
          Configure this URL in Twilio Console → Phone Numbers → your number →
          Messaging · "A MESSAGE COMES IN" · HTTP POST. Replying STOP flips
          `sms_consent` to false on every matching rider/driver row. */}
      <div className="lg:col-span-2">
        <TwilioInboundWebhookCard/>
      </div>

      {/* v2.7.58+E40 · Storefront-wide Cash App + Venmo QR uploader shown
          on the marketing "Get a Fare in Seconds" home form. Uploaded QR
          images are stored as base64 data-URLs on payment_config.storefront
          and served publicly via GET /api/storefront/payment-qrs. */}
      <div className="lg:col-span-2">
        <StorefrontQRCard/>
      </div>
    </div>
  );
}

// v2.6.8 batch P-4 · global enable/disable for the Rider Subscriptions
// feature. PIN-gated via /admin/pricing (admin/supervisor only). When OFF:
//   - rider Dashboard hides the Subscriptions tab
//   - admin Console hides the Subscriptions top-level tab + the
//     RiderSubscriptionsEditor card on the Payments page
//   - rider POST /subscriptions/purchase → 403
//   - existing active subscriptions are NOT cancelled; this is a feature
//     gate only.
function SubscriptionsFeatureToggle({ onChange }) {
  const [enabled, setEnabled] = useState(null); // null = loading
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    adminApi.get("/subscriptions/plans")
      .then(r => setEnabled(r.data?.enabled !== false))
      .catch((e) => { console.warn("subscriptions feature flag fetch failed", e?.response?.status); setEnabled(true); });
  }, []);

  const toggle = async () => {
    const next = !enabled;
    setBusy(true);
    try {
      await adminApi.put("/admin/pricing", { subscriptions_enabled: next });
      setEnabled(next);
      // v2.6.8 batch P-4 · let the parent (AdministrationGroup) refresh its
      // own copy of the flag so the Subscriptions top-level tab hides/shows
      // immediately without a page reload.
      if (typeof onChange === "function") onChange();
      toast.success(next ? "Rider Subscriptions ENABLED · feature is live for riders" : "Rider Subscriptions DISABLED · hidden from riders & purchase blocked");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    } finally { setBusy(false); }
  };

  return (
    <Card className="bg-zinc-900 border-amber-400/40 p-5 lg:col-span-2" data-testid="subscriptions-feature-toggle">
      <div className="flex items-start gap-3 flex-wrap">
        <Sparkles className={`h-7 w-7 flex-shrink-0 ${enabled ? "text-amber-400" : "text-zinc-600"}`}/>
        <div className="flex-1 min-w-0">
          <h3 className="font-bold text-amber-400 flex items-center gap-2">
            Rider Subscriptions
            {enabled === null ? null : (
              <span className={`text-[9px] uppercase tracking-wider font-extrabold px-2 py-0.5 rounded-full ${enabled ? "bg-green-500/20 text-green-300" : "bg-zinc-700 text-zinc-300"}`} data-testid="subscriptions-feature-badge">
                {enabled ? "Enabled" : "Disabled"}
              </span>
            )}
          </h3>
          <p className="text-xs text-zinc-400 mt-1 leading-snug">
            Master switch for the entire Rider Subscriptions feature (Basic / Economy / Premier / Elite plans). Disabling hides it from the rider dashboard + admin console and blocks new purchases. Existing active subscriptions continue to honor their remaining credits — this is a feature gate, not a cancellation.
          </p>
        </div>
        <div className="flex items-center gap-3 flex-shrink-0">
          {enabled === null ? (
            <Loader2 className="h-5 w-5 animate-spin text-zinc-500"/>
          ) : (
            <Switch
              checked={enabled}
              disabled={busy}
              onCheckedChange={toggle}
              data-testid="subscriptions-feature-switch"
            />
          )}
          <span className="text-[10px] uppercase tracking-wider text-zinc-500 font-bold">{busy ? "Saving…" : (enabled ? "ON" : "OFF")}</span>
        </div>
      </div>
    </Card>
  );
}

const LEGAL_KINDS = [
  { kind: "privacy", label: "Privacy Policy" },
  { kind: "terms", label: "Terms of Service" },
  { kind: "refund", label: "Refund Policy" },
  { kind: "faq", label: "FAQ" },
];

function LegalTab() {
  return <LegalPanel/>;
}

// v2.6.8 batch M · BISON AI BRANDING — single card with 5 upload slots.
// Saves to /api/admin/branding (PIN-gated). Public endpoint /api/branding lets
// the login screen + global header read the active logos without auth.
function BisonBrandingCard() {
  const [branding, setBranding] = useState({});
  const [busy, setBusy] = useState(null);

  const reload = useCallback(async () => {
    try {
      const { data } = await adminApi.get("/branding");
      setBranding(data || {});
    } catch (e) {
      // Public endpoint — quiet on transient network failure but log for debugging.
      console.warn("BISON branding fetch failed", e?.response?.status || e?.message);
    }
  }, []);
  useEffect(() => { reload(); }, [reload]);

  const uploadOne = async (slot, file) => {
    const pin = window.prompt("Enter your Operating Partner / admin PIN to save this logo:");
    if (!pin) return;
    setBusy(slot);
    try {
      // 1. Stream to GridFS via /uploads/driver-doc (same bucket — admin auth required)
      const fd = new FormData();
      fd.append("file", file);
      const upload = await adminApi.post("/uploads/driver-doc", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const base = process.env.REACT_APP_BACKEND_URL || "";
      const full_url = base + upload.data.url;
      // 2. PUT /admin/branding with the new url
      await adminApi.put("/admin/branding", { pin, [slot]: full_url });
      setBranding(b => ({ ...b, [slot]: full_url }));
      toast.success(`${BRANDING_LABELS[slot]} saved`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(null); }
  };

  const clearOne = async (slot) => {
    const pin = window.prompt(`Clear ${BRANDING_LABELS[slot]}? Enter your Operating Partner / admin PIN:`);
    if (!pin) return;
    try {
      await adminApi.put("/admin/branding", { pin, [slot]: "" });
      setBranding(b => ({ ...b, [slot]: null }));
      toast.success("Cleared");
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  return (
    <Card className="bg-zinc-900 border-amber-400/40 p-5" data-testid="bison-branding-card">
      <h3 className="font-bold mb-1 flex items-center gap-2"><Sparkles className="h-4 w-4 text-amber-400"/>BISON AI BRANDING</h3>
      <p className="text-[11px] text-zinc-500 mb-4">Upload + manage the logos displayed across the app. PIN-gated · changes go live immediately.</p>
      <div className="space-y-3">
        <BrandingSlot slot="login_logo_url" label="Login Page Logo" current={branding.login_logo_url} busy={busy === "login_logo_url"} onUpload={uploadOne} onClear={clearOne}/>
        <BrandingSlot slot="site_logo_url"  label="Site / Header Logo (replaces buller-logo-header.png)" current={branding.site_logo_url} busy={busy === "site_logo_url"} onUpload={uploadOne} onClear={clearOne}/>
        <div className="border-t border-zinc-800 pt-3 mt-3">
          <div className="text-[10px] uppercase tracking-wider text-amber-400 font-bold mb-2">Dev License Logos</div>
          <p className="text-[11px] text-zinc-500 mb-3">All three logos must be the same size. One sits at the top of the Dev License document, two side-by-side at the bottom.</p>
          <BrandingSlot slot="devlicense_logo_top_url" label="Dev License Top" current={branding.devlicense_logo_top_url} busy={busy === "devlicense_logo_top_url"} onUpload={uploadOne} onClear={clearOne}/>
          <BrandingSlot slot="devlicense_logo_bottom_left_url" label="Dev License Bottom-Left" current={branding.devlicense_logo_bottom_left_url} busy={busy === "devlicense_logo_bottom_left_url"} onUpload={uploadOne} onClear={clearOne}/>
          <BrandingSlot slot="devlicense_logo_bottom_right_url" label="Dev License Bottom-Right" current={branding.devlicense_logo_bottom_right_url} busy={busy === "devlicense_logo_bottom_right_url"} onUpload={uploadOne} onClear={clearOne}/>
        </div>
        {branding.updated_at && <div className="text-[10px] text-zinc-500 mt-2">Last updated {new Date(branding.updated_at).toLocaleString()}</div>}
      </div>
    </Card>
  );
}

const BRANDING_LABELS = {
  login_logo_url: "Login Page Logo",
  site_logo_url: "Site Logo",
  devlicense_logo_top_url: "Dev License Top Logo",
  devlicense_logo_bottom_left_url: "Dev License Bottom Left Logo",
  devlicense_logo_bottom_right_url: "Dev License Bottom Right Logo",
};

function BrandingSlot({ slot, label, current, busy, onUpload, onClear }) {
  const ref = useRef(null);
  return (
    <div className="flex items-center gap-3 py-1.5">
      <div className="w-16 h-16 rounded border border-zinc-800 bg-zinc-950 flex items-center justify-center overflow-hidden flex-shrink-0">
        {current ? <img src={current} alt={label} className="max-w-full max-h-full object-contain" data-testid={`branding-img-${slot}`}/> : <ImageIcon className="h-6 w-6 text-zinc-700"/>}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-xs font-semibold">{label}</div>
        <div className="text-[10px] text-zinc-500 truncate">{current ? "Active" : "Not set"}</div>
      </div>
      <Button size="sm" variant="outline" onClick={() => ref.current?.click()} disabled={!!busy} className="bg-zinc-950 border-zinc-700" data-testid={`branding-pick-${slot}`}>
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin"/> : <><FileText className="h-3.5 w-3.5 mr-1"/>{current ? "Replace" : "Upload"}</>}
      </Button>
      {current && <Button size="sm" variant="ghost" onClick={() => onClear(slot)} className="text-red-400 text-[10px]" data-testid={`branding-clear-${slot}`}><Trash2 className="h-3.5 w-3.5"/></Button>}
      <input
        ref={ref} type="file" accept="image/*" className="hidden"
        onChange={(e) => { const f = e.target.files?.[0]; if (f) onUpload(slot, f); e.target.value = ""; }}
        data-testid={`branding-input-${slot}`}
      />
    </div>
  );
}

function LegalPanel() {
  const [active, setActive] = useState("privacy");
  const [docs, setDocs] = useState({});
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState({ title: "", content: "" });
  const [contact, setContact] = useState({ contact_phone: "", contact_email: "" });
  const [contactBusy, setContactBusy] = useState(false);

  const reload = async () => {
    try { const { data } = await adminApi.get("/legal"); setDocs(data); } catch (err) { console.warn("legal docs reload failed", err?.response?.status); }
    try { const { data } = await adminApi.get("/dispatch/contact"); setContact(data); } catch (err) { console.warn("dispatch contact reload failed", err?.response?.status); }
  };
  useEffect(() => { reload(); }, []);
  useEffect(() => {
    if (docs[active]) setDraft({ title: docs[active].title || "", content: docs[active].content || "" });
  }, [active, docs]);

  const save = async () => {
    setBusy(true);
    try {
      await adminApi.put(`/admin/legal/${active}`, { title: draft.title, content: draft.content });
      toast.success(`${LEGAL_KINDS.find(k => k.kind === active)?.label} saved · live now at /legal/${active}`);
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  const reset = async () => {
    if (!window.confirm(`Reset ${active} to the default text? Your edits will be discarded.`)) return;
    try {
      const { data } = await adminApi.post(`/admin/legal/${active}/reset`);
      toast.success("Reset to default");
      setDraft({ title: data.title, content: data.content });
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Reset failed"); }
  };

  const saveContact = async () => {
    setContactBusy(true);
    try {
      await adminApi.put("/admin/dispatch/contact", contact);
      toast.success("Login page contact updated");
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setContactBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="legal-tab">
      <BackendBrandingCard/>
      <BisonBrandingCard/>
      <Card className="bg-zinc-900 border-zinc-800 p-5" data-testid="legal-contact-card">
        <h3 className="font-bold mb-1">Login Page · Public Contact</h3>
        <p className="text-[11px] text-zinc-500 mb-4">These show on the login screen footer. The email becomes a clickable mailto link, the phone a clickable tel link.</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-zinc-400">Phone</Label>
            <Input data-testid="legal-contact-phone" value={contact.contact_phone || ""} onChange={e=>setContact({...contact, contact_phone: e.target.value})} placeholder="(580) 219-4687" className="bg-zinc-950 border-zinc-700 h-10 mt-1 meter-display"/>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-zinc-400">Email</Label>
            <Input data-testid="legal-contact-email" value={contact.contact_email || ""} onChange={e=>setContact({...contact, contact_email: e.target.value})} placeholder="support@bullertaxi.com" className="bg-zinc-950 border-zinc-700 h-10 mt-1"/>
          </div>
        </div>
        <Button data-testid="legal-contact-save" onClick={saveContact} disabled={contactBusy} className="mt-3 bg-amber-400 hover:bg-amber-300 text-zinc-950 font-bold">{contactBusy ? <Loader2 className="h-4 w-4 animate-spin"/> : "SAVE CONTACT"}</Button>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        <Card className="bg-zinc-900 border-zinc-800 p-4 lg:col-span-1">
          <h3 className="text-xs uppercase tracking-widest text-zinc-400 font-bold mb-3">Documents</h3>
          <div className="space-y-1">
            {LEGAL_KINDS.map(k => (
              <button
                key={k.kind}
                onClick={() => setActive(k.kind)}
                data-testid={`legal-pick-${k.kind}`}
                className={`w-full text-left px-3 py-2 rounded text-sm font-semibold border ${active === k.kind ? "bg-amber-400 text-zinc-950 border-amber-400" : "bg-zinc-950 border-zinc-800 text-zinc-300 hover:border-amber-400/40"}`}
              >
                {k.label}
                {docs[k.kind]?.updated_at && <div className={`text-[10px] mt-0.5 ${active === k.kind ? "text-zinc-700" : "text-zinc-500"}`}>edited · {new Date(docs[k.kind].updated_at).toLocaleDateString()}</div>}
              </button>
            ))}
          </div>
          <Link to={`/legal/${active}`} target="_blank" rel="noreferrer" className="mt-4 block text-center text-[11px] uppercase tracking-wider text-amber-400 hover:text-amber-300" data-testid="legal-view-public">View Public Page ↗</Link>
        </Card>
      <Card className="bg-zinc-900 border-zinc-800 p-5 lg:col-span-3" data-testid={`legal-editor-${active}`}>
        <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
          <h3 className="font-bold text-lg flex items-center gap-2"><FileText className="h-4 w-4 text-amber-400"/>{LEGAL_KINDS.find(k => k.kind === active)?.label}</h3>
          <div className="text-[10px] uppercase tracking-widest text-zinc-500">/legal/{active}</div>
        </div>
        <Label className="text-xs">Document Title</Label>
        <Input data-testid="legal-title-input" value={draft.title} onChange={e=>setDraft({...draft, title: e.target.value})} className="bg-zinc-950 border-zinc-700 mt-1 mb-3"/>
        <Label className="text-xs">Content (plain text · line breaks preserved)</Label>
        <textarea
          data-testid="legal-content-input"
          value={draft.content}
          onChange={e=>setDraft({...draft, content: e.target.value})}
          className="w-full mt-1 bg-zinc-950 border border-zinc-700 rounded p-3 text-sm text-zinc-200 font-mono leading-relaxed focus:border-amber-400 focus:ring-1 focus:ring-amber-400 outline-none"
          rows={20}
        />
        <div className="flex flex-wrap gap-2 mt-4">
          <Button data-testid="legal-save-btn" onClick={save} disabled={busy} className="bg-amber-400 hover:bg-amber-300 text-zinc-950 font-bold flex-shrink-0">{busy ? "SAVING…" : "SAVE & PUBLISH"}</Button>
          <Button data-testid="legal-reset-btn" onClick={reset} variant="outline" className="bg-zinc-950 border-zinc-700 text-zinc-300">Reset to Default</Button>
          <span className="text-[11px] text-zinc-500 ml-auto self-center">Saved changes are live immediately at <span className="text-amber-400">/legal/{active}</span></span>
        </div>
      </Card>
      </div>
    </div>
  );
}


function SupportTab({ adminRole }) {
  const [unlocked, setUnlocked] = useState(false);
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);
  const [rides, setRides] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [audit, setAudit] = useState([]);
  const [filter, setFilter] = useState("");

  const isAuthorized = adminRole === "admin" || adminRole === "supervisor" || adminRole === "operating_partner" || adminRole === "developer";

  const reload = async () => {
    try {
      const [r1, r2] = await Promise.all([
        adminApi.get("/admin/support/test-rides"),
        adminApi.get("/admin/support/audit"),
      ]);
      setRides(r1.data); setAudit(r2.data);
    } catch (e) { toast.error("Failed to load support data"); }
  };

  const unlock = async () => {
    setBusy(true);
    try { await adminApi.post("/admin/support/unlock", { passcode: pin }); setUnlocked(true); toast.success("Support unlocked"); reload(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Wrong PIN"); setPin(""); }
    finally { setBusy(false); }
  };

  const toggleAll = () => {
    if (selected.size === filteredRides.length) setSelected(new Set());
    else setSelected(new Set(filteredRides.map(r => r.id)));
  };
  const toggleOne = (id) => {
    const s = new Set(selected);
    s.has(id) ? s.delete(id) : s.add(id);
    setSelected(s);
  };

  const clearSelected = async (zero = false) => {
    if (selected.size === 0) return toast.error("Select at least one ride");
    const verb = zero ? "ZERO OUT (keep audit trail)" : "PERMANENTLY DELETE";
    if (!window.confirm(`${verb} ${selected.size} ride${selected.size === 1 ? "" : "s"}? This cannot be undone.`)) return;
    setBusy(true);
    try {
      const { data } = await adminApi.post("/admin/support/clear-test-rides", { ride_ids: Array.from(selected), also_zero_totals: zero });
      toast.success(`${data.action.toUpperCase()} ${data.affected} ride${data.affected === 1 ? "" : "s"}`);
      setSelected(new Set()); reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Cleanup failed"); }
    finally { setBusy(false); }
  };

  const clearAll = async (zero = false) => {
    const verb = zero ? "ZERO OUT (keep audit trail)" : "PERMANENTLY DELETE";
    if (!window.confirm(`${verb} ALL ${rides.length} test rides? This cannot be undone.`)) return;
    setBusy(true);
    try {
      const { data } = await adminApi.post("/admin/support/clear-test-rides", { also_zero_totals: zero });
      toast.success(`${data.action.toUpperCase()} ${data.affected} ride${data.affected === 1 ? "" : "s"}`);
      setSelected(new Set()); reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Cleanup failed"); }
    finally { setBusy(false); }
  };

  const filteredRides = rides.filter(r => {
    if (!filter) return true;
    const f = filter.toLowerCase();
    return [r.waybill_no, r.customer_name, r.business_name, r.reservation_name, r.origin, r.destination, r.payment_status]
      .filter(Boolean).some(s => s.toLowerCase().includes(f));
  });

  if (!isAuthorized) {
    return (
      <Card className="bg-zinc-900 border-red-500/40 p-8 text-center" data-testid="support-locked">
        <Lock className="h-10 w-10 text-red-400 mx-auto mb-3"/>
        <h3 className="text-lg font-bold text-red-400">SUPPORT — Admin / Operating Partner Only</h3>
        <p className="text-sm text-zinc-400 mt-2">You need an Admin or Operating Partner role to access this section.</p>
      </Card>
    );
  }

  if (!unlocked) {
    return (
      <Card className="bg-zinc-900 border-red-500/40 p-8 text-center max-w-md mx-auto" data-testid="support-pin-gate">
        <Lock className="h-10 w-10 text-red-400 mx-auto mb-3"/>
        <h3 className="text-lg font-bold">SUPPORT</h3>
        <p className="text-xs text-zinc-400 mt-1 mb-5">Enter the Support passcode to unlock test-data cleanup, the secondary Square maintenance processor, and other internal tools.</p>
        <Input
          data-testid="support-pin-input"
          type="password"
          inputMode="numeric"
          maxLength={6}
          value={pin}
          onChange={e => setPin(e.target.value.replace(/\D/g, ""))}
          onKeyDown={e => e.key === "Enter" && unlock()}
          placeholder="••••"
          className="bg-zinc-950 border-zinc-700 mt-1 h-14 text-center meter-display text-3xl tracking-[0.4em]"
        />
        <Button data-testid="support-unlock-btn" onClick={unlock} disabled={busy || pin.length < 4} className="w-full h-12 mt-4 bg-amber-400 hover:bg-amber-300 text-zinc-950 font-bold">
          {busy ? "UNLOCKING…" : "UNLOCK SUPPORT"}
        </Button>
      </Card>
    );
  }

  return (
    <div className="space-y-4" data-testid="support-tab">
      <Card className="bg-zinc-900 border-amber-400/30 p-5">
        <div className="flex items-start gap-3">
          <LifeBuoy className="h-5 w-5 text-amber-400 flex-shrink-0 mt-0.5"/>
          <div>
            <h3 className="font-bold">Support Tools — Unlocked</h3>
            <p className="text-xs text-zinc-400 mt-0.5">PIN-gated section for test-data cleanup, the secondary Square maintenance processor, and platform internals. All actions are logged to the audit trail below.</p>
          </div>
          <Button onClick={() => { setUnlocked(false); setPin(""); }} variant="outline" size="sm" className="bg-zinc-950 border-zinc-700 ml-auto" data-testid="support-relock">Re-lock</Button>
        </div>
      </Card>

      <Card className="bg-zinc-900 border-zinc-800 p-5" data-testid="support-cleanup-card">
        <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
          <div>
            <h3 className="font-bold flex items-center gap-2"><Trash2 className="h-4 w-4 text-red-400"/>Test Data Cleanup</h3>
            <p className="text-[11px] text-zinc-500 mt-1">Select rides to delete or zero-out (keeps the row but voids the money — useful for audit). All actions are logged.</p>
          </div>
          <div className="flex gap-2 items-center">
            <Input data-testid="support-filter" placeholder="Filter…" value={filter} onChange={e=>setFilter(e.target.value)} className="bg-zinc-950 border-zinc-700 h-8 text-xs w-44"/>
            <Badge variant="outline" className="border-zinc-700">{filteredRides.length} / {rides.length} shown</Badge>
            <Badge variant="outline" className="border-amber-400/40 text-amber-400" data-testid="support-selected-count">{selected.size} selected</Badge>
          </div>
        </div>

        <div className="overflow-x-auto border border-zinc-800 rounded">
          <table className="w-full text-xs">
            <thead className="bg-zinc-950 text-[10px] uppercase tracking-widest text-zinc-500">
              <tr>
                <th className="p-2 w-8 text-center"><input type="checkbox" data-testid="support-select-all" checked={filteredRides.length > 0 && selected.size === filteredRides.length} onChange={toggleAll} className="accent-amber-400 cursor-pointer"/></th>
                <th className="p-2 text-left">Waybill</th>
                <th className="p-2 text-left">Kind</th>
                <th className="p-2 text-left">Who</th>
                <th className="p-2 text-left">Route</th>
                <th className="p-2 text-right">Total</th>
                <th className="p-2 text-left">Pay</th>
                <th className="p-2 text-left">Created</th>
              </tr>
            </thead>
            <tbody>
              {filteredRides.length === 0 && <tr><td colSpan={8} className="p-6 text-center text-zinc-500">No rides match.</td></tr>}
              {filteredRides.map(r => (
                <tr key={r.id} className={`border-t border-zinc-800 ${selected.has(r.id) ? "bg-amber-400/5" : ""}`} data-testid={`support-row-${r.id}`}>
                  <td className="p-2 text-center"><input type="checkbox" checked={selected.has(r.id)} onChange={()=>toggleOne(r.id)} className="accent-amber-400 cursor-pointer" data-testid={`support-select-${r.id}`}/></td>
                  <td className="p-2 font-mono text-amber-400">{r.waybill_no}</td>
                  <td className="p-2"><Badge variant="outline" className={r.kind === "business" ? "border-blue-400/40 text-blue-300" : "border-zinc-700"}>{r.kind || "rider"}</Badge></td>
                  <td className="p-2 text-zinc-300">{r.business_name || r.customer_name || "—"}</td>
                  <td className="p-2 text-zinc-400 max-w-xs truncate">{r.origin} → {r.destination}</td>
                  <td className="p-2 text-right meter-display text-amber-400 font-bold">{fmtUsd(r.total)}</td>
                  <td className="p-2 uppercase text-zinc-400">{r.payment_method} <span className="text-[10px] text-zinc-600">{(r.payment_status || "").substring(0, 18)}</span></td>
                  <td className="p-2 text-zinc-500 text-[10px]">{r.created_at && new Date(r.created_at).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="flex flex-wrap gap-2 mt-4">
          <Button data-testid="support-zero-selected" onClick={() => clearSelected(true)} disabled={busy || selected.size === 0} variant="outline" className="bg-zinc-950 border-amber-400/40 text-amber-400 hover:bg-amber-400/10">ZERO OUT SELECTED ({selected.size})</Button>
          <Button data-testid="support-delete-selected" onClick={() => clearSelected(false)} disabled={busy || selected.size === 0} variant="outline" className="bg-red-500/10 border-red-400/40 text-red-300 hover:bg-red-500/20">DELETE SELECTED ({selected.size})</Button>
          <div className="border-l border-zinc-800 ml-2 pl-2 flex gap-2">
            <Button data-testid="support-zero-all" onClick={() => clearAll(true)} disabled={busy || rides.length === 0} variant="outline" className="bg-zinc-950 border-amber-400/40 text-amber-400 text-xs">Zero out ALL</Button>
            <Button data-testid="support-delete-all" onClick={() => clearAll(false)} disabled={busy || rides.length === 0} variant="outline" className="bg-red-500/10 border-red-500/40 text-red-300 text-xs">Delete ALL</Button>
          </div>
        </div>
      </Card>

      <Card className="bg-zinc-900 border-zinc-800 p-5" data-testid="support-audit-card">
        <h3 className="font-bold mb-3 flex items-center gap-2"><FileText className="h-4 w-4 text-amber-400"/>Support Audit Log ({audit.length})</h3>
        {audit.length === 0 ? (
          <div className="text-sm text-zinc-500 py-4">No support actions recorded yet.</div>
        ) : (
          <div className="space-y-1.5 max-h-72 overflow-auto">
            {audit.map(a => (
              <div key={a.id} className="bg-zinc-950 border border-zinc-800 rounded p-2 text-xs flex items-center justify-between gap-2" data-testid={`audit-${a.id}`}>
                <span><span className="font-mono text-amber-400">{a.action}</span> · <span className="text-zinc-300">{a.count} ride{a.count === 1 ? "" : "s"}</span> · <span className="text-zinc-500">{a.performed_by}</span></span>
                <span className="text-[10px] text-zinc-500">{new Date(a.performed_at).toLocaleString()}</span>
              </div>
            ))}
          </div>
        )}
      </Card>

      <Card className="bg-zinc-900 border-zinc-800 p-5" data-testid="support-square-info">
        <h3 className="font-bold mb-2 flex items-center gap-2"><DollarSign className="h-4 w-4 text-green-400"/>Maintenance Square (Secondary Processor)</h3>
        <p className="text-xs text-zinc-400 mb-3">The $1.55 maintenance fee on every ride routes to a SEPARATE Square account. This processes independently — drivers cannot override or skip it. Configure the keys in the Payments tab → Square card → Maintenance Square section.</p>
        <Button asChild variant="outline" className="bg-zinc-950 border-zinc-700"><a href="#" onClick={(e) => { e.preventDefault(); document.querySelector('[data-testid=tab-payments]')?.click(); }}>Open Square Configuration →</a></Button>
      </Card>
    </div>
  );
}



// ============================================================================
// RIDE COORDINATION TAB (supervisor + admin master only)
// ============================================================================
function RideCoordinationTab() {
  const [items, setItems] = useState([]);
  const [drivers, setDrivers] = useState([]);
  // v2.7.65+E50 · LMS compliance gate — Driver Safety Certification map
  const [safetyCert, setSafetyCert] = useState({ total: 0, byUser: {} });
  const [busyId, setBusyId] = useState(null);
  const [picks, setPicks] = useState({});
  const lastIdsRef = useRef(new Set());
  const audioRef = useRef(null);
  const firstLoadRef = useRef(true);
  // v2.7.65+E85 · Rider Info modal + Active trip counter (system-critical spec)
  const [riderInfo, setRiderInfo] = useState(null);   // ride object being inspected
  const [activeCount, setActiveCount] = useState(0);

  // Prime the dispatch-trip-notification audio on first user gesture.
  // v2.6.7: prime with a guaranteed-silent inline WAV data URI so the prime
  // can NEVER blip audibly. The real chime audio is fetched and played fresh
  // when a NEW ride enters the queue.
  useEffect(() => {
    let unlocked = false;
    const SILENT_WAV =
      "data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEARKwAAIhYAQACABAAZGF0YQAAAAA=";
    const unlock = () => {
      if (unlocked) return;
      try {
        // Silent prime — satisfies the autoplay policy with no audible bytes.
        const primer = new Audio(SILENT_WAV);
        primer.volume = 0.0;
        primer.muted = true;
        const pp = primer.play();
        if (pp && pp.then) pp.then(() => { try { primer.pause(); } catch (pauseErr) { console.warn("audio primer pause failed", pauseErr?.message); } }).catch(() => {});

        // Prepare the REAL chime element (preloaded, paused) ready to fire.
        const a = new Audio("/sounds/online-booking-init.mp3");
        a.volume = 1.0;
        a.preload = "auto";
        audioRef.current = a;
        unlocked = true;
      } catch (e) {
        // Autoplay policy blocked priming — that's expected on a fresh tab,
        // the chime will be primed on the next user gesture (pointerdown/keydown).
        console.warn("dispatch audio primer blocked by autoplay policy", e?.message);
      }
    };
    window.addEventListener("pointerdown", unlock, { once: true, capture: true });
    window.addEventListener("keydown", unlock, { once: true, capture: true });
    return () => {
      window.removeEventListener("pointerdown", unlock, true);
      window.removeEventListener("keydown", unlock, true);
    };
  }, []);

  const reload = useCallback(() => {
    adminApi.get("/admin/dispatch/queue").then(r => {
      const list = r.data.items || [];
      // v2.7.65+E85 · Fire booking-alarm loop events on new IDs, and
      // ACK events on IDs that disappeared (e.g. assigned via another
      // tab). The global useAudioAlerts hook owns the actual audio.
      const newIds = new Set(list.map(x => x.id));
      const byId = new Map(list.map(x => [x.id, x]));
      const previous = lastIdsRef.current;
      if (!firstLoadRef.current) {
        // New rides → start (or continue) the looping alarm. v2.8+E285 · carry
        // the booking `source` so useAudioAlerts routes rider-app bookings to
        // their distinct tone (the WS path resolves source server-side).
        for (const id of newIds) {
          if (!previous.has(id)) {
            const src = (byId.get(id) || {}).source;
            try { window.dispatchEvent(new CustomEvent("rtg:public_booking", { detail: { trip_id: id, source: src } })); } catch { /* noop */ }
          }
        }
        // Departed rides → ACK them so the loop stops if none remain.
        for (const id of previous) {
          if (!newIds.has(id)) {
            try { window.dispatchEvent(new CustomEvent("rtg:public_booking_acknowledged", { detail: { trip_id: id } })); } catch { /* noop */ }
          }
        }
      }
      lastIdsRef.current = newIds;
      firstLoadRef.current = false;
      setItems(list);
    }).catch((err) => console.warn("dispatch queue poll failed", err?.response?.status));
    adminApi.get("/admin/dispatch/online-drivers").then(r => setDrivers(r.data.drivers || [])).catch((err) => console.warn("online drivers poll failed", err?.response?.status));
    // v2.7.65+E50 · LMS compliance gate — Driver Safety Certification map
    adminApi.get("/admin/training/lms/compliance").then(r => setSafetyCert({
      total: r.data.total_modules || 0,
      byUser: r.data.completed_by_user || {},
    })).catch((err) => console.warn("safety cert poll failed", err?.response?.status));
    // v2.7.65+E85 · Active-trip counter feed (in_progress or accepted).
    adminApi.get("/admin/dispatch/audit", { params: { status_filter: "in_progress,accepted", days: 3 }})
      .then(r => setActiveCount((r.data.items || []).length))
      .catch(() => { /* silent */ });
  }, []);
  useEffect(() => { reload(); const t = setInterval(reload, 8000); return () => clearInterval(t); }, [reload]);

  const reassign = async (rideId, driverId = null, mode = "force") => {
    setBusyId(rideId);
    try {
      const { data } = await adminApi.post(`/admin/rides/${rideId}/reassign`, driverId ? { driver_id: driverId } : {});
      toast.success(mode === "reping" ? "Re-pinged all online drivers" : `Assigned to ${data.driver_name}`);
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Reassign failed"); }
    finally { setBusyId(null); }
  };

  const cancel = async (rideId, waybill) => {
    if (!window.confirm(`Cancel ride ${waybill}? Driver will be released.`)) return;
    setBusyId(rideId);
    try { await adminApi.post(`/admin/rides/${rideId}/cancel`); toast.success("Ride canceled"); reload(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Cancel failed"); }
    finally { setBusyId(null); }
  };

  const smsBlast = async (rideId) => {
    if (!window.confirm("SMS every active driver about this ride?")) return;
    setBusyId(rideId);
    try {
      const { data } = await adminApi.post(`/admin/rides/${rideId}/sms-broadcast`);
      toast.success(`SMS · ${data.sent.length} sent · ${data.skipped.length} skipped`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Blast failed"); }
    finally { setBusyId(null); }
  };

  const queueOnDriver = async (rideId, driverId) => {
    if (!driverId) return toast.error("Pick a driver first");
    setBusyId(rideId);
    try {
      await adminApi.post(`/admin/drivers/${driverId}/queue-trip`, { ride_id: rideId });
      toast.success("Queued · driver app shows 'Next trip up'");
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Queue failed"); }
    finally { setBusyId(null); }
  };

  return (
    <div className="space-y-4" data-testid="ride-coordination-tab">
      {/* v2.7.65+E215 · additive indicator — Future Fares approaching within 1h */}
      <ApproachingFutureFaresStrip/>
      <Card className="bg-zinc-900 border-amber-400/40 p-5">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
          <div>
            <h3 className="font-bold flex items-center gap-2"><Zap className="h-5 w-5 text-amber-400"/>Rider Coordinator</h3>
            <p className="text-[11px] text-zinc-500 mt-0.5">Supervisor-only · route every pending ride to the right driver. Stack queue, re-ping, or directly assign in one tap.</p>
            {/* v2.8+E288 · One-tap jump to the full standalone Ride Coordinator
                (LiveQueueColumn) where dispatch can edit price, add driver
                notes, and assign rider-app bookings. Opens in a new tab so the
                dashboard stays put. */}
            <a
              href="/admin/ride-coordinator"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="coord-open-standalone"
              className="inline-flex items-center gap-1.5 mt-2 text-[11px] font-bold text-sky-300 hover:text-sky-200 border border-sky-500/40 hover:border-sky-400 bg-sky-500/10 rounded-md px-2.5 py-1 transition-colors"
              title="Open the full Ride Coordinator board (edit price · driver notes · assign)"
            >
              <Maximize2 className="h-3.5 w-3.5"/> Open Full Ride Coordinator
            </a>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-[11px] text-zinc-400">Drivers online <span className="text-amber-400 font-bold">{drivers.length}</span></span>
            {items.length > 0 && (
              <Button
                data-testid="coord-ack-alarm"
                size="sm"
                variant="outline"
                onClick={() => {
                  try { window.dispatchEvent(new CustomEvent("rtg:acknowledge_all_alarms")); } catch { /* noop */ }
                  toast.message("Alarm silenced. Assign or dismiss pending rides to keep it off.");
                }}
                className="bg-zinc-950 border-red-400/40 text-red-300 hover:bg-red-500/10 text-[10px] h-8"
              >
                🔕 Silence Alarm
              </Button>
            )}
          </div>
        </div>
        {/* v2.7.65+E50 · LMS compliance gate — safety-certification status of
            every online driver so coordinators see training gaps BEFORE assigning */}
        {safetyCert.total > 0 && drivers.length > 0 && (
          <div className="mt-3 pt-3 border-t border-zinc-800 flex flex-wrap items-center gap-1.5" data-testid="coord-cert-strip">
            <span className="text-[10px] font-mono text-zinc-500 uppercase mr-1">Safety Cert:</span>
            {drivers.map(d => {
              const done = safetyCert.byUser[d.id] || 0;
              const certified = done >= safetyCert.total;
              return certified ? (
                <span key={d.id} data-testid={`coord-cert-ok-${d.id}`}
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded border border-emerald-500/40 bg-emerald-500/10 text-emerald-300 text-[10px] font-bold">
                  ✓ {d.name}
                </span>
              ) : (
                <span key={d.id} data-testid={`coord-cert-gap-${d.id}`}
                      title={`${done}/${safetyCert.total} Driver Safety Certification modules complete — record completions in the LMS`}
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded border border-amber-500/50 bg-amber-500/10 text-amber-300 text-[10px] font-bold">
                  ⚠ {d.name} · {done}/{safetyCert.total}
                </span>
              );
            })}
          </div>
        )}
      </Card>

      {/* v2.7.65+E86 · Live Drivers Online panel · Ping any driver to force a fresh high-accuracy GPS ping */}
      {drivers.length > 0 && (
        <Card className="bg-zinc-900 border-emerald-500/30 p-4" data-testid="coord-online-drivers-panel">
          <div className="flex items-center justify-between mb-3">
            <h4 className="font-bold text-sm text-emerald-300 flex items-center gap-2">
              <Wifi className="h-4 w-4"/>Drivers Online — Live Presence
              <span className="text-[10px] text-zinc-500 font-mono">({drivers.length})</span>
            </h4>
            <div className="text-[10px] text-zinc-500">Ping → forces a high-accuracy GPS refresh on that driver's device</div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
            {drivers.map(d => (
              <div key={d.id} data-testid={`coord-online-driver-${d.id}`}
                   className="flex items-center gap-2 rounded-lg border border-zinc-800 bg-zinc-950 px-3 py-2">
                <div className={`h-2.5 w-2.5 rounded-full ${d.on_trip ? "bg-amber-400" : "bg-emerald-400"} ${d.on_trip ? "" : "animate-pulse"}`}/>
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-bold text-zinc-100 truncate">{d.name}</div>
                  <div className="text-[10px] text-zinc-500 truncate">
                    {d.plate_number ? `${d.plate_number} · ` : ""}
                    {d.on_trip ? <span className="text-amber-300">on-trip</span> : <span className="text-emerald-300">idle</span>}
                  </div>
                </div>
                <Button
                  data-testid={`coord-ping-${d.id}`}
                  size="sm"
                  variant="outline"
                  onClick={async () => {
                    try {
                      const res = await adminApi.post(`/admin/drivers/${d.id}/ping`);
                      if (res.data?.delivered) toast.success(`Pinged ${d.name} · GPS refreshing`);
                      else toast.warning(`${d.name}'s app isn't connected — ping queued`);
                    } catch (e) {
                      toast.error(`Ping failed: ${e?.response?.data?.detail || e.message}`);
                    }
                  }}
                  className="bg-zinc-950 border-sky-500/40 text-sky-300 hover:bg-sky-500/10 text-[10px] h-7"
                >
                  <MapPin className="h-3 w-3 mr-1"/>Ping
                </Button>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* v2.8+E289 · Unify Coordinators (option c) — single source of truth.
          The in-dashboard duplicate queue is retired; dispatch works the live
          board on /admin/ride-coordinator. Old row renderer kept dead below. */}
      <Card className="bg-gradient-to-br from-sky-500/10 to-zinc-900 border-sky-500/40 p-8 text-center" data-testid="coord-unified-cta">
        <div className="mx-auto max-w-md space-y-3">
          <div className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-sky-500/15 border border-sky-500/40 mx-auto">
            <Zap className="h-6 w-6 text-sky-300"/>
          </div>
          <h4 className="text-lg font-bold text-zinc-100">One board for the whole queue</h4>
          <p className="text-sm text-zinc-400">
            To keep every count in sync, all pending, assigned, and in-progress rides — Rider App and dispatch alike — now live on a single Ride Coordinator board. Review fares, add driver notes, flag urgent rides, and assign drivers there.
          </p>
          <a
            href="/admin/ride-coordinator"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="coord-open-standalone-cta"
            className="inline-flex items-center gap-2 mt-1 bg-sky-500 hover:bg-sky-400 text-zinc-950 font-bold rounded-lg px-5 py-2.5 transition-colors"
          >
            <Maximize2 className="h-4 w-4"/> Open the Ride Coordinator
          </a>
        </div>
      </Card>
      {false && items.map(r => {
        const driverPick = picks[r.id] || "";
        const setPick = (v) => setPicks({ ...picks, [r.id]: v });
        const off = r.offer || {};
        const ageSec = off.created_at ? Math.floor((Date.now() - new Date(off.created_at).getTime()) / 1000) : null;
        return (
          <Card key={r.id} className="bg-zinc-900 border-zinc-800 p-4" data-testid={`coord-row-${r.id}`}>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="md:col-span-2 space-y-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge variant="outline" className="border-amber-400/40 text-amber-400 text-[10px] uppercase">{r.kind || r.ride_type}</Badge>
                  <Badge variant="outline" className={r.status === "no_drivers_available" ? "border-red-400/60 text-red-400 text-[10px] uppercase" : "border-zinc-600 text-[10px] uppercase"}>{(r.status || "").replace(/_/g," ")}</Badge>
                  {ageSec !== null && <span className="text-[10px] text-zinc-500">offer age {Math.floor(ageSec/60)}:{String(ageSec%60).padStart(2,"0")}</span>}
                  {off.attempt > 1 && <Badge variant="outline" className="border-orange-400/40 text-orange-300 text-[10px] uppercase">attempt {off.attempt}</Badge>}
                  {off.loop_round > 0 && <Badge variant="outline" className="border-orange-400/40 text-orange-300 text-[10px] uppercase">loop {off.loop_round + 1}</Badge>}
                  <span className="text-[10px] text-zinc-500 uppercase tracking-wider">{r.waybill_no}</span>
                  {r.dispatch_confirmed && <Badge variant="outline" className="border-emerald-400/50 text-emerald-300 text-[10px] uppercase" data-testid={`coord-row-${r.id}-confirmed`} title={`Dispatch-confirmed${r.dispatch_confirmed_by ? ` by ${r.dispatch_confirmed_by}` : ""}${r.dispatch_confirmed_at ? ` · ${new Date(r.dispatch_confirmed_at).toLocaleString()}` : ""}`}>✓ Dispatch-confirmed by Ashley</Badge>}
                  {r.customer_type && <CustomerTypeBadge type={r.customer_type} short testid={`coord-row-${r.id}-customer-type`}/>}
                  {r.group_rider_count ? <span className="text-[10px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded bg-violet-500/15 text-violet-300 border border-violet-500/40" data-testid={`coord-row-${r.id}-group-riders`}>Group · {r.group_rider_count} riders</span> : null}
                  {r.source === "rider_account" && <span className="text-[10px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded bg-sky-500/15 text-sky-300 border border-sky-500/40" data-testid={`coord-row-${r.id}-source-rider`} title="Booked by the rider from their account">Rider App</span>}
                </div>
                <div className="text-sm font-semibold truncate">{r.origin} → {r.destination}</div>
                <div className="text-[11px] text-zinc-500">{r.business_name || r.customer_name || r.reservation_name || "—"} · {r.passengers} pax · {r.payment_method?.toUpperCase()} · {fmtUsd(r.total)}</div>
                {/* v2.7.65+E202 · Booking time-fields ledger. Sourced from the
                    existing db.rides fields already returned by /admin/dispatch/queue:
                      • Booked At     ← r.created_at (submission timestamp)
                      • Pickup Time   ← r.pickup_at / r.scheduled_for (rider-requested)
                      • Scheduled Date ← calendar date when r.scheduled=true AND
                                          the pickup date falls on a future day
                    Display-only, no data/logic changes. */}
                <div
                  className="flex items-center flex-wrap gap-x-3 gap-y-0.5 text-[10px] text-zinc-400 pt-0.5"
                  data-testid={`coord-row-${r.id}-times`}
                >
                  <span data-testid={`coord-row-${r.id}-booked-at`}>
                    <span className="uppercase tracking-widest text-[9px] font-bold text-zinc-500">Booked At</span>
                    <span className="ml-1.5 font-mono text-zinc-200">
                      {r.created_at
                        ? new Date(r.created_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })
                        : "Not recorded"}
                    </span>
                  </span>
                  <span data-testid={`coord-row-${r.id}-pickup-time`}>
                    <span className="uppercase tracking-widest text-[9px] font-bold text-zinc-500">Pickup Time</span>
                    <span className="ml-1.5 font-mono text-zinc-200">
                      {(() => {
                        const pu = r.pickup_at || r.scheduled_for;
                        if (!pu) return <span className="text-emerald-300">ASAP</span>;
                        const d = new Date(pu);
                        if (Number.isNaN(d.getTime())) return "Not recorded";
                        return d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
                      })()}
                    </span>
                  </span>
                  {(() => {
                    const pu = r.pickup_at || r.scheduled_for;
                    if (!pu || !r.scheduled) return null;
                    const d = new Date(pu);
                    if (Number.isNaN(d.getTime())) return null;
                    const now = new Date();
                    // Show the Scheduled Date badge whenever the ride was
                    // pre-booked for a calendar day other than today
                    // (past-orphaned OR future). Same-day scheduled pickups
                    // already read cleanly as "Pickup Time · 3:00 PM".
                    const puDay = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
                    const todayDay = `${now.getFullYear()}-${now.getMonth()}-${now.getDate()}`;
                    if (puDay === todayDay) return null;
                    const opts = { weekday: "short", month: "short", day: "numeric" };
                    if (d.getFullYear() !== now.getFullYear()) opts.year = "numeric";
                    // v2.7.65+E203 · Overdue flag — every card in this
                    // queue is by definition still unassigned/pending
                    // (endpoint filters to booked/dispatching/queued/
                    // no_drivers_available). If the full pickup datetime
                    // has passed, flip the badge red so the operator can
                    // spot orphaned pre-books at a glance.
                    const isOverdue = d.getTime() < now.getTime();
                    return (
                      <span
                        data-testid={`coord-row-${r.id}-scheduled-date`}
                        data-overdue={isOverdue ? "true" : "false"}
                        className={
                          isOverdue
                            ? "inline-flex items-center gap-1 px-1.5 py-[1px] rounded-full bg-red-500/20 border border-red-500/60 animate-pulse"
                            : "inline-flex items-center gap-1 px-1.5 py-[1px] rounded-full bg-amber-500/15 border border-amber-500/40"
                        }
                        title={isOverdue ? "Pickup window has passed — ride still unassigned" : "Pre-booked for a future date"}
                      >
                        <span
                          className={
                            isOverdue
                              ? "uppercase tracking-widest text-[9px] font-black text-red-300"
                              : "uppercase tracking-widest text-[9px] font-bold text-amber-300"
                          }
                        >
                          {isOverdue ? "Overdue" : "Scheduled Date"}
                        </span>
                        <span
                          className={
                            isOverdue
                              ? "ml-0.5 font-mono text-red-100"
                              : "ml-0.5 font-mono text-amber-100"
                          }
                        >
                          {d.toLocaleDateString(undefined, opts)}
                        </span>
                      </span>
                    );
                  })()}
                </div>
                {off.broadcast_to && <div className="text-[10px] text-zinc-600">Broadcast: {off.broadcast_to.length} drivers · Declined: {(off.declined_by || []).length}</div>}
                {/* v2.7.65+E85 · Rider Info button — opens the secure detail modal */}
                <Button
                  data-testid={`coord-rider-info-${r.id}`}
                  size="sm"
                  variant="outline"
                  onClick={() => setRiderInfo(r)}
                  className="bg-zinc-950 border-sky-500/40 text-sky-300 hover:bg-sky-500/10 text-[10px] h-7 mt-1"
                >
                  <User className="h-3 w-3 mr-1"/>Rider Info
                </Button>
              </div>

              <div className="space-y-2">
                <select
                  data-testid={`coord-pick-${r.id}`}
                  value={driverPick}
                  onChange={e => setPick(e.target.value)}
                  className="w-full bg-zinc-950 border border-zinc-700 text-zinc-100 rounded-md h-9 px-2 text-xs"
                >
                  <option value="">— Select driver —</option>
                  {drivers.map(d => (
                    <option key={d.id} value={d.id} className="bg-zinc-950">
                      {d.name} {d.on_trip ? "🟠 on trip" : "🟢 idle"}
                      {d.plate_number ? ` · ${d.plate_number}` : ""}
                      {safetyCert.total > 0 && (safetyCert.byUser[d.id] || 0) < safetyCert.total ? " · ⚠ safety-cert" : ""}
                    </option>
                  ))}
                </select>
                <div className="grid grid-cols-2 gap-1">
                  <Button data-testid={`coord-assign-${r.id}`} size="sm" disabled={busyId === r.id || !driverPick} onClick={() => reassign(r.id, driverPick, "force")} className="bg-amber-400 hover:bg-amber-300 text-zinc-950 font-bold text-xs h-8 col-span-2">Assign</Button>
                  <Button data-testid={`coord-queue-${r.id}`} size="sm" disabled={busyId === r.id || !driverPick} onClick={() => queueOnDriver(r.id, driverPick)} variant="outline" className="bg-zinc-950 border-amber-400/40 text-amber-400 text-[10px] h-7">Queue Up</Button>
                  <Button data-testid={`coord-reping-${r.id}`} size="sm" disabled={busyId === r.id} onClick={() => reassign(r.id, null, "reping")} variant="outline" className="bg-zinc-950 border-zinc-700 text-amber-400 text-[10px] h-7"><RotateCw className="h-3 w-3 mr-1"/>Re-ping</Button>
                  <Button data-testid={`coord-sms-${r.id}`} size="sm" disabled={busyId === r.id} onClick={() => smsBlast(r.id)} variant="outline" className="bg-zinc-950 border-zinc-700 text-blue-300 text-[10px] h-7 col-span-1"><MessageSquare className="h-3 w-3 mr-1"/>SMS</Button>
                  <Button data-testid={`coord-cancel-${r.id}`} size="sm" disabled={busyId === r.id} onClick={() => cancel(r.id, r.waybill_no)} variant="outline" className="bg-zinc-950 border-red-400/40 text-red-400 text-[10px] h-7 col-span-1"><XIcon className="h-3 w-3 mr-1"/>Cancel</Button>
                </div>
              </div>
            </div>
          </Card>
        );
      })}

      {/* v2.6.5 — Ride Audit panel (all rides, sign-off + reopen) */}
      <RideAuditPanel/>

      {/* v2.7.65+E85 · Rider Info modal — secure detail view per SYSTEM-CRITICAL spec */}
      <Dialog open={!!riderInfo} onOpenChange={(open) => !open && setRiderInfo(null)}>
        <DialogContent className="bg-zinc-950 border-amber-500/40 text-zinc-100 max-w-lg" data-testid="rider-info-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-300">
              <User className="h-5 w-5"/>Rider Details
            </DialogTitle>
          </DialogHeader>
          {riderInfo && (
            <div className="space-y-4 pt-2">
              <div className="grid grid-cols-3 gap-2 text-sm">
                <div className="col-span-1 text-zinc-500 uppercase text-[10px] font-mono">Name</div>
                <div className="col-span-2 font-bold" data-testid="rider-info-name">
                  {riderInfo.customer_name || riderInfo.business_name || riderInfo.reservation_name || "—"}
                </div>
                <div className="col-span-1 text-zinc-500 uppercase text-[10px] font-mono">Phone</div>
                <div className="col-span-2 font-bold text-sky-300" data-testid="rider-info-phone">
                  {riderInfo.customer_phone || riderInfo.rider_phone || riderInfo.phone ? (
                    <a href={`tel:${riderInfo.customer_phone || riderInfo.rider_phone || riderInfo.phone}`}
                       className="hover:underline flex items-center gap-1">
                      <Phone className="h-3.5 w-3.5"/>{riderInfo.customer_phone || riderInfo.rider_phone || riderInfo.phone}
                    </a>
                  ) : (<span className="text-zinc-600">not on file</span>)}
                </div>
                <div className="col-span-1 text-zinc-500 uppercase text-[10px] font-mono">Email</div>
                <div className="col-span-2 font-bold text-sky-300" data-testid="rider-info-email">
                  {riderInfo.customer_email || riderInfo.rider_email || riderInfo.email ? (
                    <a href={`mailto:${riderInfo.customer_email || riderInfo.rider_email || riderInfo.email}`}
                       className="hover:underline flex items-center gap-1">
                      <Mail className="h-3.5 w-3.5"/>{riderInfo.customer_email || riderInfo.rider_email || riderInfo.email}
                    </a>
                  ) : (<span className="text-zinc-600">not on file</span>)}
                </div>
              </div>
              <div className="border-t border-zinc-800 pt-3 grid grid-cols-3 gap-2 text-sm">
                <div className="col-span-1 text-zinc-500 uppercase text-[10px] font-mono">Pickup</div>
                <div className="col-span-2" data-testid="rider-info-pickup">{riderInfo.origin || "—"}</div>
                <div className="col-span-1 text-zinc-500 uppercase text-[10px] font-mono">Drop-off</div>
                <div className="col-span-2" data-testid="rider-info-dropoff">{riderInfo.destination || "—"}</div>
                <div className="col-span-1 text-zinc-500 uppercase text-[10px] font-mono">Scheduled</div>
                <div className="col-span-2" data-testid="rider-info-scheduled">
                  {riderInfo.scheduled_for || riderInfo.pickup_at || "ASAP"}
                </div>
                <div className="col-span-1 text-zinc-500 uppercase text-[10px] font-mono">Passengers</div>
                <div className="col-span-2">{riderInfo.passengers || 1}</div>
                <div className="col-span-1 text-zinc-500 uppercase text-[10px] font-mono">Payment</div>
                <div className="col-span-2 uppercase">{riderInfo.payment_method || "—"} · {fmtUsd(riderInfo.total)}</div>
                {riderInfo.delivery_items && (
                  <>
                    <div className="col-span-1 text-zinc-500 uppercase text-[10px] font-mono">Delivery items</div>
                    <div className="col-span-2" data-testid="rider-info-items">{riderInfo.delivery_items}</div>
                  </>
                )}
                {(riderInfo.dispatcher_notes || riderInfo.public_notes || riderInfo.notes) && (
                  <>
                    <div className="col-span-1 text-zinc-500 uppercase text-[10px] font-mono">Notes</div>
                    <div className="col-span-2 italic text-zinc-300" data-testid="rider-info-notes">
                      {riderInfo.dispatcher_notes || riderInfo.public_notes || riderInfo.notes}
                    </div>
                  </>
                )}
              </div>
              <div className="border-t border-zinc-800 pt-2 text-[10px] text-zinc-600 font-mono flex justify-between">
                <span>Waybill: {riderInfo.waybill_no || riderInfo.id?.slice(0,8)}</span>
                <span>ID: {riderInfo.id?.slice(0,8)}</span>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ============================================================================
// RideAuditPanel — v2.6.5
// All recent rides (active + completed + cancelled). Each row has sign-off and
// reopen actions. Locked rides show a green "SIGNED OFF" badge.
// ============================================================================
function RideAuditPanel() {
  const [rides, setRides] = useState([]);
  const [filter, setFilter] = useState("all");
  const [days, setDays] = useState(14);
  const [busyId, setBusyId] = useState(null);
  const reload = useCallback(() => {
    adminApi.get("/admin/dispatch/audit", { params: { status_filter: filter, days } })
      .then(r => setRides(r.data.items || []))
      .catch(e => console.warn("audit load failed", e?.response?.status));
  }, [filter, days]);
  useEffect(() => { reload(); }, [reload]);

  const signOff = async (rideId, waybill) => {
    const notes = window.prompt(`Audit notes for ${waybill || rideId.slice(0,8)} (optional):`) || "";
    if (notes === null) return;
    setBusyId(rideId);
    try { await adminApi.post(`/admin/dispatch/audit/${rideId}/sign-off`, { notes }); toast.success("Ride signed off"); reload(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Sign-off failed"); }
    finally { setBusyId(null); }
  };
  const reopen = async (rideId) => {
    if (!window.confirm("Reopen this ride for editing? Audit trail will note who reopened it.")) return;
    setBusyId(rideId);
    try { await adminApi.post(`/admin/dispatch/audit/${rideId}/reopen`, {}); toast.message("Ride reopened"); reload(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Reopen failed"); }
    finally { setBusyId(null); }
  };

  const statusColor = (s) => {
    if (s === "done" || s === "completed") return "border-emerald-400/40 text-emerald-400";
    if (s === "cancelled" || s === "canceled") return "border-red-400/40 text-red-400";
    if (s === "in_progress" || s === "accepted") return "border-blue-400/40 text-blue-300";
    return "border-zinc-600 text-zinc-300";
  };

  return (
    <Card className="bg-zinc-900 border-zinc-800 mt-6" data-testid="ride-audit-panel">
      <div className="p-4 border-b border-zinc-800 flex items-center justify-between flex-wrap gap-2">
        <div>
          <h3 className="font-bold flex items-center gap-2"><Shield className="h-4 w-4 text-amber-400"/>Ride Audit · sign-off &amp; reopen</h3>
          <p className="text-[11px] text-zinc-500">Every ride from the selected window. Sign off after manual audit · supervisor can reopen for edits.</p>
        </div>
        <div className="flex items-center gap-2">
          <select data-testid="audit-status" value={filter} onChange={(e) => setFilter(e.target.value)} className="bg-zinc-950 border border-zinc-700 text-zinc-100 rounded-md h-8 px-2 text-xs">
            <option value="all">All statuses</option>
            <option value="done,completed">Completed</option>
            <option value="cancelled,canceled">Cancelled</option>
            <option value="dispatching,booked,queued,no_drivers_available">Active</option>
            <option value="in_progress,accepted">In progress</option>
          </select>
          <input data-testid="audit-days" type="number" min={1} max={365} value={days} onChange={(e) => setDays(Math.max(1, Math.min(365, parseInt(e.target.value||"14",10))))} className="bg-zinc-950 border border-zinc-700 text-zinc-100 rounded-md h-8 w-16 px-2 text-xs" title="Days back"/>
          <Button size="sm" variant="outline" onClick={reload} className="bg-zinc-950 border-zinc-700 h-8 text-xs"><RotateCw className="h-3 w-3 mr-1"/>Refresh</Button>
        </div>
      </div>
      <div className="divide-y divide-zinc-900">
        {rides.length === 0 && <div className="p-6 text-center text-zinc-500 text-xs">No rides in this window.</div>}
        {rides.map(r => (
          <div key={r.id} className={`p-3 flex items-center justify-between gap-3 flex-wrap ${r.signed_off ? "bg-emerald-500/5" : ""}`} data-testid={`audit-row-${r.id}`}>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <Badge variant="outline" className={`uppercase text-[10px] ${statusColor(r.status)}`}>{(r.status || "").replace(/_/g," ")}</Badge>
                <span className="text-[10px] tracking-wider text-zinc-500 font-mono">{r.waybill_no || r.id?.slice(0,8).toUpperCase()}</span>
                {r.signed_off && <Badge variant="outline" className="border-emerald-400/60 text-emerald-400 text-[10px] uppercase">Signed off</Badge>}
                {r.is_test && <Badge variant="outline" className="border-purple-400/40 text-purple-300 text-[10px] uppercase">TEST</Badge>}
              </div>
              <div className="text-xs mt-0.5 truncate">{r.origin || "—"} → {r.destination || "—"}</div>
              <div className="text-[11px] text-zinc-500">{r.driver_name || "(no driver)"} · {r.customer_name || r.business_name || "—"} · {r.passengers || 1} pax · {fmtUsd(r.total)}</div>
              {r.signed_off && <div className="text-[10px] text-emerald-300 mt-0.5">✓ {r.signed_off_by} · {r.signed_off_at} {r.audit_notes ? `· ${r.audit_notes}` : ""}</div>}
            </div>
            <div className="flex gap-1">
              {r.signed_off ? (
                <Button data-testid={`audit-reopen-${r.id}`} size="sm" variant="outline" disabled={busyId === r.id} onClick={() => reopen(r.id)} className="bg-zinc-950 border-amber-400/40 text-amber-400 text-[10px] h-7">Reopen</Button>
              ) : (
                <Button data-testid={`audit-signoff-${r.id}`} size="sm" disabled={busyId === r.id} onClick={() => signOff(r.id, r.waybill_no)} className="bg-emerald-500 hover:bg-emerald-400 text-zinc-950 text-[10px] font-bold h-7">Sign off</Button>
              )}
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}
